// Full-page AI chat — the roomy counterpart to the top-bar assistant drawer.
//
// The difference that matters is streaming: answers arrive token by token from
// the /api/chat Lambda Function URL (see lib/chat.js) instead of appearing all
// at once when a buffered request returns. Long answers start reading
// immediately, and the user can stop one mid-flight.
//
// Threads are kept client-side (localStorage, scoped per user). The server is
// stateless — each turn posts the history it wants considered — so "new chat"
// is purely local and nothing about a conversation leaves the browser except
// the turns actually sent.
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom'
import { useAuth } from '../auth.jsx'
import { useSignature, withSignature } from '../lib/signature.js'
import { useManifest, loadRegionRows, annotateRows } from '../lib/data.jsx'
import { CATEGORIES, DEFAULT_CATEGORY } from '../lib/categories.js'
import { PAGES } from '../lib/appSearch.js'
import { OPERATIONS, getOperation } from '../lib/agentRun.js'
// The run host — the module-scope singleton that OWNS every long-lived piece
// of chat work now: the agent operations, the streaming answers, and the
// draft resolution that follows a receipt. This page STARTS work and RENDERS
// it; no controller lives here any more, which is what lets navigation leave
// the work running. runHost.js records the history (the run that outlived the
// page with no UI and no guard) this construction solves.
import {
  startOperation as hostStartOperation,
  startStream as hostStartStream,
  stopStream as hostStopStream,
  stopRun as hostStopRun,
  getRun,
  subscribeRun,
  getStream,
  subscribeStream,
  ensureDraftsResolving,
  // The card's recipient dropdown: switch a lead to another person at the
  // company and have the email rewritten for them (kept per person).
  switchLeadRecipient,
  RUN_BUSY_NOTICE,
} from '../lib/runHost.js'
import {
  SLASH_COMMANDS,
  closestSlashCommand,
  commandArg,
  findSlashCommand,
  matchSlashCommands,
  parseSlashInput,
  parseSlashQuery,
} from '../lib/slashCommands.js'
// The operation tiles' state picker — the same dropdown the Activity page
// uses, so "choose a place" looks identical everywhere it appears.
import Select from '../components/Select.jsx'
// Who a lead card is addressed to, and who else is on the row: the pure
// readers behind the card's recipient dropdown, plus the one mirror read that
// finds each receipt lead's row (lib/leadContacts.js says why the people are
// not on the stub).
import { contactChoices, leadRowsIndex } from '../lib/leadContacts.js'
import { listDailyTasks, subscribeDailyTasks } from '../lib/dailyTasks.js'
import { commandThreadTitle } from '../lib/threadTitle.js'
import { checkBeforeRun, reloadForUpdate, stashPendingOperation, takePendingOperation } from '../lib/appVersion.js'
import { logEvent } from '../lib/events.js'
import { useRbac, pathAllowedFor } from '../lib/rbac.js'
// Lead-card only: the mirror is read for a company id and the portal it belongs
// to, and the intel store for the facts that ground the draft. Every one of
// them fails soft — the cards render without a link and with a shorter email
// rather than not at all.
import {
  hubspotIndex,
  hubspotMatch,
  hubspotStatus,
  leadLookup,
  matchVerdict,
  normDomain,
  nameSlug,
  companyIdFor,
} from '../lib/hubspot.js'
import { listIntel, getIntel } from '../lib/intel.js'
import { buildOutreachEmail, buildFollowupEmail, humanizeDraft, htmlFromText, templateExec } from '../lib/outreachDraft.js'
// The /daily-tasks receipt's RULES, kept out of the page: which
// raw HubSpot statuses get a filter chip, what a meeting row's clock says on
// Bo's calendar, which held facts a customer card's evidence line may state.
// Pure and node-tested (scripts/test-receiptSections.mjs) so the receipt and
// the test can never hold two opinions about them.
import {
  taskStatusBadge,
  customerEvidenceLine,
  customerStatusPill,
  taskEvidenceLine,
  taskStatusPill,
} from '../lib/receiptSections.js'
import {
  buildDraftPasses,
  buildFollowupDraftPasses,
  buildCallPasses,
} from '../lib/draftProvenance.js'
import { CADENCE_LINE, touchLabel } from '../lib/cadence.js'
import AgentRunView from '../components/AgentRunView.jsx'
import DraftFlowView from '../components/DraftFlowView.jsx'
import LeadTimelineView from '../components/LeadTimelineView.jsx'
import { buildWorkspaceContext } from '../lib/workspaceContext.js'
import {
  loadConversations,
  saveConversations,
  newConversation,
  titleFor,
  summarizeTitle,
  normalizeTitle,
  TITLE_MAX,
  // ── history now lives in the account, not in this browser ──
  // localStorage is the cache that paints instantly; the table behind
  // /api/chat-history is the source of truth. See the sync section in chat.js.
  syncConversations,
  mergeConversations,
  queueChanged,
  queueConversationDelete,
  flushChatSync,
  subscribeChatSync,
  chatSyncStatus,
  // The host writes finished work straight into the store (appendMessage /
  // patchMessage / patchReceiptLead in chat.js); this broadcast is how the
  // OPEN page hears those writes and merges them into its own copy.
  subscribeConversations,
} from '../lib/chat.js'
import { receiptLooksShed, refillReceipt } from '../lib/receiptRefill.js'
import { currentScope } from '../lib/userScope.js'
// The Settings-managed list of our own domains/addresses the daily briefing
// leaves off its task list. Read here only to NAME the list next to the
// "N hidden" count — the dropping itself happens in the run (lib/agentRun.js).
import { readExclusions, subscribeExclusions } from '../lib/taskExclusions.js'
// Drafts already shown, remembered across reloads. Read-only over facts: a hit
// paints the email with no beat, a miss rebuilds from the receipt's own stub.
// Nothing in here fetches, enriches or researches anything.
import * as draftCache from '../lib/draftCache.js'
import {
  isDraftCopied,
  markDraftCopied,
  subscribeCopied,
} from '../lib/copiedDrafts.js'
import Markdown from '../components/Markdown.jsx'
import {
  SparklesIcon,
  SendIcon,
  StopIcon,
  PlusIcon,
  TrashIcon,
  CopyIcon,
  CheckIcon,
  ChatIcon,
  ChevronRightIcon,
  ClockIcon,
  ChevronDownIcon,
  CloseIcon,
  PencilIcon,
} from '../components/icons.jsx'
import { ReportIssueButton } from '../components/ReportIssueModal.jsx'
import ApolloSendControls, { ApolloBatch } from '../components/ApolloSendControls.jsx'
import { matchStates, resolveState, statesFromManifest, rowInState } from '../lib/leadScope.js'
import { crmLeadRows, claimedCompanyIds } from '../lib/crmLeads.js'

// Chat itself is open to every role — reading and asking questions is not a
// gated action. An OPERATION is: it researches every lead it selects and has
// the model write an email for each one, which is real spend against the same
// APIs the leads directory gates behind 'leads.enrich'. Same key here (see
// canRunOperation), so the agent can never be the back door around a cost a
// role is held away from everywhere else.
const NO_OPERATION_REASON = 'Your role can’t research leads or draft emails'

// The empty state used to offer four suggested QUESTIONS alongside the
// operations. It offers the operations alone now: the shortcuts on this page
// ARE the three things the agent can do, not examples of things to type. A
// question is one keystroke away in the composer; an operation is not.

/**
 * One short line describing what a finished daily task found — a receipt, not a
 * boast. Every piece is read off what the run actually reported: the time it
 * finished and the number IT counted. The target in an operation's label
 * ("50 new leads") is never used here, so a run that found 12 says 12.
 */
function receiptLine(task) {
  if (!task?.done) return ''
  const bits = []
  const at = task.doneAt ? new Date(task.doneAt) : null
  bits.push(
    at && !Number.isNaN(at.getTime())
      ? `Done ${at.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`
      : 'Done today',
  )
  const receipt = task.receipt || {}
  // Deliberately generic ("selected", not "leads"/"threads"): this file knows
  // no operation ids and must not start guessing what a given run counted.
  if (typeof receipt.count === 'number') bits.push(`${receipt.count} selected`)
  // A briefing run (Daily Tasks, Follow-Up) stores no count — it selects
  // nothing — but its own one-line summary, which is the honest tile text.
  else if (receipt.summary) bits.push(String(receipt.summary))
  return bits.join(' · ')
}


// The pass-graph bookkeeping that used to live here (rootsOf / notStarted /
// blamePasses / cancelledPasses, and the apiTurns flattening) moved to
// lib/runHost.js with the run itself: the HOST posts every receipt now, so
// the host owns the story of where a run died. This page only renders what
// the receipt says.

function CopyButton({ text }) {
  const [copied, setCopied] = useState(false)
  return (
    <button
      type="button"
      aria-label="Copy answer"
      onClick={() => {
        navigator.clipboard?.writeText(text)
        setCopied(true)
        setTimeout(() => setCopied(false), 1600)
      }}
      className="inline-flex cursor-pointer items-center gap-x-1.5 rounded-[7px] bg-transparent px-2 py-1 text-[12px] font-medium text-faint transition-colors hover:bg-panel hover:text-ink"
    >
      {copied ? <CheckIcon className="h-3.5 w-3.5" /> : <CopyIcon className="h-3.5 w-3.5" />}
      {copied ? 'Copied' : 'Copy'}
    </button>
  )
}

// A conversation title that scrolls its full text into view on hover — a
// "banner" marquee — but only when it actually overflows, so short titles sit
// still. The overflow distance is measured and passed in as a CSS var; the
// keyframe (marqueeTitle, in styles.css) animates to it and back.
function MarqueeTitle({ text }) {
  const wrapRef = useRef(null)
  const [dist, setDist] = useState(0)
  useLayoutEffect(() => {
    const wrap = wrapRef.current
    const inner = wrap?.firstChild
    if (!wrap || !inner) return
    const over = inner.scrollWidth - wrap.clientWidth
    setDist(over > 4 ? over + 10 : 0)
  }, [text])
  return (
    <span ref={wrapRef} className="block overflow-hidden whitespace-nowrap">
      <span
        className="inline-block will-change-transform group-hover:[animation:marqueeTitle_2500ms_ease-in-out_infinite_alternate] motion-reduce:group-hover:[animation:none]"
        style={dist ? { '--marq': `-${dist}px` } : undefined}
      >
        {text}
      </span>
    </span>
  )
}

// ── the lead cards inside a receipt ─────────────────────────────────────────
//
// A receipt row is a record of what the agent did. What it now also has to be
// is a place a rep can act FROM — but only in ways that keep the promise the
// operation made: a draft a human reads, edits and sends themselves. So the
// card carries a link OUT to HubSpot and text ON to the clipboard, and nothing
// on it can put a message in front of a lead. There is no send path here, no
// mailto, no form; the copy buttons hand a string to the operating system and
// stop.

// The portal id, asked for ONCE per session and shared by every card. It is
// never hardcoded: the mirror reports which portal it is mirroring, and if it
// cannot tell us we render no link at all rather than a link into somebody
// else's portal. A miss is not cached — HubSpot may be configured by the time
// the next receipt renders — mirroring hubspotIndex()'s own soft-fail cache.
let portalPromise = null
function hubspotPortal() {
  if (!portalPromise) {
    portalPromise = hubspotStatus()
      .then((status) => {
        const id =
          status && status.available && status.configured ? String(status.portal_id || '') : ''
        if (!id) portalPromise = null
        return id
      })
      .catch(() => {
        portalPromise = null
        return ''
      })
  }
  return portalPromise
}

// The card matcher — companyIdFor — lives in lib/hubspot.js now, beside the
// index it reads, so the chat cards and the directory narrow an ambiguous
// contact the same way (an email's own domain outranks a namesake; see
// emailDomainCompanyId there for the Ball Construction case that moved it).

// The portal's own companies list, pre-filtered to the company name, for a
// lead that matched NO mirrored company. It deliberately links a SEARCH, not a
// record — "find it" is an honest claim when we could not resolve one, and
// most initial-outreach picks are new companies that are not in HubSpot at all
// yet. The objects-list `?query=` deep link is the one HubSpot actually
// honors (the /search/... page ignores its query string).
function hubspotSearchUrl(portalId, name) {
  const q = String(name || '').trim()
  if (!portalId || !q) return ''
  return `https://app.hubspot.com/contacts/${encodeURIComponent(portalId)}/objects/0-2/views/all/list?query=${encodeURIComponent(q)}`
}

function hubspotCompanyUrl(portalId, companyId) {
  if (!portalId || !companyId) return ''
  // The modern record URL (0-2 = company object type) — the shape HubSpot's
  // own UI uses. The legacy /company/{id} form still redirects, but emitting
  // the canonical form skips the hop and matches what users copy from HubSpot.
  return `https://app.hubspot.com/contacts/${encodeURIComponent(portalId)}/record/0-2/${encodeURIComponent(companyId)}`
}

// Copy the draft as BOTH text/html and text/plain in one clipboard write, so
// HubSpot's composer keeps the paragraphs (it reads the HTML flavour) and a
// plain textarea still gets sane text. Degrades in order, and the last step is
// telling the user — a copy button that silently does nothing is worse than one
// that says it couldn't.
async function copyDraft(html, text) {
  const canRich =
    typeof window !== 'undefined' &&
    typeof window.ClipboardItem === 'function' &&
    typeof navigator !== 'undefined' &&
    navigator.clipboard &&
    typeof navigator.clipboard.write === 'function'
  if (canRich) {
    try {
      await navigator.clipboard.write([
        new window.ClipboardItem({
          'text/html': new Blob([html], { type: 'text/html' }),
          'text/plain': new Blob([text], { type: 'text/plain' }),
        }),
      ])
      return { ok: true }
    } catch {
      // Permission denied, an insecure origin, an unfocused document — all of
      // them throw here, and all of them are still worth a plain-text attempt.
    }
  }
  if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text)
      return { ok: true, plain: true }
    } catch {
      /* fall through to the legacy path */
    }
  }
  // Insecure origins have no navigator.clipboard at all; the old selection API
  // is the only thing left there.
  if (legacyCopy(text)) return { ok: true, plain: true }
  return { ok: false, error: 'The browser blocked the clipboard — select the text and copy it.' }
}

function legacyCopy(text) {
  try {
    const ta = document.createElement('textarea')
    ta.value = text
    ta.setAttribute('readonly', '')
    ta.style.cssText = 'position:fixed;top:0;left:-9999px;opacity:0'
    document.body.appendChild(ta)
    ta.select()
    const ok = document.execCommand('copy')
    document.body.removeChild(ta)
    return ok
  } catch {
    return false
  }
}

// Plain text only — for talking points, which go to a notepad or a call log,
// not an email composer. No text/html flavour on purpose: pasting bullets into
// HubSpot's rich editor as HTML would invite "the app wrote my call notes".
async function copyPlain(text) {
  if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text)
      return { ok: true }
    } catch {
      /* fall through to the legacy path */
    }
  }
  if (legacyCopy(text)) return { ok: true }
  return { ok: false, error: 'The browser blocked the clipboard — select the text and copy it.' }
}

const CARD_BTN =
  'inline-flex cursor-pointer items-center gap-x-1 rounded-[7px] bg-surface px-2 py-1 text-[11.5px] font-semibold text-primary-dark transition-colors [border:1px_solid_var(--border)] hover:bg-primary-tint disabled:cursor-not-allowed disabled:opacity-60'

// The copy actions are what the rep is actually here to press, fifty times in a
// row — so they get a real target instead of the hairline chip size the
// secondary links use.
const COPY_BTN =
  'inline-flex cursor-pointer items-center gap-x-1.5 rounded-[9px] bg-surface px-3.5 py-2 text-[13px] font-semibold text-primary-dark transition-colors [border:1px_solid_var(--border)] hover:bg-primary-tint disabled:cursor-not-allowed disabled:opacity-60'

// The inline font every email client agrees on — the same Arial/Helvetica
// stack outreachDraft.js writes into the HTML. The PREVIEW follows the app
// theme (dark mode shows a dark card) for reading comfort; what the copy
// buttons put on the clipboard is draft.bodyHtml, which stays the standard
// black-on-white Arial email markup regardless of theme — so the preview's
// colors never leak into what lands in HubSpot.
const EMAIL_FONT = { fontFamily: 'Arial, Helvetica, sans-serif' }

// The bounded personalization fields a run stamps on a lead stub (see
// packRunMessage's leadStub) — real directory columns, length-capped at pack
// time. builderFacts copies them onto the draft-builder input when present; a
// stub from before they existed simply has none, and nothing changes.
const STUB_FACT_FIELDS = [
  'service_area',
  'commercial_residential',
  'annual_volume',
  'trade',
  'city_state',
  'enriched_source',
  'contact_name',
  // The bounded web-research copies the engine stamps on a researched stub
  // (chat.js leadStub): the provenance pair plus the researched facts
  // outreachDraft's readResearch reads. Only ever set by a run that really
  // researched the lead — an old or unresearched stub carries none of them,
  // so its builder input (and draft) is byte-identical to before.
  'researched',
  'researchedAt',
  'research_sources',
  'research_source_urls',
  'research_opener',
  'research_recent_activity',
  'research_buying_signal',
  'research_current_solution',
  'research_service_area',
  'research_pain_points',
  'research_exec',
]

// The subset of fields ONLY the personalization/citation feature reads — never
// taken from an intel snapshot, only from the receipt's own stub, so a
// pre-feature receipt keeps rendering byte-for-byte as it always did even when
// this account happens to hold a snapshot of the lead. (city_state, trade and
// contact_name are NOT here: the builder has always read those off snapshots.)
const PERSONALIZATION_ONLY_FIELDS = [
  'service_area',
  'commercial_residential',
  'annual_volume',
  'enriched_source',
  // Research is the RUN's story alone: an intel snapshot that carries research
  // fields (a lead someone opened and enriched later) must not rewrite what
  // THIS receipt's run actually held — deleted here, restored from the stub
  // below only when the run really stamped them.
  'researched',
  'researchMode',
  'researchedAt',
  'research_sources',
  'research_source_urls',
  'research_opener',
  'research_recent_activity',
  'research_buying_signal',
  'research_current_solution',
  'research_service_area',
  'research_pain_points',
  'research_exec',
]
// Snapshot records may carry camelCase twins (readFacts tolerates both).
const SNAKE_TO_CAMEL = {
  service_area: 'serviceArea',
  commercial_residential: 'commercialResidential',
  annual_volume: 'annualVolume',
  enriched_source: 'enrichedSource',
}

// The overlay the STORY builders take: every stub fact field EXCEPT the
// research copies. draftProvenance, draftNarrative and DraftFlowView each read
// the research signal off the stub themselves (readResearch(lead)) whenever the
// snapshot carries none, so routing it through here as well would only give
// those readers a second path to the same values — and research is deliberately
// the one thing only the receipt's own stub may ever tell.
const STORY_STUB_FACT_FIELDS = STUB_FACT_FIELDS.filter((f) => !f.startsWith('research'))

// Draft-cache namespaces. One lead can appear as an email card in one receipt
// and a call card in another; both would key on the same lead key, and two
// entries fighting over one slot would each evict the other on every mount —
// a permanent miss, and the drafting beat back on every reload.
const DRAFT_CACHE_NS = 'd:'
const CALL_CACHE_NS = 'c:'

// One frozen empty array, so a call stub with no points does not hand its memo
// a fresh identity on every render.
const EMPTY_POINTS = Object.freeze([])

/**
 * The fact input one lead's builders read — the SAME merge for the draft and
 * for the story about that draft, which is the only way the two can be held to
 * one account of what was known:
 *
 *   the local intel snapshot
 *   − PERSONALIZATION_ONLY_FIELDS   (a snapshot may not speak for the run)
 *   + the fact fields this receipt's own stub carries
 *
 * `stubFields` is that last overlay: STUB_FACT_FIELDS for the draft, and
 * STORY_STUB_FACT_FIELDS for the story, which takes the research signal off the
 * stub directly instead. Returns a plain object; `name`/`email` are not added
 * here, because the draft builders want them on the input and the story
 * builders add them for themselves.
 */
function builderFacts(lead = {}, facts = null, stubFields = STUB_FACT_FIELDS) {
  // A snapshot is an object or nothing at all; no other shape holds a named
  // fact, and every reader falls back to lead.name for the company.
  const base = { ...(typeof facts === 'object' && facts ? facts : {}) }
  // Personalization is the RUN's story, so its fields come from the stub alone.
  // An intel snapshot (a lead this account once opened) may carry service_area
  // etc. too, but letting those through would rewrite OLD receipts — a
  // pre-feature stub plus a snapshot would suddenly grow the personal sentence
  // and a sources chip, when the requirement is that old receipts render
  // byte-for-byte as they always have. City, trade and the contact name stay:
  // the builders have always read those off the snapshot.
  for (const f of PERSONALIZATION_ONLY_FIELDS) {
    delete base[f]
    delete base[SNAKE_TO_CAMEL[f]]
  }
  // The bounded fact fields the run stamped on the stub (see packRunMessage's
  // leadStub) — copied on ONLY when the stub really carries them, so an old
  // receipt's input object is exactly what it was before these fields existed
  // and its draft renders byte-for-byte as today. The stub value wins over a
  // snapshot's: it is what THIS receipt's run actually held.
  for (const f of stubFields) {
    if (lead[f] != null && lead[f] !== '') base[f] = lead[f]
  }
  return base
}

/**
 * One lead, one card — and the card is the draft. It shows the whole email the
 * way it will look when pasted (subject + body, in the real email font), the
 * two copies a rep takes, a link into HubSpot, and — on the right edge — an
 * arrow into the story of how THIS draft was composed.
 *
 * While the draft "thinks" it shows a spinner: a short, staggered beat so a
 * receipt of sixty cards resolves in a cascade rather than all at once. The
 * text itself is built synchronously and off real facts; the beat is honesty
 * about the drafting, not a fake load.
 *
 * `facts` is whatever we genuinely know beyond the stub (city, trade, a named
 * contact) — read from the local intel snapshot when this account has opened
 * the lead before, and simply absent otherwise. buildOutreachEmail leaves out
 * every clause it has no fact for, and humanizeDraft then runs the openclaw
 * de-AI-tell pass, so a sparse lead gets a shorter, plainer, true email rather
 * than an invented one.
 */
// The workspace ledger stamps the ACTOR from the verified sign-in token, so
// when the owner is viewing the app as a member ("Sign in as") every event
// is the owner's. These two fields ride in `data` so the Activity page can
// keep the owner's clicks OUT of the member's numbers — and show them apart.
function impersonationData(user) {
  return user?.impersonated ? { impersonating: true, onBehalfOf: String(user.email || '') } : {}
}

function LeadCard({
  lead,
  facts,
  index = 0,
  onOpenDraft,
  onOpenThread,
  companyId = '',
  hubspotUrl,
  searchUrl = '',
  receiptId = '',
  operationId = '',
  // The lead's own row out of the leads mirror (null while LeadCards is still
  // reading it, or when the mirror has no such row), the category it was
  // found under, and the thread this receipt lives in — what a recipient
  // switch needs to reopen the slot store-level.
  row = null,
  category = '',
  peopleReady = false,
  scope = null,
  convoId = '',
}) {
  // WHO THIS CARD IS ADDRESSED TO, and who else is on the row. `current` is
  // the rep's pick when they made one (lead.recipient), else the run's own
  // recipient; `choices` is every person the row lists, the current one
  // guaranteed among them. The stub carries no people, so the row (or, when
  // the mirror has none, the intel snapshot) is where they come from.
  const recipientSig = lead.recipient ? JSON.stringify(lead.recipient) : ''
  const people = useMemo(
    () => contactChoices(row || (facts && typeof facts === 'object' ? facts : null), lead),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [row, facts, lead.key, lead.email, lead.contact_name, recipientSig],
  )
  const recipient = people.current
  // THE COPIED MARK IS PER DRAFT, NOT PER COMPANY. It used to key on lead.key
  // alone, so a company copied from LAST week's receipt lit up "Copied" on a
  // receipt generated a minute ago — a fresh draft nobody had touched, wearing
  // a done tick. The mark now keys on the receipt AND the lead: a new run is a
  // new draft, and starts unmarked. Repaints on any copy, so the same draft
  // copied from one place is marked wherever that draft is shown.
  // AND PER PERSON: an email rewritten for somebody else at the company is a
  // different draft, so it starts unmarked too — and switching back to the
  // run's own recipient brings that draft's own mark back with it.
  const copyKey = receiptId
    ? `${lead.key}@${receiptId}${recipient.picked ? `:${recipient.key}` : ''}`
    : String(lead.key || '')
  const [copied, setCopied] = useState(() => isDraftCopied(copyKey))
  const [showThread, setShowThread] = useState(false)
  // The recipient list is open: the card lets it overflow its rounded frame,
  // which otherwise clips the listbox to the card's own height.
  const [peopleOpen, setPeopleOpen] = useState(false)

  // WHEN THE LAST EMAIL ACTUALLY WENT OUT, and the messages before it. The
  // touch number is the engine's arithmetic; these are the mirrored HubSpot
  // engagements it counted, so a rep can check one against the other. The
  // newest outbound is already summarised above as "Last email …", so it is
  // dropped from the list rather than printed twice.
  const threadMail = Array.isArray(lead.history) ? lead.history.filter((m) => m && m.at) : []
  const lastSentAt = lead.lastTouchAt || threadMail.find((m) => m.dir === 'out' && !m.bounced)?.at || ''
  const lastSentLabel = shortDate(lastSentAt)
  const earlierMessages = threadMail.filter((m) => !(m.dir === 'out' && m.at === lastSentAt))
  useEffect(() => {
    // Re-read on a key change too: a recipient switch changes which draft this
    // card shows, and its mark with it.
    setCopied(isDraftCopied(copyKey))
    return subscribeCopied(() => setCopied(isDraftCopied(copyKey)))
  }, [copyKey])
  const [status, setStatus] = useState(null)
  const timerRef = useRef(null)
  const draftTimerRef = useRef(null)

  useEffect(() => () => clearTimeout(timerRef.current), [])

  // A follow-up stub carries the cadence position the run computed (touch 2+,
  // when it was last touched, how many business days ago). Touch 1 or a stub
  // from before follow-ups existed has none of it, and the card renders
  // EXACTLY as it always has — initial outreach is unchanged.
  const followTouch = Number.isFinite(lead.touch) && lead.touch >= 2 ? lead.touch : 0
  // THE THREAD, IN THE FORM THE TEMPLATE READS IT, as one stable string.
  //
  // Two reasons it is reduced and stringified rather than passed through whole.
  // First, this value ends up inside the draft SIGNATURE, and a signature is a
  // localStorage key (draftCache.js) — six snippets of prose per lead across
  // four hundred leads is more than the whole cache is allowed to weigh, so the
  // cache would start evicting itself and the drafting beat would come back on
  // every reload. Second, the memo below must key on a value that does not
  // change identity on a repaint; an array off the stub does not, today, but
  // the comment on that memo's deps exists because a fresh identity there
  // replays the beat, and a string cannot regress that way.
  //
  // Only the fields outreachDraft.js#inboundReplyOf actually reads survive:
  // direction, date, and whether a machine sent it. The template never quotes
  // the thread — it uses it to STOP writing sentences that presume silence —
  // so subject and snippet would be weight with no reader.
  const replySig = useMemo(() => {
    const rows = Array.isArray(lead.history) ? lead.history : null
    if (!rows || !rows.length) return ''
    return JSON.stringify(
      rows.slice(0, MAX_HISTORY_MESSAGES).map((m) => ({
        dir: m?.dir === 'in' ? 'in' : m?.dir === 'out' ? 'out' : '',
        ...(m?.at ? { at: String(m.at) } : {}),
        ...(m?.bounced ? { bounced: true } : {}),
        ...(m?.auto ? { auto: true } : {}),
      })),
    )
  }, [lead.history])
  const meta = useMemo(
    () =>
      followTouch
        ? {
            touch: followTouch,
            daysSince: Number.isFinite(lead.daysSince) ? lead.daysSince : null,
            lastTouchAt: lead.lastTouchAt || '',
            touchCount: Number.isFinite(lead.touchCount) ? lead.touchCount : null,
            // The run's own admissions, stamped on the stub by the engine: a
            // lane it could not read (the inbox, the outcome log). The
            // provenance story repeats the admission instead of claiming the
            // check passed.
            ...(lead.replyUnchecked ? { replyUnchecked: true } : {}),
            ...(lead.exclUnchecked ? { exclUnchecked: true } : {}),
            // The mail itself, when the run handed any over. buildFollowupEmail
            // reads it to keep its wording true — an absent list leaves every
            // sentence exactly as it was, which is what every receipt written
            // before the list existed gets.
            ...(replySig ? { history: JSON.parse(replySig) } : {}),
          }
        : null,
    [followTouch, lead.daysSince, lead.lastTouchAt, lead.touchCount, lead.replyUnchecked, lead.exclUnchecked, replySig],
  )

  // ONE build per lead per set of facts, for the life of the browser profile.
  //
  // Three tiers, cheapest and most authoritative first, and only the LAST of
  // them is work:
  //
  //   1. THE SAVED DRAFT WINS. The receipt stores the email the run actually
  //      produced (chat.js#leadStub), built from the full row rather than the
  //      capped stub — so a reload shows exactly what Bo copied, not today's
  //      re-derivation of it. bodyHtml is derived, never stored: it is a pure
  //      function of bodyText, and storing it would double the receipt's bulk.
  //   2. THE DRAFT CACHE. An older receipt (or one whose builder declined at
  //      pack time) has no saved draft, so the card builds one — but only once.
  //      What it built is kept under lead.key + a signature of the exact facts
  //      it read, and every later mount reads it straight back.
  //   3. BUILD IT. First sight of this lead with these facts. Built, cached,
  //      and — deliberately — rendered from the cache's own round-trip, so what
  //      is on screen now is byte-for-byte what the next reload will show.
  //
  // `prebuilt` is the honest answer to "was any drafting done here?", and it is
  // what gates the beat below. Nothing in any tier fetches, enriches or
  // researches: a miss costs a pure rebuild from the stub, never a request.
  //
  // firstAnswerRef pins that answer per signature. StrictMode re-invokes this
  // memo, and React may discard a memoized value and recompute at any time —
  // the second pass would find THIS card's own write in the cache and report
  // "already drafted" for a draft that was made a millisecond ago. The first
  // answer for a given signature is the true one; later passes reuse it.
  // WAITING ON ITS OWN RESEARCH. The run marked this lead `researchPending` and
  // deliberately did not draft it: its dossier is still being produced, and an
  // email written from the import row now would be frozen onto the receipt
  // before the research it was supposed to use arrives. The card shows the wait
  // instead — LeadCards' resolver is reading the workspace cache for this exact
  // lead, and the moment the dossier lands the draft appears, written from it.
  // Nothing here fetches: the card renders state, as it always has.
  const awaitingResearch = Boolean(lead.researchPending) && !lead.draft?.bodyText
  // WAITING ON THE MODEL. The receipt left this slot open for the draft API to
  // write (chat.js#leadStub → resolveLeadDraft); until the answer lands there
  // is no email to show and NOTHING to build here — building the template
  // would put a browser-assembled email on screen seconds before the model's,
  // and the write-once slot would then refuse the model's. The card shows the
  // wait; it is a real wait, not a beat.
  const awaitingDraft = Boolean(lead.draftPending) && !lead.draft?.bodyText
  const awaiting = awaitingResearch || awaitingDraft

  const firstAnswerRef = useRef(null)
  const { draft, prebuilt } = useMemo(() => {
    // Tier 0, and it comes FIRST for a reason: a lead still owed its dossier
    // must not be drafted from its row here. Building one would put a
    // row-written email on screen (and in the draft cache) seconds before the
    // research lands, which is the exact outcome the pending state exists to
    // prevent. No draft, and the card shows the wait.
    if (awaiting) {
      return { prebuilt: false, draft: { subject: '', bodyText: '', bodyHtml: '', report: null, sources: [] } }
    }
    // Tier 0b: THE DRAFT API REFUSED. No email exists, and this emptiness is a
    // decision rather than a gap — so the rebuild tiers below must not run.
    // They exist for a receipt that never had a draft to save; reaching them
    // here would put the browser template's pitch on screen in place of the
    // refusal, which is the one outcome a refusal is for. Carried out of the
    // memo so the card can render the reason and the story can explain it.
    if (lead.draft?.skipped) {
      return {
        prebuilt: true,
        draft: {
          subject: '',
          bodyText: '',
          bodyHtml: '',
          report: null,
          sources: [],
          skipped: true,
          skipReason: String(lead.draft.skipReason || ''),
          // What KIND of refusal (internal | vendor | support | personal |
          // recruiting | not-a-prospect). The card does not render it — the
          // reason already says it in words a rep reads — but the story view
          // behind the arrow gets this object, and a refusal that arrives there
          // without its category is a decision nobody can count.
          ...(lead.draft.skipCategory ? { skipCategory: String(lead.draft.skipCategory) } : {}),
          exec: lead.draft.exec || null,
        },
      }
    }
    if (lead.draft?.bodyText) {
      return {
        prebuilt: true,
        draft: {
          subject: lead.draft.subject || '',
          bodyText: lead.draft.bodyText,
          bodyHtml: htmlFromText(lead.draft.bodyText),
          report: lead.draft.report || null,
          sources: lead.draft.sources || [],
          // The record of what wrote it — the model on the server, or the
          // browser template — carried to the provenance view untouched.
          exec: lead.draft.exec || null,
        },
      }
    }
    // No saved draft — a receipt from before drafts were saved, or a builder
    // that declined. Rebuild from what the stub carries, exactly as before.
    const base = {
      ...builderFacts(lead, facts),
      name: lead.name,
      email: lead.email,
      // A card switched to another person rebuilds for THAT person — same
      // override the host's resolver applies (runHost.js#resolveLeadDraft).
      ...(recipient.picked
        ? { email: recipient.email, contact_name: recipient.name, ...(recipient.title ? { contact_role: recipient.title } : {}) }
        : // The greeting the run worked out from the full row, when the stub
          // stored one (lib/greeting.js) — the template says hello to the
          // same person the model would have.
          lead.greeting && typeof lead.greeting === 'object'
          ? { greeting: lead.greeting }
          : {}),
    }
    // The signature is the builder INPUT, whole: change a fact and the lead
    // re-drafts; change nothing and it never does. DRAFT_VERSION rides inside
    // (draftCache.signature), so a builder change busts every lead at once.
    const sig = draftCache.signature({ kind: meta ? 'followup' : 'outreach', base, meta })
    // Namespaced: the same lead can be an email card in one receipt and a call
    // card in another, and two entries under one bare key would evict each
    // other forever — every mount a miss, every miss a beat.
    // A stub with no key gets no cache entry rather than sharing one: every
    // keyless lead would land in the same slot and evict the one before it.
    const cacheKey = sig && lead.key ? `${DRAFT_CACHE_NS}${lead.key}` : ''
    // resolve() calls the builder ONLY on a miss, and on a miss hands back the
    // round-trip of what it stored — so the first paint and every reload after
    // it are the same bytes. bodyHtml is not stored, for the same reason the
    // receipt does not store one: htmlFromText(bodyText) reproduces it exactly
    // and it is the biggest field on the object.
    const { value, prebuilt: cached } = draftCache.resolve(cacheKey, sig, () => {
      // buildFollowupEmail rotates the hook and writes a NEW subject per touch
      // (the Stage 2 playbook); both paths end in the same de-AI-tell pass.
      const built = humanizeDraft(meta ? buildFollowupEmail(base, meta) : buildOutreachEmail(base))
      return {
        subject: built.subject || '',
        bodyText: built.bodyText || '',
        report: built.report || null,
        sources: Array.isArray(built.sources) ? built.sources : [],
        // Rebuilt in the browser from the stub — an OLD receipt with no saved
        // draft. Named as such, so the Developer view never mistakes it for a
        // model draft.
        exec: templateExec(meta ? 'followup' : 'initial', 'receipt saved no draft — rebuilt from the stub in this browser'),
      }
    })
    if (firstAnswerRef.current?.sig !== sig) firstAnswerRef.current = { sig, prebuilt: cached }
    return {
      prebuilt: firstAnswerRef.current.prebuilt,
      draft: { ...value, bodyHtml: htmlFromText(value.bodyText) },
    }
    // Scalar deps on purpose (same reason as before this memo grew): a fresh
    // `lead` object identity on a re-render must NOT rebuild the draft, or the
    // [index, draft] effect below replays the drafting beat on every repaint.
    // The spread is a FIXED length (STUB_FACT_FIELDS is a module constant), so
    // the deps array length never changes between renders.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    facts,
    lead.key,
    lead.name,
    lead.email,
    lead.draft?.bodyText,
    // A refusal arrives as a draft with no bodyText, so bodyText alone cannot
    // see it land; without this the memo would keep serving the pre-refusal
    // answer (the drafting beat) after the patch had already been stored.
    lead.draft?.skipped,
    awaiting,
    meta,
    recipient.key,
    ...STUB_FACT_FIELDS.map((f) => lead[f]),
  ])

  // Starts false whenever the draft was already made, so a reload paints the
  // email immediately rather than flashing a spinner for one frame.
  const [drafting, setDrafting] = useState(() => !prebuilt)

  // THE SIGNATURE, under the sign-off — the rep's own (Settings › Email
  // signature; the profile default until one is saved), applied at render and
  // copy time and never written into the stored draft (lib/signature.js says
  // why). What is shown is what is copied: bodyHtml is rebuilt from the signed
  // text so the clipboard and the card cannot disagree.
  const { user: signer } = useAuth()
  const signature = useSignature(signer)
  const signed = useMemo(() => {
    if (!draft.bodyText) return draft
    const bodyText = withSignature(draft.bodyText, signature)
    return bodyText === draft.bodyText ? draft : { ...draft, bodyText, bodyHtml: htmlFromText(bodyText) }
  }, [draft, signature])

  const paragraphs = useMemo(
    () => String(signed.bodyText || '').split(/\n{2,}/).filter(Boolean),
    [signed],
  )

  // THE DRAFT API DECLINED TO WRITE THIS ONE. Not a failure and not a wait —
  // an answer, and the card owes the reader the answer rather than an email
  // standing in for it. Read off the draft the card is actually showing;
  // withSignature leaves a bodyless draft alone, so the flag survives `signed`.
  const skipped = Boolean(signed.skipped)
  const skipReason = String(signed.skipReason || '')
  // WHAT "Send via Apollo" SENDS: exactly the email this card shows, to the
  // person it is addressed to. The unsigned body rides along so the dialog can
  // leave our signature off when the Apollo mailbox appends its own. Nothing
  // while the email is still being written, refused, or has no address.
  const apolloEmail = useMemo(
    () =>
      skipped || drafting || !signed.bodyText || !recipient.email
        ? null
        : {
            to: recipient.email,
            to_name: recipient.name || '',
            to_title: recipient.title || '',
            company: lead.name || '',
            website: lead.website || '',
            lead_key: String(lead.key || ''),
            kind: followTouch ? 'follow_up' : 'outreach',
            subject: signed.subject || '',
            body_text: signed.bodyText,
            body_html: signed.bodyHtml,
            unsigned: { body_text: draft.bodyText, body_html: draft.bodyHtml },
          },
    [skipped, drafting, signed, draft, recipient.email, recipient.name, recipient.title, lead.name, lead.website, lead.key, followTouch],
  )
  // DID THE API ACTUALLY HAVE A THREAD IN FRONT OF IT? The refusal card used to
  // say "the draft API read this thread" on every refusal, and one of the two
  // refusal paths fires without a thread at all: the our-own-domains gate
  // (handler.py#_draft) decides on the recipient address alone, so an initial
  // outreach lead with no history whatsoever got a card claiming a conversation
  // had been read, above a card area where no thread is rendered.
  //
  // The lambda's own exec says how many messages reached it (inputs.thread), and
  // that is the count this reads — not the card's own list, because the two
  // answer different questions: what the SERVER was shown decides what the
  // server can be said to have read. An old receipt whose exec predates that
  // field says nothing, and then the thread the card itself renders is the best
  // evidence available and is used instead.
  const skipThreadSeen = Number(signed.exec?.inputs?.thread)
  const readAThread = Number.isFinite(skipThreadSeen) ? skipThreadSeen > 0 : threadMail.length > 0

  // The "thinking about the draft" beat — staggered by position, capped so the
  // last card in a long receipt is not left spinning for ten seconds.
  //
  // It plays ONLY when a draft is genuinely being made. A receipt that saved
  // its drafts — or a lead whose draft is already in the cache — is a record
  // being reopened, not work being done: pretending to draft on every reload is
  // theatre, and it is exactly what made a reload look like it was re-spending
  // on the model. Already drafted => no beat.
  useEffect(() => {
    // The real wait, not the beat: this card has no email yet because its
    // research has not come back. No timer may end it — only the dossier
    // landing (or the resolver giving up, which writes a row-drafted email and
    // clears researchPending) takes this card out of the waiting state.
    if (awaiting) {
      clearTimeout(draftTimerRef.current)
      setDrafting(true)
      return undefined
    }
    if (prebuilt) {
      setDrafting(false)
      return undefined
    }
    setDrafting(true)
    const delay = 450 + Math.min(index, 10) * 220
    clearTimeout(draftTimerRef.current)
    draftTimerRef.current = setTimeout(() => setDrafting(false), delay)
    return () => clearTimeout(draftTimerRef.current)
  }, [index, draft, prebuilt, awaiting])

  const flash = useCallback((next) => {
    setStatus(next)
    clearTimeout(timerRef.current)
    // An error stays until the next attempt: it asks the reader to do
    // something, and a message that vanishes has not been read.
    if (next && next.ok) timerRef.current = setTimeout(() => setStatus(null), 2500)
  }, [])

  // A copied card KEEPS its email on screen (2026-09-05, at the owner's
  // request). It used to collapse to a receipt with a "Show email" toggle, and
  // the collapse cost more than it saved: the rep who copies, pastes into
  // HubSpot and glances back to check a line found the email gone and a
  // button to press. The green tint, the green index and the Copied badge
  // already say everything the collapse said — copied is copied — so the box
  // stays put and nothing on the card moves when the button is pressed.

  // What the workspace ledger is told about this card — the same fields for
  // a copy and for a HubSpot click, so the Activity page can join them per
  // company and per run. `touch` is the cadence position (1 = first email).
  const ledgerData = useCallback(
    (extra) => ({
      key: String(lead.key || ''),
      company: String(lead.name || ''),
      // The address the SHOWN draft is for — the rep's pick when they switched
      // the card, else the run's — so the ledger counts the person actually
      // written to. '' for a person with no address on file.
      email: String(recipient.email || ''),
      contact: String(recipient.name || ''),
      ...(recipient.picked ? { recipientSwitched: true } : {}),
      companyId: String(companyId || ''),
      hubspotUrl: String(hubspotUrl || ''),
      receiptId: String(receiptId || ''),
      touch: followTouch || 1,
      operationId: String(operationId || ''),
      ...impersonationData(signer),
      ...extra,
    }),
    [lead.key, lead.name, recipient.email, recipient.name, recipient.picked, companyId, hubspotUrl, receiptId, followTouch, operationId, signer],
  )

  // SWITCH THE CARD TO ANOTHER PERSON. Store-level (runHost.js): the slot is
  // reopened under the pick and the receipt's resolver rewrites the email —
  // or, for somebody already written for on this receipt, swaps their email
  // straight back in. This card only asks; the store broadcast repaints it.
  const choosePerson = useCallback(
    (key) => {
      const choice = people.choices.find((c) => c.key === key)
      if (!choice || !convoId || !receiptId) return
      const res = switchLeadRecipient({
        scope,
        convoId,
        msgAt: Number(receiptId),
        leadKey: lead.key,
        choice,
        category,
      })
      if (!res.ok) {
        flash({
          ok: false,
          text:
            res.reason === 'drafting'
              ? 'Wait for this email to finish writing, then switch.'
              : res.reason === 'no-category'
                ? 'This receipt can no longer write emails — run the operation again.'
                : 'Could not switch the recipient on this card.',
        })
      }
    },
    [people.choices, convoId, receiptId, scope, lead.key, category, flash],
  )
  // The lead as the story behind the arrow should read it: addressed to the
  // person the card shows. The stub's own fields describe the run's recipient.
  const storyLead = useMemo(
    () =>
      recipient.picked
        ? {
            ...lead,
            email: recipient.email,
            contact_name: recipient.name,
            ...(recipient.title ? { contact_role: recipient.title } : {}),
          }
        : lead,
    [lead, recipient],
  )

  const copyBody = useCallback(async () => {
    const res = await copyDraft(signed.bodyHtml, signed.bodyText)
    // Only a real success marks it — a failed copy must not tell the rep they
    // have already worked this lead.
    if (res.ok) {
      markDraftCopied(copyKey)
      // Recorded server-side too (actor from the sign-in token): the copy is
      // the moment a draft becomes Bo's work, and the investor page counts
      // exactly these. Logged only on a real copy, same as the mark.
      logEvent('draft_copied', {
        actor: signer?.email || '',
        actorName: signer?.name || '',
        detail: `Copied the email for ${lead.name || lead.key || 'a lead'}`,
        data: ledgerData({ kind: 'email' }),
      })
    }
    flash(
      res.ok
        ? { ok: true, text: res.plain ? 'Copied as plain text' : 'Copied' }
        : { ok: false, text: res.error },
    )
  }, [signed, flash, copyKey, signer, lead.name, lead.key, ledgerData])

  const copySubject = useCallback(async () => {
    const res = await copyDraft(draft.subject, draft.subject)
    if (res.ok) {
      logEvent('draft_copied', {
        actor: signer?.email || '',
        actorName: signer?.name || '',
        detail: `Copied the subject for ${lead.name || lead.key || 'a lead'}`,
        data: ledgerData({ kind: 'subject' }),
      })
    }
    flash(res.ok ? { ok: true, text: 'Subject copied' } : { ok: false, text: res.error })
  }, [draft, flash, signer, lead.name, lead.key, ledgerData])

  // The three ways out to HubSpot, told to the ledger with the kind kept
  // apart: a record open, the email composer, or a search because nothing
  // matched. No preventDefault — the link opens exactly as before; the event
  // is a side note on the click.
  const noteHubspotOpen = useCallback(
    (kind, url) =>
      logEvent('hubspot_email_opened', {
        actor: signer?.email || '',
        actorName: signer?.name || '',
        detail: `${kind === 'email' ? 'Opened the HubSpot email composer' : kind === 'record' ? 'Opened the HubSpot record' : 'Searched HubSpot'} for ${lead.name || lead.key || 'a lead'}`,
        data: ledgerData({ kind, url: String(url || '') }),
      }),
    [signer, lead.name, lead.key, ledgerData],
  )

  return (
    <div
      className={`flex items-stretch rounded-[14px] ${peopleOpen ? 'overflow-visible' : 'overflow-hidden'} ${
        copied
          ? 'bg-[rgba(34,197,94,0.07)] [border:1px_solid_rgba(34,197,94,0.35)]'
          : 'bg-panel [border:1px_solid_var(--border)]'
      }`}
    >
      {/* the ordinal — which card of the run this is, so a rep working the
          stack top to bottom always knows where they are. Mirrors the arrow
          rail on the right; goes green once the draft is taken. */}
      <span
        aria-hidden="true"
        className={`flex w-[38px] flex-none items-start justify-center pt-[19px] text-[12.5px] font-semibold tabular-nums [border-right:1px_solid_var(--border)] ${
          copied ? 'text-good' : 'text-faint'
        }`}
      >
        {String(index + 1).padStart(2, '0')}
      </span>
      <div className="min-w-0 flex-1 p-4">
        {/* header: who this is, and the one link out */}
        <div className="flex items-start justify-between gap-x-3">
          <div className="min-w-0">
            <div className="flex min-w-0 items-center gap-x-2">
              <span className="truncate text-[15px] font-semibold text-ink" title={lead.name}>
                {lead.name}
              </span>
              {/* Where this lead sits in Bo's 7-touch ladder — only on a
                  follow-up stub; an initial-outreach card has no chip. */}
              {followTouch > 0 && (
                <span
                  className="inline-flex flex-none items-center rounded-full bg-panel px-2 py-[1px] text-[11px] font-semibold text-dim"
                  title={`Bo’s cadence: ${CADENCE_LINE}`}
                >
                  Touch {followTouch} · {touchLabel(followTouch)}
                </span>
              )}
              {/* "Copied", not "Sent": the app never sees the paste, so this
                  says what it can prove and no more. */}
              {copied && (
                <span
                  className="inline-flex flex-none items-center gap-x-1 rounded-full bg-[rgba(34,197,94,0.14)] px-2 py-[1px] text-[11px] font-semibold text-good"
                  title="You copied this draft — it does not mean it was sent"
                >
                  <CheckIcon className="h-[11px] w-[11px]" />
                  Copied
                </span>
              )}
            </div>
            {/* WHO THE EMAIL IS FOR — and the switch to anyone else on the
                row. Every person the row lists, first and last name with
                their role; the run's own recipient is where it starts. Picking
                somebody else rewrites the email for them on the spot and keeps
                it, so coming back is instant. Disabled while an email is being
                written (an answer on the wire is for the previous person) and
                when the row lists nobody else. Until the row has been read the
                plain address stands, as it always did. */}
            {peopleReady && people.choices.length > 0 ? (
              <div className="mt-1.5 flex min-w-0 flex-wrap items-center gap-x-2.5 gap-y-1">
                <Select
                  size="sm"
                  value={recipient.key}
                  onChange={choosePerson}
                  onOpenChange={setPeopleOpen}
                  options={people.choices.map((c) => ({
                    value: c.key,
                    label: c.name || c.email || 'Unnamed contact',
                    ...(c.title ? { hint: c.title } : {}),
                    ...(c.email ? {} : { note: 'no email on file' }),
                  }))}
                  disabled={awaiting || people.choices.length < 2}
                  ariaLabel={`Who the email for ${lead.name} is written to`}
                  buttonClassName="max-w-[min(100%,360px)]"
                />
                {recipient.email ? (
                  <span className="min-w-0 truncate text-[12px] text-faint" title={recipient.email}>
                    {recipient.email}
                  </span>
                ) : (
                  <span className="text-[12px] text-warn">
                    No email on file for {recipient.name || 'this person'} — the draft greets them by name; find
                    their address in HubSpot
                    {recipient.linkedin ? (
                      <>
                        {' '}
                        or on{' '}
                        <a
                          href={recipient.linkedin}
                          target="_blank"
                          rel="noreferrer noopener"
                          className="text-primary-dark underline"
                        >
                          LinkedIn
                        </a>
                      </>
                    ) : null}{' '}
                    before sending.
                  </span>
                )}
                {people.choices.length < 2 && (
                  <span className="text-[11.5px] text-faint">Only person on file for this company</span>
                )}
              </div>
            ) : lead.email ? (
              <div className="mt-0.5 truncate text-[12px] text-faint" title={lead.email}>
                {lead.email}
              </div>
            ) : null}
            {/* THE TOUCH THIS ONE FOLLOWS, in its own words. Read from the
                HubSpot mirror's hs_email_subject / hs_email_text — the real
                message Bo sent, not a reconstruction — so the card says what
                is being followed up ON, not just that something was. Absent
                whenever the mirror held no text, and on every receipt written
                before this existed. */}
            {(lead.lastSubject || lead.lastSnippet) && (
              <div className="mt-1 text-[12px] text-dim">
                <span className="text-faint">
                  Last email{lastSentLabel ? ` sent ${lastSentLabel}` : ''}:{' '}
                </span>
                {lead.lastSubject && <span className="font-semibold">{lead.lastSubject}</span>}
                {lead.lastSubject && lead.lastSnippet && <span className="text-faint"> — </span>}
                {lead.lastSnippet && <span className="text-faint">“{lead.lastSnippet}”</span>}
              </div>
            )}
            {/* THE THREAD, so the ladder can be checked against the mail.
                Every line is a real mirrored HubSpot engagement — date,
                direction, subject — and a rep who can see the conversation
                never has to take the touch number on faith. Collapsed by
                default: it is evidence, not the point of the card. */}
            {earlierMessages.length > 0 && (
              <div className="mt-1.5">
                <button
                  type="button"
                  className="inline-flex cursor-pointer items-center gap-x-1 border-none bg-transparent p-0 text-[11.5px] font-semibold text-dim transition-colors hover:text-ink"
                  onClick={() => setShowThread((v) => !v)}
                  aria-expanded={showThread}
                >
                  <ChevronRightIcon
                    className={`h-[11px] w-[11px] transition-transform duration-150 ${showThread ? 'rotate-90' : ''}`}
                  />
                  {showThread ? 'Hide' : 'Show'} the {earlierMessages.length} earlier{' '}
                  {earlierMessages.length === 1 ? 'email' : 'emails'} in this thread
                </button>
                {showThread && (
                  <ol className="mt-1.5 m-0 flex list-none flex-col gap-y-1.5 border-l border-line pl-3">
                    {earlierMessages.map((m, i) => (
                      <li key={`${m.at}-${i}`} className="text-[11.5px] leading-[1.45]">
                        <span className="font-mono text-faint">{shortDate(m.at) || 'undated'}</span>
                        <span className="text-faint"> · </span>
                        <span className={m.dir === 'in' ? 'font-semibold text-good' : 'font-semibold text-dim'}>
                          {m.dir === 'in' ? 'they replied' : 'we wrote'}
                        </span>
                        {m.bounced && <span className="text-warn"> · bounced, not a touch</span>}
                        {m.auto && <span className="text-faint"> · auto-reply, not an answer</span>}
                        {m.subject && (
                          <>
                            <span className="text-faint"> · </span>
                            <span className="text-dim">{m.subject}</span>
                          </>
                        )}
                        {m.snippet && <div className="mt-0.5 text-faint">“{m.snippet}”</div>}
                      </li>
                    ))}
                  </ol>
                )}
              </div>
            )}
            {lead.bouncedCount > 0 && (
              <div className="mt-0.5 text-[11.5px] text-faint">
                {lead.bouncedCount} earlier{' '}
                {lead.bouncedCount === 1 ? 'send bounced' : 'sends bounced'} and{' '}
                {lead.bouncedCount === 1 ? 'was' : 'were'} not counted as a touch — nobody received{' '}
                {lead.bouncedCount === 1 ? 'it' : 'them'}.
              </div>
            )}
          </div>
          {/* Only ever rendered for a lead that really matched a mirrored
              company, with a portal id the mirror itself reported. */}
          {hubspotUrl && (
            <a
              href={hubspotUrl}
              target="_blank"
              rel="noreferrer noopener"
              onClick={() => noteHubspotOpen('record', hubspotUrl)}
              aria-label={`Open ${lead.name} in HubSpot (new tab)`}
              className={`${CARD_BTN} flex-none no-underline`}
            >
              Open in HubSpot ↗
            </a>
          )}
        </div>

        {/* the email, in full, in the app's own theme — or the drafting beat.
            The clipboard is the source of truth for formatting, not this box. */}
        {/* Always rendered, copied or not — see the note above copyBody. */}
        {(
          <div className="mt-3 overflow-hidden rounded-[10px] bg-surface [border:1px_solid_var(--border)]">
            {drafting ? (
              <div className="flex items-center gap-x-2.5 px-4 py-7">
                <span
                  aria-hidden="true"
                  className="h-[15px] w-[15px] flex-none animate-spin rounded-full [border:1.5px_solid_var(--primary)] [border-top-color:transparent] motion-reduce:animate-none"
                />
                <span className="text-[12.5px] text-dim" role="status">
                  {awaitingResearch
                    ? `Researching ${lead.name} on the web — the draft is written from what it finds…`
                    : awaitingDraft
                      ? `Writing ${lead.name}’s email…`
                      : followTouch > 0
                      ? `Drafting touch ${followTouch} for ${lead.name}…`
                      : `Drafting outreach for ${lead.name}…`}
                </span>
              </div>
            ) : skipped ? (
              /* THE REFUSAL, WHERE THE EMAIL WOULD HAVE BEEN. The server's own
                 words, verbatim and unframed: the reason may be that the thread
                 is not a sales conversation, or that the address is one of
                 ours, or something the lambda learns to say next month —
                 wrapping it in a headline that names one of those would be this
                 card asserting a reason it was not given. The lead, its
                 thread, and every link out stay exactly as they are; what is
                 gone is only the email nobody wrote. */
              <div className="px-4 py-3.5">
                <div className="text-[10.5px] font-semibold uppercase tracking-[0.05em] text-warn">
                  No draft written
                </div>
                {skipReason ? (
                  <p className="m-0 mt-1.5 text-[13px] leading-[1.5] text-dim">
                    {readAThread
                      ? 'The draft API read this thread and declined to write an email for it: '
                      : 'The draft API declined to write an email for this lead: '}
                    <span className="text-ink">{skipReason}</span>
                  </p>
                ) : (
                  <p className="m-0 mt-1.5 text-[13px] leading-[1.5] text-dim">
                    The draft API answered without an email and without a reason. Nothing was
                    written for this lead, and nothing was put in its place.
                  </p>
                )}
                {/* WHAT TO DO NEXT, pointed at something that is actually on
                    screen. "Read the thread above" is only true when a thread
                    is rendered above — a refusal on a lead with no mail at all
                    (the our-own-domains gate needs none) would be sending the
                    rep to look at nothing. */}
                <p className="m-0 mt-2 text-[12px] leading-[1.5] text-faint">
                  {earlierMessages.length || threadMail.length
                    ? 'Read the thread above and write this one yourself — the HubSpot links below still work.'
                    : 'Write this one yourself if it should be written — the HubSpot links below still work.'}
                </p>
              </div>
            ) : (
              <>
                <div className="px-4 py-2.5 [border-bottom:1px_solid_var(--border)]">
                  <div className="text-[10.5px] font-semibold uppercase tracking-[0.05em] text-faint">
                    Subject
                  </div>
                  <div className="mt-[3px] text-[14px] font-semibold text-ink" style={EMAIL_FONT}>
                    {draft.subject}
                  </div>
                </div>
                <div className="px-4 py-3">
                  {paragraphs.map((p, i) => (
                    <p
                      key={i}
                      className="m-0 mb-3 whitespace-pre-line text-[14px] leading-[1.5] text-ink last:mb-0"
                      style={EMAIL_FONT}
                    >
                      {p}
                    </p>
                  ))}
                </div>
              </>
            )}
          </div>
        )}

        {/* the copies — a human carries this to HubSpot — and, since
            2026-10-20, "Send via Apollo": the one control here that sends, and
            only through a dialog that names the mailbox, the recipient and the
            time before anything goes (components/ApolloSendDialog.jsx). */}
        <div className="mt-3 flex flex-wrap items-center gap-1.5">
          {/* NO COPY BUTTONS ON A REFUSED DRAFT — not even greyed out. A
              disabled "Copy email" still says an email is there and something
              is merely stopping you taking it; there is no email here at all,
              and the clipboard would carry an empty string. The rest of the row
              — the composer, the thread, the search — is what a rep actually
              needs for a lead he now has to write by hand, so it stays. */}
          {!skipped && (
            <>
              <button
                type="button"
                onClick={copyBody}
                disabled={drafting}
                aria-label={`Copy the outreach email for ${lead.name}`}
                title="Copy the body — paste it straight into HubSpot's email composer"
                className={COPY_BTN}
              >
                <CopyIcon className="h-[14px] w-[14px]" />
                Copy email
              </button>
              <button
                type="button"
                onClick={copySubject}
                disabled={drafting}
                aria-label={`Copy the subject line for ${lead.name}`}
                className={COPY_BTN}
              >
                Copy subject
              </button>
              <ApolloSendControls email={apolloEmail} draftKey={copyKey} buttonClassName={COPY_BTN} />
            </>
          )}
          {/* Straight into HubSpot's email composer: ?interaction=email opens
              the record with the email pane already up, so the copy buttons and
              this link are one motion — copy, click, paste. Rendered only for a
              lead that really matched a mirrored company (same rule as the
              header link, whose URL this suffixes; it carries no query of its
              own). Opening a composer is not sending — Bo writes and sends. */}
          {hubspotUrl && (
            <a
              href={`${hubspotUrl}?interaction=email`}
              target="_blank"
              rel="noreferrer noopener"
              onClick={() => noteHubspotOpen('email', `${hubspotUrl}?interaction=email`)}
              aria-label={`Open ${lead.name} in HubSpot with the email composer (new tab)`}
              title="Open the HubSpot record with the email composer already open"
              className={`${COPY_BTN} no-underline`}
            >
              Email in HubSpot ↗
            </a>
          )}
          {/* THE THREAD SO FAR. A due touch is a decision, and the decision
              needs the history: every email both ways, and the notes, calls,
              meetings, tasks and deals in between, in the order they happened.
              Opens the same right-hand pane the draft story uses. Rendered only
              for a lead that really matched a mirrored company — there is no
              history to read without a record. */}
          {/* Also for a card whose company matched NO record but which carries
              mail the run counted (a follow-up whose contact is logged under a
              record the matcher could not settle): the timeline reads by the
              lead's own addresses too, so the thread the touch number rests
              on is always one click away. */}
          {(companyId || threadMail.length > 0) && onOpenThread && (
            <button
              type="button"
              onClick={() => onOpenThread(lead, companyId, hubspotUrl)}
              aria-label={`View the HubSpot thread for ${lead.name}`}
              title="Every email, note, call, task and deal on this company, oldest first"
              className={COPY_BTN}
            >
              View thread
            </button>
          )}
          {/* No mirrored record resolved — most NEW companies aren't in
              HubSpot yet — so the button is the portal's own search for the
              company name: an honest "find it", never a record at a guess. */}
          {!hubspotUrl && searchUrl && (
            <a
              href={searchUrl}
              target="_blank"
              rel="noreferrer noopener"
              onClick={() => noteHubspotOpen('search', searchUrl)}
              aria-label={`Search HubSpot for ${lead.name} (new tab)`}
              title="No matched record in the mirror — search the portal for this company"
              className={`${COPY_BTN} no-underline`}
            >
              Find in HubSpot ↗
            </a>
          )}
          {status && (
            <span
              role="status"
              className={`text-[11.5px] ${status.ok ? 'text-good' : 'text-danger'}`}
            >
              {status.ok && <CheckIcon className="mr-1 inline h-[11px] w-[11px]" />}
              {status.text}
            </span>
          )}
        </div>

        {/* No sources chip on the card by design — the provenance (with its
            sources) lives one click away behind the arrow, in the story view. */}
      </div>

      {/* the arrow — how THIS draft got here — as a tall affordance on the right
          edge of the card, so provenance is one click from the draft it explains.
          WITHHELD while the card is still waiting on its research: there is no
          draft yet, so there is no story of how it got here — and a story built
          from a missing draft would describe an email nobody has written. It
          appears with the draft.
          KEPT, though, on a REFUSED draft, where there is also no email: the
          difference is that something did happen and is worth explaining. The
          refusal rides on the draft's exec record (skippedDraft), so the
          Developer view behind this arrow names the deployed code that decided
          and shows the reason beside it. The story's email preview renders
          nothing when there is no bodyText, so nothing there claims an email
          exists, and draftProvenance.js#draftNode now hands a refusal to its
          own node (refusalNode) rather than describing it as an email — so the
          two views agree, and neither of them writes a sentence about a
          greeting, a quoted fact or a model call that never happened. */}
      <button
        type="button"
        onClick={() =>
          onOpenDraft(storyLead, facts, signed, followTouch > 0 ? 'followup' : undefined, meta || undefined)
        }
        disabled={awaiting}
        aria-label={
          awaitingResearch
            ? `The draft for ${lead.name} is still waiting on its research`
            : awaitingDraft
              ? `The draft for ${lead.name} is still being written`
              : skipped
                ? `Why no email was written for ${lead.name}`
                : `How the draft for ${lead.name} got here`
        }
        title={
          awaitingResearch
            ? 'Waiting on the research'
            : awaitingDraft
              ? 'Waiting on the draft'
              : skipped
                ? 'Why no email was written'
                : 'How this draft got here'
        }
        className={`group flex w-[46px] flex-none items-center justify-center bg-transparent text-faint transition-colors [border-left:1px_solid_var(--border)] ${
          awaiting
            ? 'cursor-default opacity-40'
            : 'cursor-pointer hover:bg-primary-tint hover:text-primary-dark'
        }`}
      >
        <ChevronRightIcon className="h-5 w-5 transition-transform group-hover:translate-x-0.5" />
      </button>
    </div>
  )
}

/**
 * The card list, and the one place the HubSpot lookups happen: a single index
 * fetch and a single status fetch for the whole receipt, both module-cached, so
 * sixty cards cost no more than one. Both fail soft to nothing — a mirror that
 * is down, unconfigured or forbidden leaves every card fully working, minus the
 * link.
 */
// One server-side match per lead, remembered for the session. The index is a
// single fetch but a blunt instrument (receipt stubs carry no website, and a
// contact attached to two CRM companies reads ambiguous); the /hubspot/match
// endpoint is the SAME resolver the lead detail page trusts for its "View in
// HubSpot" — richer precedence, server-side data. '' means "asked, no
// confident record", so a card only falls back to the filtered-list search
// after the real matcher has actually said no.
const SERVER_MATCH_CACHE = new Map()

async function serverCompanyId(lead) {
  const key = `${String(lead.name || '').toLowerCase()}|${String(lead.email || '').toLowerCase()}`
  if (SERVER_MATCH_CACHE.has(key)) return SERVER_MATCH_CACHE.get(key)
  const promise = (async () => {
    const { lookup } = leadLookup(
      { name: lead.name, website: lead.website || '', email: lead.email },
      lead.email,
    )
    if (!lookup.name && !lookup.email && !lookup.domain) return ''
    const match = await hubspotMatch(lookup)
    const verdict = matchVerdict(match)
    return verdict.confident && match.company_id ? String(match.company_id) : ''
  })().catch(() => {
    // A transport failure must not be remembered as "not in HubSpot".
    SERVER_MATCH_CACHE.delete(key)
    return ''
  })
  SERVER_MATCH_CACHE.set(key, promise)
  return promise
}

// ── the draft resolver moved into the host ─────────────────────────────────
//
// The research wait, the model draft call, the refusal handling and the
// template fallback all live in lib/runHost.js#resolveLeadDraft now, driven
// by ensureDraftsResolving — so the fifty emails keep writing whether or not
// this page is mounted. LeadCards keeps ONLY rendering and copy actions;
// RunMessage re-arms the host for an OLD receipt that still owes drafts.

// How much of the mirrored thread one card lists inline (the same bound the
// host sends the draft API — runHost.js#historyForDraft): six entries, the
// order the producer gave them.
const MAX_HISTORY_MESSAGES = 6

function LeadCards({ leads, onOpenDraft, onOpenThread, receiptId = '', operationId = '', scope = null, convoId = '' }) {
  const [hs, setHs] = useState({ index: null, portalId: '' })
  // EACH LEAD'S ROW, out of this browser's leads mirror — the people on it
  // are what the card's recipient dropdown offers. One read for the whole
  // receipt (the index is built once per mirror generation and shared by
  // every receipt on screen). null until it has been read: the cards show
  // the plain address until then rather than a dropdown that grows later.
  // An empty mirror is synced once when the manifest is known, the same way
  // the lead page opens a bare URL; a mirror that cannot be read offers
  // nobody beyond what the stub says.
  const { manifest, mode } = useManifest()
  const [rows, setRows] = useState(null)
  useEffect(() => {
    let alive = true
    leadRowsIndex({ manifest, mode })
      .then((map) => {
        if (alive) setRows(map instanceof Map ? map : new Map())
      })
      .catch(() => {
        if (alive) setRows(new Map())
      })
    return () => {
      alive = false
    }
  }, [manifest, mode])
  // key -> resolved company id ('' = the server matcher said no). Absent =
  // still resolving; the card shows no HubSpot affordance until the answer
  // lands, so it can never flash a search link that upgrades under the click.
  const [resolved, setResolved] = useState({})

  useEffect(() => {
    let alive = true
    Promise.all([hubspotIndex(), hubspotPortal()])
      .then(([index, portalId]) => {
        if (alive) setHs({ index: index || null, portalId: portalId || '' })
      })
      .catch(() => {
        /* hubspotIndex/hubspotPortal already resolve soft; nothing to show */
      })
    return () => {
      alive = false
    }
  }, [])

  // Resolve every card that the INDEX could not settle, through the server
  // matcher, a few at a time. Cached at module scope, so a re-render, another
  // receipt or another thread never re-asks for the same lead.
  useEffect(() => {
    if (!hs.portalId) return undefined
    let alive = true
    const pending = leads.filter((l) => l && resolved[l.key || l.name] === undefined)
    if (!pending.length) return undefined
    let i = 0
    const CONCURRENCY = 5
    const worker = async () => {
      for (;;) {
        const at = i++
        if (at >= pending.length || !alive) return
        const lead = pending[at]
        const id = await serverCompanyId(lead)
        if (!alive) return
        setResolved((prev) => ({ ...prev, [lead.key || lead.name]: id }))
      }
    }
    Promise.all(Array.from({ length: Math.min(CONCURRENCY, pending.length) }, worker)).catch(() => {})
    return () => {
      alive = false
    }
    // `resolved` intentionally omitted: it is what this effect writes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leads, hs.portalId])

  // What this account has previously opened, read once from the local intel
  // store. It is the only extra fact source available here — the receipt trims
  // its leads to {key, name, email} on purpose — and it is honest by
  // construction: a lead nobody has opened simply has no snapshot, and its
  // draft is written from the company name alone.
  const snapshots = useMemo(() => {
    const map = new Map()
    try {
      for (const rec of listIntel()) {
        if (rec?.id && rec.company) map.set(rec.id, rec.company)
      }
    } catch {
      /* a corrupt or unavailable store just means no extra facts */
    }
    return map
  }, [])

  // THE RESOLVER LOOP THAT LIVED HERE IS THE HOST'S NOW (runHost.js#
  // ensureDraftsResolving) — it ran only WHILE MOUNTED, so navigating away
  // stalled the fifty emails being written. RunMessage re-arms the host per
  // receipt; each resolved draft lands store-level via patchReceiptLead and
  // reaches these cards through the store broadcast. This component keeps
  // only rendering and copy actions.


  // No inner scroller. A receipt is part of the transcript, so it grows to its
  // full height and the CHAT scrolls — nesting a scroll area inside a message
  // means the wheel fights itself and half the leads sit behind a scrollbar
  // nobody notices.
  // Free-standing: the cards are their own containers now, not rows inside a
  // receipt bubble — no wrapper chrome, just the stack.
  // ApolloBatch: one "Send all via Apollo" bar over every card below that has
  // an email which has not gone yet (each card registers itself).
  return (
    <ApolloBatch noun="emails">
    <div className="flex flex-col gap-2">
      <div className="flex flex-col gap-2">
        {leads.map((lead, i) => {
          const facts = snapshots.get(lead.key) || null
          // Two resolvers, cheapest first: the one-fetch index (website domain
          // included so the DOMAIN tier decides first, same as the directory
          // table), then the server-side /hubspot/match — the SAME resolver the
          // lead detail page's "View in HubSpot" trusts — for anything the
          // index could not settle. The filtered-list search renders only
          // after the server matcher has actually answered "no record".
          const indexId = companyIdFor(
            { ...(facts || {}), name: lead.name, email: lead.email, website: lead.website },
            hs.index,
          )
          const serverId = resolved[lead.key || lead.name]
          const companyId = indexId || serverId || ''
          const settled = Boolean(indexId) || serverId !== undefined
          const found = rows ? rows.get(String(lead.key || '')) || null : null
          return (
            <LeadCard
              key={`${lead.key || lead.name}-${i}`}
              lead={lead}
              facts={facts}
              row={found?.row || null}
              category={found?.category || ''}
              peopleReady={rows !== null}
              scope={scope}
              convoId={convoId}
              index={i}
              onOpenDraft={onOpenDraft}
              onOpenThread={onOpenThread}
              companyId={companyId}
              hubspotUrl={hubspotCompanyUrl(hs.portalId, companyId)}
              searchUrl={
                companyId || !settled ? '' : hubspotSearchUrl(hs.portalId, lead.name)
              }
              receiptId={receiptId}
              operationId={operationId}
            />
          )
        })}
      </div>
    </div>
    </ApolloBatch>
  )
}

// A short display date ("Aug 8") off an ISO string, or '' when the date is
// unreadable — the clause is then simply not written, never guessed.
function shortDate(iso) {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  // PRINTED IN THE CALENDAR IT WAS JUDGED IN. Every day bucket in this app is
  // an America/Chicago day — the cadence gate, the task window, the Activity
  // ledger. Formatting in the VIEWER's zone instead let the two disagree: a
  // task correctly judged "due today" in Chicago printed as tomorrow's date to
  // anyone whose laptop was set further east, so the pill and the date beside
  // it contradicted each other on screen.
  return d.toLocaleDateString([], { month: 'short', day: 'numeric', timeZone: 'America/Chicago' })
}

/**
 * One due CALL touch (touch 4 or 7 of Bo's cadence), same card geometry as the
 * email cards around it. The app never dials: the phone number is a tel: link
 * Bo taps himself, the talking points are text he copies, and that is the
 * whole surface. Points were built by the run (buildCallPoints) from facts it
 * actually held and travel on the receipt — nothing is re-derived here.
 */
function CallCard({ call, index = 0, onOpenCall }) {
  const [status, setStatus] = useState(null)
  const timerRef = useRef(null)
  const beatRef = useRef(null)

  useEffect(() => () => clearTimeout(timerRef.current), [])

  const touch = Number.isFinite(call.touch) ? call.touch : 0
  const days = Number.isFinite(call.daysSince) ? call.daysSince : null
  // Stable identity for an absent points list, so the memo below is not
  // invalidated by a fresh `[]` on every render.
  const points = useMemo(() => (Array.isArray(call.points) ? call.points : EMPTY_POINTS), [call.points])

  // What "Copy talking points" puts on the clipboard: plain text, the facts on
  // this card and nothing else — and the same one-build-per-card rule the email
  // cards follow. The points themselves were built by the run and ride on the
  // receipt; this is the copy artifact assembled from them, cached under the
  // call key + a signature of exactly the fields it reads.
  //
  // firstAnswerRef pins "was this prep actually done here?" per signature — see
  // the same guard in LeadCard for why a recomputed memo must not read its own
  // write as evidence the work happened earlier.
  const firstAnswerRef = useRef(null)
  const { copyText, prepped } = useMemo(() => {
    const sig = draftCache.signature({ kind: 'call', name: call.name, phone: call.phone, touch, points })
    // Its own namespace — see the note on DRAFT_CACHE_NS in LeadCard.
    const cacheKey = sig && call.key ? `${CALL_CACHE_NS}${call.key}` : ''
    const { value, prebuilt } = draftCache.resolve(cacheKey, sig, () => {
      const head = [
        call.name,
        touch ? `Touch ${touch} · ${touchLabel(touch)}` : '',
        call.phone ? `Phone: ${call.phone}` : '',
      ]
        .filter(Boolean)
        .join('\n')
      const list = points.map((p) => `- ${p}`).join('\n')
      return [head, list].filter(Boolean).join('\n\n')
    })
    if (firstAnswerRef.current?.sig !== sig) firstAnswerRef.current = { sig, prepped: prebuilt }
    return { copyText: String(value ?? ''), prepped: firstAnswerRef.current.prepped }
  }, [call.key, call.name, call.phone, touch, points])

  // The same staggered beat the email cards use — and, like theirs, it plays
  // only the first time this card's prep is worked out. A reopened receipt is a
  // record, not work: `prepped` means these points were already laid out under
  // this exact signature, so the card paints them straight away.
  const [thinking, setThinking] = useState(() => !prepped)
  useEffect(() => {
    if (prepped) {
      setThinking(false)
      return undefined
    }
    setThinking(true)
    const delay = 450 + Math.min(index, 10) * 220
    clearTimeout(beatRef.current)
    beatRef.current = setTimeout(() => setThinking(false), delay)
    return () => clearTimeout(beatRef.current)
  }, [index, prepped])

  const flash = useCallback((next) => {
    setStatus(next)
    clearTimeout(timerRef.current)
    if (next && next.ok) timerRef.current = setTimeout(() => setStatus(null), 2500)
  }, [])

  const copyPoints = useCallback(async () => {
    const res = await copyPlain(copyText)
    flash(res.ok ? { ok: true, text: 'Copied' } : { ok: false, text: res.error })
  }, [copyText, flash])

  return (
    <div className="flex items-stretch overflow-hidden rounded-[14px] bg-panel [border:1px_solid_var(--border)]">
      <span
        aria-hidden="true"
        className="flex w-[38px] flex-none items-start justify-center pt-[19px] text-[12.5px] font-semibold tabular-nums text-faint [border-right:1px_solid_var(--border)]"
      >
        {String(index + 1).padStart(2, '0')}
      </span>
      <div className="min-w-0 flex-1 p-4">
        {/* header: who to call, where they sit in the cadence, and the number */}
        <div className="flex items-start justify-between gap-x-3">
          <div className="min-w-0">
            <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
              <span
                className="inline-flex flex-none items-center rounded-full bg-primary-tint px-2 py-[1px] text-[10.5px] font-bold uppercase tracking-[0.05em] text-primary-dark"
                title="A phone touch is due — Bo dials, the app never does"
              >
                Call due
              </span>
              <span className="truncate text-[15px] font-semibold text-ink" title={call.name}>
                {call.name}
              </span>
              {touch > 0 && (
                <span
                  className="inline-flex flex-none items-center rounded-full bg-panel px-2 py-[1px] text-[11px] font-semibold text-dim"
                  title={`Bo’s cadence: ${CADENCE_LINE}`}
                >
                  Touch {touch} · {touchLabel(touch)}
                </span>
              )}
            </div>
            {call.email && (
              <div className="mt-0.5 truncate text-[12px] text-faint" title={call.email}>
                {call.email}
              </div>
            )}
            {days !== null && (
              <div className="mt-0.5 text-[12px] text-faint">
                Last touched {shortDate(call.lastTouchAt) || 'earlier'} ·{' '}
                {days === 1 ? '1 business day' : `${days} business days`} ago
              </div>
            )}
            {/* The thread Bo is about to ring about, in its own words — same
                mirror-read evidence the email cards carry. */}
            {(call.lastSubject || call.lastSnippet) && (
              <div className="mt-1 text-[12px] text-dim">
                <span className="text-faint">Last email: </span>
                {call.lastSubject && <span className="font-semibold">{call.lastSubject}</span>}
                {call.lastSubject && call.lastSnippet && <span className="text-faint"> — </span>}
                {call.lastSnippet && <span className="text-faint">“{call.lastSnippet}”</span>}
              </div>
            )}
          </div>
          {/* A tel: link Bo taps is the one dial path allowed. No number on the
              row is said plainly — never a placeholder that looks dialable. */}
          {call.phone ? (
            <a
              href={`tel:${call.phone.replace(/[^\d+]/g, '')}`}
              aria-label={`Call ${call.name} at ${call.phone}`}
              className={`${CARD_BTN} flex-none no-underline`}
            >
              Call {call.phone}
            </a>
          ) : (
            <span className="flex-none pt-0.5 text-[11.5px] text-faint">
              No number on the row — check the HubSpot record
            </span>
          )}
        </div>

        {/* the talking points — or the prep beat */}
        <div className="mt-3 overflow-hidden rounded-[10px] bg-surface [border:1px_solid_var(--border)]">
          {thinking ? (
            <div className="flex items-center gap-x-2.5 px-4 py-7">
              <span
                aria-hidden="true"
                className="h-[15px] w-[15px] flex-none animate-spin rounded-full [border:1.5px_solid_var(--primary)] [border-top-color:transparent] motion-reduce:animate-none"
              />
              <span className="text-[12.5px] text-dim" role="status">
                Working out talking points…
              </span>
            </div>
          ) : (
            <div className="px-4 py-3">
              <div className="text-[10.5px] font-semibold uppercase tracking-[0.05em] text-faint">
                Talking points
              </div>
              {points.length > 0 ? (
                <ul className="m-0 mt-1.5 list-disc p-0 pl-5">
                  {points.map((p, i) => (
                    <li key={i} className="mb-1.5 text-[13.5px] leading-[1.5] text-ink last:mb-0">
                      {p}
                    </li>
                  ))}
                </ul>
              ) : (
                // A receipt this old (or this shed) has no points — say so
                // rather than inventing any.
                <p className="m-0 mt-1.5 text-[13px] leading-[1.5] text-dim">
                  No talking points on this receipt — check the lead’s history before dialing.
                </p>
              )}
            </div>
          )}
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-1.5">
          <button
            type="button"
            onClick={copyPoints}
            disabled={thinking || points.length === 0}
            aria-label={`Copy the talking points for ${call.name}`}
            title="Copy the talking points as plain text"
            className={COPY_BTN}
          >
            <CopyIcon className="h-[14px] w-[14px]" />
            Copy talking points
          </button>
          {status && (
            <span
              role="status"
              className={`text-[11.5px] ${status.ok ? 'text-good' : 'text-danger'}`}
            >
              {status.ok && <CheckIcon className="mr-1 inline h-[11px] w-[11px]" />}
              {status.text}
            </span>
          )}
        </div>
      </div>

      {/* the arrow — why THIS call is due, and where the points came from */}
      <button
        type="button"
        onClick={() => onOpenCall(call)}
        aria-label={`Why the call for ${call.name} is due`}
        title="Why this call is due"
        className="group flex w-[46px] flex-none cursor-pointer items-center justify-center bg-transparent text-faint transition-colors [border-left:1px_solid_var(--border)] hover:bg-primary-tint hover:text-primary-dark"
      >
        <ChevronRightIcon className="h-5 w-5 transition-transform group-hover:translate-x-0.5" />
      </button>
    </div>
  )
}

function CallCards({ calls, onOpenCall }) {
  return (
    <div className="flex flex-col">
      <p className="m-0 mb-2 px-1 text-[11px] font-bold uppercase tracking-[0.06em] text-faint">
        Call touches — Bo dials, the app never does
      </p>
      <div className="flex flex-col gap-2">
        {calls.map((call, i) => (
          <CallCard
            key={`${call.key || call.name}-${i}`}
            call={call}
            index={i}
            onOpenCall={onOpenCall}
          />
        ))}
      </div>
    </div>
  )
}

// ── the titled receipts ─────────────────────────────────────────────────────
//
// 2026-08-28: the briefing stopped being a flat card list and became titled
// sections. 2026-09-23 (owner's ask): the briefing was RECUT into two
// operations — Daily Tasks is the HubSpot-tasks section alone, Follow-Up is
// the customer lanes alone (inactive users, trials ending within 7 days,
// expired trials, payment failed), every card wearing a status pill.
// RunMessage decides PER RECEIPT which layout to draw off the `briefing`
// stamp the packer wrote: a receipt from before the recut carries no stamp and
// takes the four-section or the old lanes path exactly as it always did — the
// stored thread is the durable record, and a redeploy must not make last
// week's receipt unreadable.

/** One numbered section title — the receipt's order, worn on its sleeve. */
/**
 * A section heading that STICKS to the top of the thread while its own section
 * is on screen.
 *
 * The briefing is four sections deep and each one can run to thirty cards, so
 * by the time Bo is reading a card he is usually far below the heading that
 * says what he is looking at — and an inactive-user card and an expired-
 * subscription card look alike at a glance. Sticking the heading answers
 * "which section am I in?" without a scroll back up.
 *
 * The handoff is free: a sticky element only sticks within its own containing
 * block, so section 1's heading rides the top until section 1 scrolls past,
 * then section 2's heading takes over. That works only because nothing between
 * here and the thread's scroll container clips overflow — the receipt bubble
 * above IS overflow-hidden, which is exactly why these sections are its
 * siblings rather than its children. Keep it that way.
 *
 * The background is the thread's own ground, not transparent: cards must pass
 * UNDER the heading, not show through it. That ground is `bg-panel`
 * (--surface-2), the colour the Shell paints behind the thread — NOT `bg-bg`,
 * which despite the name is the fixed near-black of the sign-in hero and is
 * identical in both themes. Using it here put a black bar across the light
 * theme; the token that actually follows the theme is this one.
 */
function WorklistSectionTitle({ children }) {
  return (
    <p className="sticky top-0 z-10 m-0 mb-2 bg-panel px-1 pb-1.5 pt-2 text-[11px] font-bold uppercase tracking-[0.06em] text-dim">
      {children}
    </p>
  )
}

/**
 * Section 1 — the rep's HubSpot tasks (Bo's, per the roster), overdue or due
 * today, in one plain list.
 *
 * It has no controls. The status chips this docstring used to describe were
 * removed on 2026-09-02 and the per-row status badge on 2026-09-03, both at
 * the owner's request and both for the same reason: this window is filtered to
 * overdue-or-due-today, where NOT_STARTED is every untouched task's resting
 * state, so neither said anything that told one row from another. The raw
 * hs_task_status is still never collapsed into our own vocabulary — a value
 * HubSpot adds next month still badges itself without a deploy
 * (taskStatusBadge in receiptSections.js).
 */
// A task's status, badged ONLY when it is worth a reader's attention — see
// taskStatusBadge. A row sitting at the resting NOT_STARTED renders nothing
// here, which is the whole point: on a list filtered to overdue-or-due-today
// that badge was on every row and therefore told nobody anything.
function TaskStatusBadge({ status }) {
  const label = taskStatusBadge(status)
  if (!label) return null
  return (
    <span
      className="flex-none rounded-full bg-panel px-[7px] py-px text-[11px] font-semibold text-dim"
      title={`HubSpot status: ${status}`}
    >
      {label}
    </span>
  )
}

// The ONE status pill every Follow-Up card wears (owner's ask, 2026-09-23):
// the lane fact that put the card there — "Inactive 12d", "Trial ends in 3
// days", "Trial expired 30d ago", "Payment failed 2d ago" — decided by the
// pure rule in receiptSections.js, so a node test can pin the wording. Tone
// is the only thing the page adds: red for act-today, amber for soon, quiet
// for lapsed.
const PILL_TONE = {
  danger: 'bg-[#fef2f2] text-[#b91c1c] dark:bg-[rgba(185,28,28,0.2)] dark:text-[#fca5a5]',
  warn: 'bg-[rgba(217,119,6,0.12)] text-[#b45309] dark:bg-[rgba(217,119,6,0.22)] dark:text-[#fbbf24]',
  neutral: 'bg-panel text-dim',
}
function StatusPill({ item, kind }) {
  const pill = kind === 'task_followup' ? taskStatusPill(item) : customerStatusPill(item, kind)
  if (!pill) return null
  return (
    <span
      className={`inline-flex flex-none items-center rounded-full px-2 py-[1px] text-[11px] font-semibold ${PILL_TONE[pill.tone] || PILL_TONE.neutral}`}
      title="Why this customer is on the list"
    >
      {pill.label}
    </span>
  )
}

/**
 * The words for "N tasks were left off this list because they sit at one of
 * our own domains". A dropped row is never silent: the run counts what it
 * hid (judgeBoTasks in lib/agentRun.js) and this names the count, so an
 * empty section reads as "nothing due at customers" and not "nothing at all".
 *
 * THE DOMAINS NAMED ARE THE ONES THAT DID THE HIDING. The run puts the list
 * it judged against on the receipt (`excluded.entries`), so a receipt read
 * after the rep edits Settings still attributes its count to the right list
 * — the review caught the first version naming today's list under
 * yesterday's count. Only a receipt from before the run carried the list
 * falls back to what Settings holds now, and says it is a fallback.
 *
 * The other sentence this can print is not a count at all: when the mirror
 * answered without a company domain on its task rows (a lambda one deploy
 * behind), the list could not be applied, and "0 hidden" would have been the
 * same silence the count exists to prevent — so the run flags `unapplied`
 * and this says so.
 */
function HiddenTasksNote({ excluded }) {
  const carried = Array.isArray(excluded?.entries) ? excluded.entries.filter(Boolean) : null
  const [live, setLive] = useState(() => (carried ? [] : readExclusions().entries))
  useEffect(() => {
    if (carried) return undefined
    return subscribeExclusions(() => setLive(readExclusions().entries))
  }, [carried])
  const entries = carried || live
  const count = Number(excluded?.count) || 0
  const change = (
    <>
      Change the list in{' '}
      <Link to="/settings" className="font-semibold text-primary-dark no-underline hover:underline">
        Settings
      </Link>
      .
    </>
  )
  if (count <= 0) {
    if (!excluded?.unapplied) return null
    return (
      <>
        The excluded-domains list{entries.length ? ` (${entries.join(', ')})` : ''} could not be applied to
        this read — the HubSpot mirror sent no company domain on its task rows, so tasks at our own domains
        may be listed above. {change}
      </>
    )
  }
  return (
    <>
      {count} hidden —{' '}
      {entries.length
        ? `${count === 1 ? 'a task' : 'tasks'} at our own domains (${entries.join(', ')}${carried ? '' : ' — today’s list; this receipt did not record the one it used'})`
        : `${count === 1 ? 'a task' : 'tasks'} at domains that were on the excluded list when this ran (the list is empty now)`}
      . {change}
    </>
  )
}

/**
 * The quiet line Daily Tasks adds under its list (2026-09-25, after the rep's
 * field report): tasks LEFT OFF because their company was written off after
 * the task was set (every deal Closed Lost, or a "not a fit" note), counted
 * and named — a dropped row is never silent. Older overdue tasks superseded by
 * a later task on the same company are still left off the list, but no longer
 * get a line of their own (removed on request, 2026-10-11).
 */
function LeftOffTasksNote({ dismissed = null }) {
  const off = dismissed && (Number(dismissed.count) > 0 || dismissed.unapplied === true) ? dismissed : null
  if (!off) return null
  const offNames = (Array.isArray(off?.companies) ? off.companies : [])
    .map((c) => `${c.name} (${c.reason === 'closed_lost' ? 'Closed Lost' : 'a “not a fit” note'}${shortDate(c.at) ? `, ${shortDate(c.at)}` : ''})`)
    .join('; ')
  return (
    <>
      {off && Number(off.count) > 0 && (
        <p className="m-0 text-[11.5px] leading-[1.5] text-faint">
          {off.count} {off.count === 1 ? 'task' : 'tasks'} left off — the company was written off after the task was
          set{offNames ? `: ${offNames}` : ''}.
        </p>
      )}
      {off && !(Number(off.count) > 0) && off.unapplied && (
        <p className="m-0 text-[11.5px] leading-[1.5] text-warn">
          The Closed Lost / not-a-fit check could not run on this read — a written-off company may be listed above.
        </p>
      )}
    </>
  )
}

/**
 * Follow-Up's one quiet line under the four lanes (2026-09-25): the customers
 * LEFT OFF because their company is written off — every deal Closed Lost, or
 * a "not a fit" note — named with the reason, so "where did JD Long go?" has
 * an answer on the receipt itself. And, when the check could not run, the
 * admission instead: unchecked is never dressed as "nobody written off".
 */
function WrittenOffCustomersNote({ dismissed = null, unread = '' }) {
  const n = Number(dismissed?.count) || 0
  if (!n && !unread) return null
  const names = (Array.isArray(dismissed?.companies) ? dismissed.companies : [])
    .map((c) => `${c.name} (${c.reason === 'closed_lost' ? 'Closed Lost' : 'a “not a fit” note'})`)
    .join(', ')
  return (
    <div className="flex flex-col gap-y-1 px-1">
      {n > 0 && (
        <p className="m-0 text-[11.5px] leading-[1.5] text-faint">
          {n} {n === 1 ? 'customer' : 'customers'} left off — the company is written off
          {names ? `: ${names}` : ''}. An open or won deal on the company brings them back.
        </p>
      )}
      {unread && (
        <p className="m-0 text-[11.5px] leading-[1.5] text-warn">
          The Closed Lost / not-a-fit check did not fully run — {String(unread)}
        </p>
      )}
    </div>
  )
}

function HubspotTasksSection({ rows, unread, forNames = [], unreadNames = [], excluded = null, dismissed = null, receiptId = '', operationId = '', onOpenThread = null }) {
  const names = Array.isArray(forNames) ? forNames.filter(Boolean) : []
  // A receipt from the email-card run carries a kind on every row; one from
  // before it carries none and keeps the plain list it was written as.
  const emailCards = rows.some((t) => t && t.kind === 'task_followup')
  const missing = Array.isArray(unreadNames) ? unreadNames.filter(Boolean) : []
  const who = names.length ? names.join(' and ') : 'The rep'
  const whose = names.length ? `${names.join(' and ')}’s` : 'The rep’s'
  // Only a run that actually dropped something — or could not apply the list
  // at all — gets the line; a receipt from before the list existed carries no
  // `excluded` and must not claim "0 hidden".
  const hidden = excluded && (Number(excluded.count) > 0 || excluded.unapplied === true) ? excluded : null
  return (
    <div className="overflow-hidden rounded-[18px] bg-surface [border:1px_solid_var(--border)]">
      {/* A PARTIAL read is not a short day. When one rep could be read and
          another could not, the list below is real but incomplete, and saying
          so is the difference between an honest section and the bug this whole
          lane was rewritten to fix. */}
      {!unread && missing.length > 0 && (
        <p className="m-0 px-4 pt-3 text-[12px] font-semibold text-warn">
          {missing.join(' and ')}’s HubSpot tasks could not be read — they are missing from this
          list, not absent from HubSpot.
        </p>
      )}
      {unread ? (
        // Unread is never rendered as empty: "no tasks" and "the tasks could
        // not be read" are different facts.
        <p className="m-0 px-4 py-3 text-[12.5px] font-semibold text-warn">
          {whose} HubSpot tasks could not be read — this section cannot say what is overdue.
        </p>
      ) : rows.length === 0 ? (
        // The empty state carries the hidden count too: "no task overdue or
        // due today" must never read as "nothing was left off". When every
        // due task sat at our own domain, this is the only place that says so.
        <>
          <p className={`m-0 px-4 text-[12.5px] text-dim ${hidden || dismissed ? 'pt-3 pb-1' : 'py-3'}`}>
            {who} has no HubSpot task overdue or due today.
          </p>
          {hidden && (
            <p className="m-0 px-4 pb-3 text-[11.5px] text-faint">
              <HiddenTasksNote excluded={hidden} />
            </p>
          )}
          {dismissed && (
            <div className="flex flex-col gap-y-1 px-4 pb-3">
              <LeftOffTasksNote dismissed={dismissed} />
            </div>
          )}
        </>
      ) : emailCards ? (
        /* THE EMAIL CARDS (owner's ask, 2026-10-09): one card per task, the
           email drafted from the task, the real thread and the rep's notes.
           A task nobody can be written to (no contact with an address, or
           only our own) keeps its row and says why — nothing is invented to
           fill a card. Rendered OUTSIDE the section bubble's padding so each
           card is its own container, like every other card stack. */
        <ApolloBatch noun="follow-ups" className="mx-2 mt-2">
        <div className="flex flex-col gap-2 p-2">
          {rows.map((t, i) =>
            t.recipient ? (
              <CustomerCard
                key={t.key || t.id || `${t.subject}-${i}`}
                item={t}
                slot="tasks"
                index={i}
                receiptId={receiptId}
                operationId={operationId}
                onOpenThread={onOpenThread}
              />
            ) : (
              <div
                key={t.key || t.id || `${t.subject}-${i}`}
                className="flex items-stretch overflow-hidden rounded-[14px] bg-panel [border:1px_solid_var(--border)]"
              >
                <span
                  aria-hidden="true"
                  className="flex w-[38px] flex-none items-start justify-center pt-[15px] text-[12.5px] font-semibold tabular-nums text-faint [border-right:1px_solid_var(--border)]"
                >
                  {String(i + 1).padStart(2, '0')}
                </span>
                <div className="min-w-0 flex-1 px-4 py-3">
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                    <span className="truncate text-[15px] font-semibold text-ink">{t.company || t.subject || '(no subject)'}</span>
                    <StatusPill item={t} kind="task_followup" />
                  </div>
                  <div className="mt-1 text-[12px] text-dim">{taskEvidenceLine(t)}</div>
                  <p className="m-0 mt-1.5 text-[12.5px] leading-[1.5] text-warn">
                    No email drafted — {t.noRecipientReason || 'nobody on this company can be written to'}. The task is still yours in HubSpot.
                  </p>
                  {t.hubspotUrl && (
                    <a
                      href={t.hubspotUrl}
                      target="_blank"
                      rel="noreferrer noopener"
                      className="mt-2 inline-block text-[12px] font-semibold text-primary-dark no-underline hover:text-ink"
                    >
                      Open task ↗
                    </a>
                  )}
                </div>
              </div>
            ),
          )}
          {hidden && (
            <p className="m-0 px-2 pb-1 text-[11.5px] text-faint">
              <HiddenTasksNote excluded={hidden} />
            </p>
          )}
          {dismissed && (
            <div className="flex flex-col gap-y-1 px-2 pb-1">
              <LeftOffTasksNote dismissed={dismissed} />
            </div>
          )}
        </div>
        </ApolloBatch>
      ) : (
        <>
          <ul className="m-0 list-none px-4 pb-3 pt-1.5">
            {rows.map((t, i) => (
              <li
                key={t.id || `${t.subject}-${i}`}
                className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 py-[5px] text-[12.5px] leading-[1.5]"
              >
                {(t.overdue || t.dueToday) && (
                  <span
                    className={`flex-none rounded-full px-[7px] py-px text-[11px] font-semibold ${
                      t.overdue
                        ? 'bg-[#fef2f2] text-[#b91c1c] dark:bg-[rgba(185,28,28,0.2)] dark:text-[#fca5a5]'
                        : 'bg-[rgba(217,119,6,0.1)] text-[#b45309] dark:bg-[rgba(217,119,6,0.22)] dark:text-[#fbbf24]'
                    }`}
                  >
                    {t.overdue ? 'Overdue' : 'Due today'}
                  </span>
                )}
                <span className="min-w-0 text-[14px] font-semibold text-ink">
                  {t.subject || '(no subject)'}
                </span>
                {names.length > 1 && t.rep && <span className="flex-none text-faint">· {t.rep}</span>}
                {shortDate(t.dueAt) && (
                  <span className="flex-none text-faint">due {shortDate(t.dueAt)}</span>
                )}
                <TaskStatusBadge status={t.status} />
                {/* The way out to the task in HubSpot: the company name when
                    the row names one, an explicit link when it does not —
                    never a link dressed as a fact the row does not hold. */}
                {t.hubspotUrl ? (
                  <a
                    href={t.hubspotUrl}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="flex-none text-[12px] font-semibold text-primary-dark no-underline hover:text-ink"
                  >
                    {t.company ? `${t.company} ↗` : 'View in HubSpot ↗'}
                  </a>
                ) : t.company ? (
                  <span className="flex-none text-faint">· {t.company}</span>
                ) : null}
              </li>
            ))}
            {hidden && (
              <li className="pt-1 text-[11.5px] text-faint">
                <HiddenTasksNote excluded={hidden} />
              </li>
            )}
            {dismissed && (
              <li className="flex flex-col gap-y-1 pt-1">
                <LeftOffTasksNote dismissed={dismissed} />
              </li>
            )}
          </ul>
        </>
      )}
    </div>
  )
}

/**
 * One customer check-in card — the inactive-user / expired-subscription
 * lanes' counterpart to LeadCard, and deliberately the same behaviour where
 * the two overlap: the same pending/model/fallback draft states ("Writing …"
 * while the host's resolver is still owed the model's answer), the same
 * per-receipt Copied mark, the same ledger events (draft_copied /
 * hubspot_email_opened) with impersonation carried, and the same refusal
 * rendering — the reason where the email would have been, no copy buttons,
 * never a template standing in for a decision.
 *
 * What it does NOT have is research: a customer is not researched. Every
 * fact on this card was read from the HubSpot mirror by the run itself, and
 * the evidence line states only fields the item actually carries
 * (receiptSections.js#customerEvidenceLine).
 */
function CustomerCard({ item, slot, index = 0, receiptId = '', operationId = '', onOpenThread = null }) {
  // The kind travels on the stub; the array it sits in is the backstop for a
  // stub packed before the kind existed — the slot cannot lie about which
  // lane the run put the item in. Same rule as the host's resolver.
  const kind =
    item.kind === 'expired_winback' || item.kind === 'inactive_user' || item.kind === 'payment_failed' || item.kind === 'task_followup'
      ? item.kind
      : slot === 'expiredSubscriptions'
        ? 'expired_winback'
        : slot === 'paymentFailed'
          ? 'payment_failed'
          : slot === 'tasks'
            ? 'task_followup'
            : 'inactive_user'
  // WHICH LANE CARRIES AN EMAIL AT ALL. The expired lane stopped carrying one
  // on 2026-09-03, at the owner's request — the win-back note is Bo's own, so
  // the card is the facts plus the two doors into HubSpot, exactly like the
  // trial card. Decided from the item's own kind rather than from the slot so a
  // STORED receipt written before the change (its expired items still hold a
  // draft, and some are even marked copied) renders under today's rule and not
  // its own: what the reader must not be shown is an email to copy, and that is
  // as true of an old receipt as a new one.
  // The payment-failed lane (2026-09-23) never had one: same shape as expired.
  // A TASK card (2026-10-09) is an email by definition — the task is what the
  // email does — so it carries one whenever somebody was found to write to.
  const task = kind === 'task_followup'
  const drafted = kind === 'inactive_user' || (task && Boolean(item.recipient))
  // The task card's two doors: the contact record (where the email is sent
  // from) and the task itself. A customer card has one record for both.
  const recordUrl = task ? String(item.contactUrl || '') : String(item.hubspotUrl || '')
  const emailNoun = kind === 'expired_winback' ? 'win-back' : task ? 'follow-up' : 'check-in'
  // The resolver's stable slot key (cust:<email>); an OLD stub from before it
  // existed falls back to the address so the Copied mark still has an
  // identity to hang on. Per receipt, same as LeadCard, for the same reason.
  const itemKey = String(item.key || (item.email ? `cust:${item.email}` : '') || '')
  const copyKey = receiptId && itemKey ? `${itemKey}@${receiptId}` : itemKey
  const [copied, setCopied] = useState(() => isDraftCopied(copyKey))
  useEffect(() => subscribeCopied(() => setCopied(isDraftCopied(copyKey))), [copyKey])
  const [status, setStatus] = useState(null)
  const timerRef = useRef(null)
  useEffect(() => () => clearTimeout(timerRef.current), [])

  // Three draft states, LeadCard's minus the research wait a customer never
  // has: waiting on the model (the host's resolver fills the write-once
  // slot), a refusal (an ANSWER, not a wait), or an email — which on an old
  // receipt is the eagerly-packed template, rendered exactly as it was saved.
  const awaiting = Boolean(item.draftPending) && !item.draft?.bodyText && !item.draft?.skipped
  const skipped = Boolean(item.draft?.skipped)
  const skipReason = String(item.draft?.skipReason || '')

  const draft = useMemo(
    () =>
      item.draft?.bodyText
        ? {
            subject: item.draft.subject || '',
            bodyText: item.draft.bodyText,
            // Derived, never stored — a pure function of bodyText, same as
            // every receipt draft.
            bodyHtml: htmlFromText(item.draft.bodyText),
          }
        : { subject: '', bodyText: '', bodyHtml: '' },
    // Scalar deps on purpose: a fresh item identity per store merge must not
    // re-derive anything (see LeadCard's memo for the history).
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [item.draft?.subject, item.draft?.bodyText],
  )

  // The rep's signature under the sign-off, applied at render and copy time
  // and never written into the stored draft — what is shown is what is
  // copied, same as LeadCard (the templates end at "Best," on purpose).
  const { user: signer } = useAuth()
  const signature = useSignature(signer)
  const signed = useMemo(() => {
    if (!draft.bodyText) return draft
    const bodyText = withSignature(draft.bodyText, signature)
    return bodyText === draft.bodyText
      ? draft
      : { ...draft, bodyText, bodyHtml: htmlFromText(bodyText) }
  }, [draft, signature])
  const paragraphs = useMemo(
    () => String(signed.bodyText || '').split(/\n{2,}/).filter(Boolean),
    [signed],
  )
  const hasEmail = Boolean(signed.bodyText)
  // What "Send via Apollo" sends — the shown email to the one address the
  // draft was written for (`recipient`, the address the draft route allows).
  const to = String(item.recipient || item.email || '')
  const apolloEmail = useMemo(
    () =>
      !drafted || skipped || awaiting || !hasEmail || !to
        ? null
        : {
            to,
            to_name: String(item.name || ''),
            to_title: String(item.contactRole || ''),
            company: String(item.company || ''),
            lead_key: itemKey,
            kind,
            subject: signed.subject || '',
            body_text: signed.bodyText,
            body_html: signed.bodyHtml,
            unsigned: { body_text: draft.bodyText, body_html: draft.bodyHtml },
          },
    [drafted, skipped, awaiting, hasEmail, to, item.name, item.contactRole, item.company, itemKey, kind, signed, draft],
  )

  const flash = useCallback((next) => {
    setStatus(next)
    clearTimeout(timerRef.current)
    if (next && next.ok) timerRef.current = setTimeout(() => setStatus(null), 2500)
  }, [])

  // Same ledger fields as LeadCard so the Activity page joins copies and
  // HubSpot opens per receipt whichever card they came from. `kind` in the
  // data stays the click/copy kind (email / subject / record) because that is
  // what the ledger's existing readers filter on; `draftKind` names the lane
  // (inactive_user / expired_winback).
  const ledgerData = useCallback(
    (extra) => ({
      key: itemKey,
      company: String(item.company || ''),
      name: String(item.name || ''),
      email: String(item.email || ''),
      companyId: String(item.companyId || ''),
      hubspotUrl: String(item.hubspotUrl || ''),
      receiptId: String(receiptId || ''),
      operationId: String(operationId || ''),
      draftKind: kind,
      ...impersonationData(signer),
      ...extra,
    }),
    [itemKey, item.company, item.name, item.email, item.companyId, item.hubspotUrl, receiptId, operationId, kind, signer],
  )

  const copyBody = useCallback(async () => {
    const res = await copyDraft(signed.bodyHtml, signed.bodyText)
    // Only a real success marks it — a failed copy must not tell the rep they
    // have already worked this customer.
    if (res.ok) {
      markDraftCopied(copyKey)
      logEvent('draft_copied', {
        actor: signer?.email || '',
        actorName: signer?.name || '',
        detail: `Copied the ${emailNoun} email for ${item.name || item.email || 'a customer'}`,
        data: ledgerData({ kind: 'email' }),
      })
    }
    flash(
      res.ok
        ? { ok: true, text: res.plain ? 'Copied as plain text' : 'Copied' }
        : { ok: false, text: res.error },
    )
  }, [signed, flash, copyKey, signer, kind, item.name, item.email, ledgerData])

  const copySubject = useCallback(async () => {
    const res = await copyDraft(draft.subject, draft.subject)
    if (res.ok) {
      logEvent('draft_copied', {
        actor: signer?.email || '',
        actorName: signer?.name || '',
        detail: `Copied the subject for ${item.name || item.email || 'a customer'}`,
        data: ledgerData({ kind: 'subject' }),
      })
    }
    flash(res.ok ? { ok: true, text: 'Subject copied' } : { ok: false, text: res.error })
  }, [draft, flash, signer, item.name, item.email, ledgerData])

  const noteHubspotOpen = useCallback(
    (clickKind, url) =>
      logEvent('hubspot_email_opened', {
        actor: signer?.email || '',
        actorName: signer?.name || '',
        detail: `${clickKind === 'email' ? 'Opened the HubSpot email composer' : 'Opened the HubSpot record'} for ${item.name || item.email || 'a customer'}`,
        data: ledgerData({ kind: clickKind, url: String(url || '') }),
      }),
    [signer, item.name, item.email, ledgerData],
  )

  // Only clauses backed by held fields — an item with no version, no counts
  // and no dates gets an empty line, and the line is then not rendered.
  const evidence = task ? taskEvidenceLine(item) : customerEvidenceLine(item, kind)

  return (
    <div
      className={`flex items-stretch overflow-hidden rounded-[14px] ${
        copied
          ? 'bg-[rgba(34,197,94,0.07)] [border:1px_solid_rgba(34,197,94,0.35)]'
          : 'bg-panel [border:1px_solid_var(--border)]'
      }`}
    >
      <span
        aria-hidden="true"
        className={`flex w-[38px] flex-none items-start justify-center pt-[19px] text-[12.5px] font-semibold tabular-nums [border-right:1px_solid_var(--border)] ${
          copied ? 'text-good' : 'text-faint'
        }`}
      >
        {String(index + 1).padStart(2, '0')}
      </span>
      <div className="min-w-0 flex-1 p-4">
        {/* evidence header: who this customer is, and the one link out */}
        <div className="flex items-start justify-between gap-x-3">
          <div className="min-w-0">
            <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
              <span
                className="truncate text-[15px] font-semibold text-ink"
                title={item.name || item.email || item.company}
              >
                {item.name || item.email || item.company}
              </span>
              {item.company && (item.name || item.email) && (
                <span className="truncate text-[12.5px] text-dim" title={item.company}>
                  · {item.company}
                </span>
              )}
              <StatusPill item={item} kind={kind} />
              {copied && (
                <span
                  className="inline-flex flex-none items-center gap-x-1 rounded-full bg-[rgba(34,197,94,0.14)] px-2 py-[1px] text-[11px] font-semibold text-good"
                  title="You copied this draft — it does not mean it was sent"
                >
                  <CheckIcon className="h-[11px] w-[11px]" />
                  Copied
                </span>
              )}
            </div>
            {item.email && item.name && (
              <div className="mt-0.5 truncate text-[12px] text-faint" title={item.email}>
                {item.email}
              </div>
            )}
            {evidence && <div className="mt-1 text-[12px] text-dim">{evidence}</div>}
            {/* Contact history, when this run's mail read held any for the
                address — evidence, so Bo knows whether this note lands cold.
                Not on a TASK card: taken off on request (2026-10-11); the
                thread itself is one press away under View thread. */}
            {!task && (item.lastOutboundAt || item.lastInboundAt) && (
              <div className="mt-0.5 text-[11.5px] text-faint">
                {[
                  shortDate(item.lastOutboundAt) ? `we last wrote ${shortDate(item.lastOutboundAt)}` : '',
                  shortDate(item.lastInboundAt) ? `they last wrote ${shortDate(item.lastInboundAt)}` : '',
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </div>
            )}
          </div>
          {item.hubspotUrl && (
            <a
              href={item.hubspotUrl}
              target="_blank"
              rel="noreferrer noopener"
              onClick={() => noteHubspotOpen('record', item.hubspotUrl)}
              aria-label={task ? `Open the HubSpot task for ${item.name || item.email || item.company} (new tab)` : `Open ${item.name || item.email} in HubSpot (new tab)`}
              className={`${CARD_BTN} flex-none no-underline`}
            >
              {task ? 'Open task ↗' : 'Open in HubSpot ↗'}
            </a>
          )}
        </div>

        {/* THE REP'S OWN NOTES, on a task card only: the current situation in
            their words, which is what the email was drafted from. Quoted so
            the rep can check the email against them, never rewritten. */}
        {task && Array.isArray(item.notes) && item.notes.length > 0 && (
          <div className="mt-2 rounded-[10px] bg-surface px-3 py-2 [border:1px_solid_var(--border)]">
            <div className="text-[10.5px] font-semibold uppercase tracking-[0.05em] text-faint">Your notes</div>
            {item.notes.map((n, i) => (
              <p key={i} className="m-0 mt-1 text-[12.5px] leading-[1.5] text-dim">
                {shortDate(n.at) ? <span className="text-faint">{shortDate(n.at)} · </span> : null}
                {n.text}
              </p>
            ))}
          </div>
        )}

        {/* the email — or the wait, or the refusal. Stays on screen after a
            copy, same as LeadCard (the green tint is the whole signal). A lane
            with no draft (`drafted` false) renders no box in any state: there
            is nothing being written and nothing refused. */}
        {drafted && (
          <div className="mt-3 overflow-hidden rounded-[10px] bg-surface [border:1px_solid_var(--border)]">
            {awaiting ? (
              <div className="flex items-center gap-x-2.5 px-4 py-7">
                <span
                  aria-hidden="true"
                  className="h-[15px] w-[15px] flex-none animate-spin rounded-full [border:1.5px_solid_var(--primary)] [border-top-color:transparent] motion-reduce:animate-none"
                />
                <span className="text-[12.5px] text-dim" role="status">
                  Writing {item.name || item.email || 'this customer'}’s {emailNoun} email…
                </span>
              </div>
            ) : skipped ? (
              /* THE REFUSAL, where the email would have been — the server's
                 own words, verbatim and unframed, exactly as LeadCard renders
                 it. No template stands in for a decision. */
              <div className="px-4 py-3.5">
                <div className="text-[10.5px] font-semibold uppercase tracking-[0.05em] text-warn">
                  No draft written
                </div>
                {skipReason ? (
                  <p className="m-0 mt-1.5 text-[13px] leading-[1.5] text-dim">
                    The draft API declined to write an email for this customer:{' '}
                    <span className="text-ink">{skipReason}</span>
                  </p>
                ) : (
                  <p className="m-0 mt-1.5 text-[13px] leading-[1.5] text-dim">
                    The draft API answered without an email and without a reason. Nothing was
                    written for this customer, and nothing was put in its place.
                  </p>
                )}
                <p className="m-0 mt-2 text-[12px] leading-[1.5] text-faint">
                  Write this one yourself if it should be written
                  {item.hubspotUrl ? ' — the HubSpot links still work.' : '.'}
                </p>
              </div>
            ) : hasEmail ? (
              <>
                <div className="px-4 py-2.5 [border-bottom:1px_solid_var(--border)]">
                  <div className="text-[10.5px] font-semibold uppercase tracking-[0.05em] text-faint">
                    Subject
                  </div>
                  <div className="mt-[3px] text-[14px] font-semibold text-ink" style={EMAIL_FONT}>
                    {signed.subject}
                  </div>
                </div>
                <div className="px-4 py-3">
                  {paragraphs.map((p, i) => (
                    <p
                      key={i}
                      className="m-0 mb-3 whitespace-pre-line text-[14px] leading-[1.5] text-ink last:mb-0"
                      style={EMAIL_FONT}
                    >
                      {p}
                    </p>
                  ))}
                </div>
              </>
            ) : (
              // No draft, no pending mark, no refusal: a shed or half-synced
              // receipt. Said plainly rather than rebuilt — the templates
              // belong to the resolver's fallback path, not to a render.
              <p className="m-0 px-4 py-3.5 text-[13px] leading-[1.5] text-dim">
                No draft on this receipt — write this one yourself
                {item.hubspotUrl ? '; the HubSpot links still work.' : '.'}
              </p>
            )}
          </div>
        )}

        {/* the copies — a human carries this to HubSpot — plus "Send via
            Apollo", which sends only after its confirmation dialog. */}
        <div className="mt-3 flex flex-wrap items-center gap-1.5">
          {/* No copy buttons on a refusal — same reasoning as LeadCard: there
              is no email at all, and a disabled button would claim otherwise.
              And none on a lane that never had one (`drafted`), for the same
              reason said once more: a Copy button over no email is a promise
              the card cannot keep. */}
          {drafted && !skipped && (
            <>
              <button
                type="button"
                onClick={copyBody}
                disabled={awaiting || !hasEmail}
                aria-label={`Copy the email for ${item.name || item.email}`}
                title="Copy the body — paste it straight into HubSpot's email composer"
                className={COPY_BTN}
              >
                <CopyIcon className="h-[14px] w-[14px]" />
                Copy email
              </button>
              <button
                type="button"
                onClick={copySubject}
                disabled={awaiting || !hasEmail}
                aria-label={`Copy the subject line for ${item.name || item.email}`}
                className={COPY_BTN}
              >
                Copy subject
              </button>
              <ApolloSendControls email={apolloEmail} draftKey={copyKey} buttonClassName={COPY_BTN} />
            </>
          )}
          {/* Straight into the composer on the CONTACT record — the customer
              item's hubspotUrl is the contact's 0-1 URL, and ?interaction=
              email opens it with the email pane already up. Copy, click,
              paste; Bo writes and sends. */}
          {recordUrl && (
            <a
              href={`${recordUrl}?interaction=email`}
              target="_blank"
              rel="noreferrer noopener"
              onClick={() => noteHubspotOpen('email', `${recordUrl}?interaction=email`)}
              aria-label={`Open ${item.name || item.email} in HubSpot with the email composer (new tab)`}
              title="Open the HubSpot contact with the email composer already open"
              className={`${COPY_BTN} no-underline`}
            >
              Email in HubSpot ↗
            </a>
          )}
          {/* THE CONVERSATION SO FAR, beside the composer — the same pane a
              lead card opens. On a card with no draft it is the whole of the
              preparation: before writing a win-back by hand, the one thing
              worth reading is what was already said. Rendered only for a
              customer that resolved to a mirrored company, because without a
              record there is no history to read. */}
          {item.companyId && onOpenThread && (
            <button
              type="button"
              onClick={() => onOpenThread(item, String(item.companyId), String(item.hubspotUrl || ''))}
              aria-label={`View the HubSpot thread for ${item.name || item.email}`}
              title="Every email, note, call, task and deal on this company, oldest first"
              className={COPY_BTN}
            >
              View thread
            </button>
          )}
          {status && (
            <span
              role="status"
              className={`text-[11.5px] ${status.ok ? 'text-good' : 'text-danger'}`}
            >
              {status.ok && <CheckIcon className="mr-1 inline h-[11px] w-[11px]" />}
              {status.text}
            </span>
          )}
        </div>
      </div>
    </div>
  )
}

// ACTIVE TRIALS — the one customer card with NO email on it. Section 4 exists
// so Bo can see who is on trial right now and go push them to convert; the
// pitch is his, by phone or from HubSpot, so the card carries the facts that
// decide it — when the trial ends, whether the product was ever opened, what
// was built, whether a card is on file — and the two doors into HubSpot. No
// draft box and no copy buttons: a draft nobody asked for would be a template
// dressed as a plan. (If one is ever wanted, it is a 'trial_convert' kind on
// POST /api/enrich/draft plus a fallback template in lib/outreachDraft.js —
// the same pair the check-in lanes use — and the resolver's CUSTOMER_SLOTS.)
function TrialCard({ item, index = 0, receiptId = '', operationId = '' }) {
  const { user: signer } = useAuth()
  const itemKey = String(item.key || (item.email ? `cust:${item.email}` : '') || '')
  // Same ledger fields as CustomerCard, so the Activity page joins HubSpot
  // opens per receipt whichever card they came from; draftKind names the lane.
  const noteHubspotOpen = useCallback(
    (clickKind, url) =>
      logEvent('hubspot_email_opened', {
        actor: signer?.email || '',
        actorName: signer?.name || '',
        detail: `${clickKind === 'email' ? 'Opened the HubSpot email composer' : 'Opened the HubSpot record'} for ${item.name || item.email || 'a trial user'}`,
        data: {
          key: itemKey,
          company: String(item.company || ''),
          name: String(item.name || ''),
          email: String(item.email || ''),
          companyId: String(item.companyId || ''),
          hubspotUrl: String(item.hubspotUrl || ''),
          receiptId: String(receiptId || ''),
          operationId: String(operationId || ''),
          draftKind: 'trial_convert',
          ...impersonationData(signer),
          kind: clickKind,
          url: String(url || ''),
        },
      }),
    [signer, item.name, item.email, item.company, item.companyId, item.hubspotUrl, itemKey, receiptId, operationId],
  )
  // Only clauses backed by held fields (receiptSections.js) — an item with no
  // end date, no usage and no card value gets an empty line, not a guess.
  const evidence = customerEvidenceLine(item, 'trial_convert')
  return (
    <div className="flex items-stretch overflow-hidden rounded-[14px] bg-panel [border:1px_solid_var(--border)]">
      <span
        aria-hidden="true"
        className="flex w-[38px] flex-none items-start justify-center pt-[19px] text-[12.5px] font-semibold tabular-nums text-faint [border-right:1px_solid_var(--border)]"
      >
        {String(index + 1).padStart(2, '0')}
      </span>
      <div className="min-w-0 flex-1 p-4">
        <div className="flex items-start justify-between gap-x-3">
          <div className="min-w-0">
            <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
              <span className="truncate text-[15px] font-semibold text-ink" title={item.name || item.email}>
                {item.name || item.email}
              </span>
              {item.company && (
                <span className="truncate text-[12.5px] text-dim" title={item.company}>
                  · {item.company}
                </span>
              )}
              {/* The status pill, always: how long this trial has left is the
                  whole reason the card is on the list. Red inside three days. */}
              <StatusPill item={item} kind="trial_convert" />
            </div>
            {item.email && item.name && (
              <div className="mt-0.5 truncate text-[12px] text-faint" title={item.email}>
                {item.email}
              </div>
            )}
            {evidence && <div className="mt-1 text-[12px] text-dim">{evidence}</div>}
            {/* Contact history, when this run's mail read held any for the
                address — so Bo knows whether this call lands cold. */}
            {(item.lastOutboundAt || item.lastInboundAt) && (
              <div className="mt-0.5 text-[11.5px] text-faint">
                {[
                  shortDate(item.lastOutboundAt) ? `we last wrote ${shortDate(item.lastOutboundAt)}` : '',
                  shortDate(item.lastInboundAt) ? `they last wrote ${shortDate(item.lastInboundAt)}` : '',
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </div>
            )}
          </div>
          {item.hubspotUrl && (
            <a
              href={item.hubspotUrl}
              target="_blank"
              rel="noreferrer noopener"
              onClick={() => noteHubspotOpen('record', item.hubspotUrl)}
              aria-label={`Open ${item.name || item.email} in HubSpot (new tab)`}
              className={`${CARD_BTN} flex-none no-underline`}
            >
              Open in HubSpot ↗
            </a>
          )}
        </div>
        {/* The way to the pitch: straight into the composer on the contact
            record. Nothing here sends, and nothing here writes the email. */}
        {item.hubspotUrl && (
          <div className="mt-3 flex flex-wrap items-center gap-1.5">
            <a
              href={`${item.hubspotUrl}?interaction=email`}
              target="_blank"
              rel="noreferrer noopener"
              onClick={() => noteHubspotOpen('email', `${item.hubspotUrl}?interaction=email`)}
              aria-label={`Open ${item.name || item.email} in HubSpot with the email composer (new tab)`}
              title="Open the HubSpot record with the email composer already open"
              className={`${COPY_BTN} no-underline`}
            >
              Email in HubSpot ↗
            </a>
          </div>
        )}
      </div>
    </div>
  )
}

/**
 * One customer lane's body — the unread admission, the honest empty line, or
 * the card stack with its "+N more" — under the numbered title RunMessage
 * renders. `unread` is the engine's own reason string (usersUnread): truthy
 * means the lane could not be READ, which is never rendered as empty.
 */
function CustomerLane({ items, total, unread, emptyText, slot, receiptId = '', operationId = '', onOpenThread = null }) {
  const rows = Array.isArray(items) ? items : []
  const more = Math.max(0, (Number(total) || 0) - rows.length)
  if (unread) {
    return (
      <div className="rounded-[18px] bg-surface px-4 py-3 [border:1px_solid_var(--border)]">
        <p className="m-0 text-[12.5px] font-semibold text-warn">
          {typeof unread === 'string' && unread.trim()
            ? unread.trim().slice(0, 200)
            : 'The customer usage data could not be read — this lane is unknown, not empty.'}
        </p>
      </div>
    )
  }
  if (!rows.length) {
    return (
      <div className="rounded-[18px] bg-surface px-4 py-3 [border:1px_solid_var(--border)]">
        <p className="m-0 text-[12.5px] text-dim">{emptyText}</p>
      </div>
    )
  }
  return (
    <ApolloBatch noun="emails" className="mb-2">
      <div className="flex flex-col gap-2">
        {rows.map((item, i) =>
          slot === 'trialUsers' ? (
            <TrialCard
              key={`${item.key || item.email || item.name || 'customer'}-${i}`}
              item={item}
              index={i}
              receiptId={receiptId}
              operationId={operationId}
            />
          ) : (
            <CustomerCard
              key={`${item.key || item.email || item.name || 'customer'}-${i}`}
              item={item}
              slot={slot}
              index={i}
              receiptId={receiptId}
              operationId={operationId}
              onOpenThread={onOpenThread}
            />
          ),
        )}
      </div>
      {more > 0 && (
        <p className="m-0 mt-1.5 px-1 text-[11.5px] text-faint">
          …and {more} more — this receipt lists the first {rows.length}.
        </p>
      )}
    </ApolloBatch>
  )
}

/**
 * The leads inside Bo's 3-business-day wait window: nothing is due from them
 * yet, and each row says exactly when that changes. Collapsed by default — a
 * briefing leads with what is DUE — but the count in the header is always
 * visible, because "nothing due, 12 waiting until Thursday" is the honest
 * story of a quiet morning.
 */
/**
 * YOUR TASKS — the first thing Bo's daily pass is required to show, in his own
 * words. Open by default and never collapsed away: his skill file calls these
 * MANDATORY and puts them above the cadence, so hiding them behind a toggle
 * would be the app deciding his handwriting matters less than its arithmetic.
 *
 * Overdue is called overdue. A task with no subject shows "(no subject)" rather
 * than a description of it — the value here is that the sentence is HIS.
 */
function TasksSection({ rows, total, unread, forNames = [] }) {
  const count = Math.max(Number(total) || 0, rows.length)
  const names = Array.isArray(forNames) ? forNames.filter(Boolean) : []
  const whose = names.length ? `${names.join(' and ')}’s` : 'The rep’s'
  if (unread) {
    return (
      <div className="[border-top:1px_solid_var(--border)] px-4 py-2.5">
        <span className="text-[12.5px] font-semibold text-warn">
          {whose} HubSpot tasks could not be read — this pass cannot say what is overdue.
        </span>
      </div>
    )
  }
  if (!rows.length) return null
  return (
    <div className="[border-top:1px_solid_var(--border)]">
      <div className="px-4 pb-1 pt-2.5">
        <span className="text-[12.5px] font-semibold text-ink">
          {count} of {whose} HubSpot {count === 1 ? 'task' : 'tasks'} — overdue or due today
        </span>
      </div>
      <ul className="m-0 list-none px-4 pb-3 pt-0">
        {rows.map((t, i) => (
          <li
            key={t.id || `${t.subject}-${i}`}
            className="flex items-baseline gap-x-2 py-[3px] text-[12.5px] leading-[1.5]"
          >
            {/* Only claim what the row actually says. This pill used to render
                unconditionally, so anything not overdue was labelled "today" —
                including rows on RECEIPTS SAVED BEFORE the window narrowed,
                which were "due within two business days" and may be due next
                week. A saved receipt is a record of what was true when it ran;
                relabelling its rows to fit today's rule would falsify it. */}
            {(t.overdue || t.dueToday) && (
              <span
                className={`flex-none rounded-full px-[7px] py-px text-[11px] font-semibold ${
                  t.overdue
                    ? 'bg-[#fef2f2] text-[#b91c1c] dark:bg-[rgba(185,28,28,0.2)] dark:text-[#fca5a5]'
                    : 'bg-panel text-dim'
                }`}
              >
                {t.overdue ? 'overdue' : 'today'}
              </span>
            )}
            <span className="min-w-0 text-ink">{t.subject || '(no subject)'}</span>
            {t.company && <span className="flex-none text-faint">· {t.company}</span>}
            {shortDate(t.dueAt) && <span className="flex-none text-faint">· {shortDate(t.dueAt)}</span>}
          </li>
        ))}
        {count > rows.length && (
          <li className="pt-1 text-[11.5px] text-faint">
            …and {count - rows.length} more in HubSpot.
          </li>
        )}
      </ul>
    </div>
  )
}

function WaitingSection({ rows, total }) {
  const [open, setOpen] = useState(false)
  const count = Math.max(Number(total) || 0, rows.length)
  return (
    <div className="[border-top:1px_solid_var(--border)]">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex w-full cursor-pointer items-center justify-between gap-x-2 bg-transparent px-4 py-2.5 text-left transition-colors hover:bg-panel"
      >
        <span className="text-[12.5px] font-semibold text-dim">
          {count} waiting — inside Bo’s 3-business-day wait
        </span>
        <ChevronDownIcon
          className={`h-3.5 w-3.5 flex-none text-faint transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>
      {open && (
        <ul className="m-0 list-none px-4 pb-3 pt-0">
          {rows.map((w, i) => {
            // Only clauses backed by a readable value are written.
            const bits = [
              Number.isFinite(w.touch) && touchLabel(w.touch)
                ? `Touch ${w.touch} · ${touchLabel(w.touch)}`
                : '',
              shortDate(w.lastTouchAt) ? `last touched ${shortDate(w.lastTouchAt)}` : '',
              shortDate(w.dueAt) ? `due ${shortDate(w.dueAt)}` : '',
            ].filter(Boolean)
            return (
              <li
                key={`${w.key || w.name}-${i}`}
                className="flex flex-wrap items-baseline gap-x-2 py-1 text-[12.5px]"
              >
                <span className="font-semibold text-ink">{w.name}</span>
                {bits.length > 0 && <span className="text-faint">{bits.join(' · ')}</span>}
              </li>
            )
          })}
          {count > rows.length && (
            <li className="py-1 text-[12px] text-faint">
              {count - rows.length} more waiting — this receipt lists the first {rows.length}.
            </li>
          )}
        </ul>
      )}
    </div>
  )
}

/**
 * The leads who actually replied — pulled out of the cadence, because a human
 * answers a human. And the one honest twist: when the inbox could not be READ,
 * the header says so instead of implying zero. "Nobody replied" and "we could
 * not look" are different facts, and this section never confuses them.
 */
// THE TWO FACTS ARE INDEPENDENT, so the lane states both.
//
// This section used to gate its rows on `!inboxUnread`, which was right when
// Gmail was the ONLY reply source: an unread inbox meant no reply could be
// known. HubSpot email engagements are now the system of record for replies,
// so "the inbox could not be read" and "here are real replies we DID see"
// are true at the same time — and the old gate silently swallowed every
// HubSpot reply on a workspace with no Gmail connected, which is the common
// case. Rows now render whenever we have them; the inbox admission becomes a
// caveat UNDER them ("what we found, and what we still cannot see") instead of
// replacing them.
function NeedsReplySection({ rows, total, inboxUnread }) {
  const count = Math.max(Number(total) || 0, rows.length)
  const has = rows.length > 0
  return (
    <div className="px-4 py-3 [border-top:1px_solid_var(--border)]">
      <p className="m-0 text-[12.5px] font-semibold text-dim">
        {has
          ? `${count} replied — pulled out of the cadence for a human`
          : 'The inbox could not be read — replies are unknown, not zero. Prospects who wrote back may be waiting on you right now.'}
      </p>
      {has && (
        <ul className="m-0 mt-1 list-none p-0">
          {rows.map((r, i) => {
            const when = shortDate(r.replyAt || r.lastInboundAt)
            return (
              <li key={`${r.key || r.name}-${i}`} className="py-1 text-[12.5px]">
                <div className="flex flex-wrap items-baseline gap-x-2">
                  <span className="font-semibold text-ink">{r.name}</span>
                  {r.email && <span className="text-faint">{r.email}</span>}
                  {when && <span className="text-faint">replied {when}</span>}
                </div>
                {/* What they actually wrote, quoted from the source that saw
                    it. Bo is about to answer a person; the words are the
                    point. Rendered as text, never as HTML. */}
                {(r.replySubject || r.replySnippet) && (
                  <div className="mt-0.5 text-[12px] text-dim">
                    {r.replySubject && <span className="font-semibold">{r.replySubject}</span>}
                    {r.replySubject && r.replySnippet && <span className="text-faint"> — </span>}
                    {r.replySnippet && <span>“{r.replySnippet}”</span>}
                  </div>
                )}
                {r.via && <div className="mt-0.5 text-[11.5px] text-faint">Seen in {r.via}</div>}
              </li>
            )
          })}
          {count > rows.length && (
            <li className="py-1 text-[12px] text-faint">
              {count - rows.length} more replied — this receipt lists the first {rows.length}.
            </li>
          )}
        </ul>
      )}
      {/* The admission stands even when rows were found: HubSpot saw these,
          but anything that reached only Gmail is still unread. */}
      {inboxUnread && has && (
        <p className="m-0 mt-1.5 text-[11.5px] text-faint">
          The connected inbox still could not be read, so this lane is what HubSpot saw — not
          necessarily everyone who wrote back.
        </p>
      )}
      {/* Not a murmur, a to-do: the worklist's job includes catching who needs
          a response, and it cannot do that job without the inbox. */}
      {inboxUnread && (
        <Link
          to="/settings"
          className="mt-2 inline-flex items-center gap-x-1.5 rounded-[8px] bg-primary px-3 py-[6px] text-[12.5px] font-semibold text-white no-underline transition-colors hover:bg-primary-dark"
        >
          Connect Gmail →
        </Link>
      )}
    </div>
  )
}

// ── the receipt an operation posts into the thread ──────────────────────────
//
// This is the entry point to provenance. The header opens the flow for the run
// as a whole; each lead row opens it FOR THAT LEAD, so "why is this company on
// my list?" is one click from the answer. Between the header button, the lead
// rows and the footer button the whole card is clickable — as real buttons,
// rather than a click handler on the card that swallows keyboard users and
// steals the clicks meant for the rows inside it.
//
// It renders from the message alone. Nothing here reads live run state, which
// is why a receipt from last week opens exactly like the one posted a second
// ago, and why two receipts in one thread stay two receipts.
// A RECEIPT THAT LOST ITS CARDS TO THE BROWSER'S STORAGE gets them back from
// its server run (lib/receiptRefill.js) - in memory, once per run per page
// load, never written back where the store would only shed it again. While
// the read is in flight the card says so in one plain line and draws no
// section, so a demo never shows "No inactive users today" over twenty-seven.
const receiptRefills = new Map() // runId -> { promise, fields }

function useRefilledReceipt(msg) {
  const runId = String(msg?.serverRun?.runId || '')
  const shed = Boolean(runId) && receiptLooksShed(msg)
  const [fields, setFields] = useState(() => (shed ? receiptRefills.get(runId)?.fields || null : null))
  useEffect(() => {
    if (!shed) return undefined
    let alive = true
    let entry = receiptRefills.get(runId)
    if (!entry) {
      entry = { fields: null, promise: null }
      entry.promise = refillReceipt(msg)
        .then((got) => {
          entry.fields = got
          return got
        })
        .catch(() => null)
      receiptRefills.set(runId, entry)
    }
    entry.promise.then((got) => {
      if (alive && got) setFields(got)
    })
    return () => {
      alive = false
    }
    // The receipt's identity is its stamp and its run; a store merge hands this
    // component a fresh object per write, and re-running on each would be waste.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shed, runId, msg?.at])
  if (!shed) return msg
  if (!fields) return { ...msg, refilling: true }
  return { ...msg, ...fields, runShed: false }
}

function RunMessage({ msg: storedMsg, scope = null, convoId = '', onOpenDraft, onOpenThread }) {
  const msg = useRefilledReceipt(storedMsg)
  // A receipt still owed drafts re-arms the HOST's resolver on render — the
  // route an OLD receipt (reopened after a reload, or synced in from another
  // browser) takes back into the queue. ensureDraftsResolving is idempotent
  // per (receipt at, lead key), so this and the host's own call after posting
  // a fresh receipt cost nothing when the loop is already live; the resolved
  // drafts land store-level (patchReceiptLead) and reach this card through
  // the store broadcast the page subscribes to.
  // COUNTED, not a boolean: a sync merge can reopen a slot after the host's
  // loop drained and released this receipt, and the count changing is what
  // re-fires the effect so a fresh loop starts. A count that only shrinks
  // re-fires into the idempotent no-op, which is free.
  //
  // Two kinds of owed slot now. Lead stubs still need the receipt's research
  // category (no category, no cache, no lead resolver — unchanged); the
  // customer lanes resolve under the host's fixed 'customers' category, so
  // they count WITHOUT one — same conditions as the host's own owedSlot: a
  // pending mark, no email in the slot yet, and a key to claim it by.
  const owedLeadDrafts = !msg?.researchCategory
    ? 0
    : (msg.leads || []).filter(
        (l) => l && (l.researchPending || l.draftPending) && !l.draft?.bodyText,
      ).length
  const owedCustomerDrafts = ['inactiveUsers', 'expiredSubscriptions', 'tasks'].reduce(
    (n, slot) =>
      n +
      (Array.isArray(msg?.[slot])
        ? msg[slot].filter((c) => c && c.draftPending && !c.draft?.bodyText && c.key).length
        : 0),
    0,
  )
  const owedDrafts = owedLeadDrafts + owedCustomerDrafts
  useEffect(() => {
    if (owedDrafts && convoId) ensureDraftsResolving({ scope, convoId, msg })
    // `msg` deliberately not a dep by identity — the at stamp is the receipt's
    // identity, and a store merge hands this component a fresh object per
    // write; re-running on every one would only hammer a free dedupe.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [owedDrafts, scope, convoId, msg?.at])

  const built = msg.outcome === 'built'
  const stopped = msg.outcome === 'stopped'
  const leads = msg.leads || []
  // The follow-up lanes. Every read is guarded: a receipt from before these
  // fields existed simply has none of them and renders exactly as it used to.
  const calls = msg.calls || []
  const tasks = msg.tasks || []
  const waiting = msg.waiting || []
  const replies = msg.manualReply || []
  // ── the TITLED /daily-tasks shape (2026-08-28) ──
  // A worklist receipt PACKED with any of the new fields draws the numbered
  // sections; one from before the change carries none of them and takes the
  // old lanes path below, byte-for-byte as it always rendered. Both shapes
  // stay readable forever — the stored thread is the durable record.
  //
  // Section 4 changed hands on 2026-09-03: the calendar-and-needs-a-reply lane
  // was removed and Active Trials took its number. The DETECTION deliberately
  // still asks about the meetings fields: it is answering "was this receipt
  // written in the rebuilt shape", and a receipt stored between August 28 and
  // today carries them. Dropping the test would send those receipts down the
  // old-lanes path and silently change how an already-written record reads,
  // which is the one thing this branch exists to prevent.
  const inactiveUsers = Array.isArray(msg.inactiveUsers) ? msg.inactiveUsers : []
  const expiredSubs = Array.isArray(msg.expiredSubscriptions) ? msg.expiredSubscriptions : []
  const trialUsers = Array.isArray(msg.trialUsers) ? msg.trialUsers : []
  // ── the RECUT shapes (2026-09-23) ──
  // `briefing` is stamped by the packer off the run result: 'tasks' is Daily
  // Tasks (the rep's HubSpot tasks, one section, nothing else) and
  // 'customers' is Follow-Up (the four customer lanes, no task section). A
  // receipt without it was written before the recut and takes one of the two
  // older paths below, byte-for-byte as it always rendered.
  const briefing = msg.briefing === 'tasks' || msg.briefing === 'customers' ? msg.briefing : ''
  const paymentFailed = Array.isArray(msg.paymentFailed) ? msg.paymentFailed : []
  const titledSections =
    !briefing &&
    // The op id is the identity that NEVER changes (the contract's words);
    // the worklist flag rides along as the belt to that brace.
    (Boolean(msg.worklist) || msg.operationId === 'reply-open-threads') &&
    (Array.isArray(msg.inactiveUsers) ||
      Array.isArray(msg.expiredSubscriptions) ||
      Array.isArray(msg.trialUsers) ||
      Array.isArray(msg.meetings) ||
      Boolean(msg.meetingsUnread) ||
      Boolean(msg.meetingsNeedReconnect))
  // The pre-titled lanes path: only a receipt that is neither recut nor titled.
  const legacyLanes = !msg.refilling && !briefing && !titledSections
  const more = Math.max(0, (msg.leadCount || 0) - leads.length)
  const at = msg.at ? new Date(msg.at) : null
  const stamp =
    at && !Number.isNaN(at.getTime())
      ? at.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      : ''

  const Icon = built ? CheckIcon : stopped ? CloseIcon : SparklesIcon
  const iconTone = built
    ? 'bg-[rgba(22,163,74,0.12)] text-good'
    : stopped
      ? 'bg-[#fef2f2] text-danger dark:bg-[rgba(220,38,38,0.18)]'
      : 'bg-panel text-dim'

  // A FILTER OVER HISTORY, kept on purpose. Receipts stored before the
  // outreach surfaces were removed carry confirmation lines naming things this
  // app no longer has ("Audience list … saved" / "Campaign … staged as a
  // draft"). The stored thread is the durable record and is never rewritten,
  // so those two lines are dropped at render instead — an old receipt keeps
  // its title, its leads and its provenance, and simply stops pointing at a
  // page that is gone. Nothing written today produces them.
  const lines = (msg.lines || []).filter((l) => !/^Audience list “|^Campaign “/.test(l))

  return (
    <div className="flex justify-start">
      <div className="flex w-full min-w-0 max-w-[85%] flex-col gap-y-2">
        {/* The receipt bubble holds the RUN's story: title, admissions, the
            quieter lanes. The lead/call cards are deliberately OUTSIDE it —
            each card is its own container, not a row nested in a bubble. */}
        <div className="overflow-hidden rounded-[18px] bg-surface [border:1px_solid_var(--border)]">
          {/* Not interactive: provenance is answered per lead, on the lead's
              own card. There is deliberately no run-level flow. */}
          <div className="flex w-full items-start gap-x-3 px-4 py-3 text-left">
            <span
              className={`mt-px flex h-[28px] w-[28px] flex-none items-center justify-center rounded-[9px] ${iconTone}`}
            >
              <Icon className="h-[15px] w-[15px]" aria-hidden="true" />
            </span>
            <span className="min-w-0 flex-1">
              <span className="block text-[14px] font-semibold leading-[1.4] text-ink">
                {msg.title}
              </span>
              <span className="mt-0.5 block text-[11.5px] text-faint">
                {msg.operationLabel}
                {msg.stepCount ? ` · ${msg.stepCount} steps` : ''}
                {stamp ? ` · ${stamp}` : ''}
              </span>
              {lines.map((line, i) => (
                <span key={i} className="mt-1.5 block text-[12.5px] leading-[1.5] text-dim">
                  {line}
                </span>
              ))}
            </span>
          </div>

          {(msg.refilling || more > 0) && (
            <p className="m-0 px-4 py-[7px] text-[11.5px] text-faint [border-top:1px_solid_var(--border)]">
              {msg.refilling
                ? 'Reading the cards from the run…'
                : msg.worklist
                  ? `${more.toLocaleString()} more ${more === 1 ? 'lead is' : 'leads are'} due — this receipt shows the first ${leads.length}.`
                  : `${more.toLocaleString()} more ${more === 1 ? 'lead was' : 'leads were'} selected — this receipt shows the first ${leads.length}.`}
            </p>
          )}
          {(msg.callCount || 0) > calls.length && (
            <p className="m-0 px-4 py-[7px] text-[11.5px] text-faint [border-top:1px_solid_var(--border)]">
              {msg.callCount - calls.length} more call{' '}
              {msg.callCount - calls.length === 1 ? 'touch is' : 'touches are'} due — this receipt
              lists the first {calls.length}.
            </p>
          )}
          {/* The quieter lanes: who is inside the wait window, and who replied.
              They are the RUN's story, so they stay in the bubble — including
              under a "nothing was due" headline, where they ARE the story.
              OLD receipts only: a rebuilt receipt renders its own
              numbered sections below, and drawing these too would say the
              same facts twice. */}
          {/* A DAILY-TASKS RECEIPT FROM BEFORE THE TITLED-SECTION REBUILD. The
              stored thread is the durable record, so the old shape still
              renders — but without this line it renders as a silent answer to
              "where are the 4 sections?", and the user reads a format change
              as a bug. Say what it is and what to do; never rewrite history. */}
          {legacyLanes && (Boolean(msg.worklist) || msg.operationId === 'reply-open-threads') && (
            <div className="mb-3 rounded-[10px] border border-line bg-panel px-3 py-2 text-[12.5px] text-dim">
              This briefing was written in an older format. Run{' '}
              <span className="font-mono text-ink">/follow-up</span> again for the current one —
              HubSpot tasks, inactive users, expired subscriptions and active trials.
            </div>
          )}
          {/* Bo's own tasks lead the daily pass — his spec, his order. */}
          {legacyLanes && (tasks.length > 0 || msg.tasksUnread) && (
            <TasksSection
              rows={tasks}
              total={msg.taskCount}
              unread={Boolean(msg.tasksUnread)}
              forNames={msg.tasksFor}
            />
          )}
          {legacyLanes && waiting.length > 0 && (
            <WaitingSection rows={waiting} total={msg.waitingCount} />
          )}
          {legacyLanes && (replies.length > 0 || msg.inboxUnread) && (
            <NeedsReplySection
              rows={replies}
              total={msg.manualReplyCount}
              inboxUnread={Boolean(msg.inboxUnread)}
            />
          )}
        </div>

        {/* ── the titled sections, in the user's own order ───────────────────
            1 HubSpot tasks · 2 Inactive users · 3 Expired subscriptions ·
            4 Active trials. Every section renders even when empty, each with
            its honest state — "no tasks due", "nobody on trial today", not a
            blank — because the numbered structure IS the briefing: a section
            silently missing would read as the pass not having looked.

            SECTION 4 CHANGED HANDS on 2026-09-03, at the owner's request. It
            was "Miscellaneous": today's meetings off Google Calendar, then
            the needs-a-reply items off the Gmail inbox. Both are gone from
            the briefing and the calendar read is gone from the run with
            them. The INBOX read is not — it is what freezes a lead who has
            already written back out of the cadence lane (judgeReplies in
            lib/agentRun.js), so it still runs and still admits when it could
            not be read; it simply no longer draws a lane of its own here. A
            stored receipt from before that day keeps its own meetings and
            reply rows in the record; this page just stops drawing them.
            The number now belongs to ACTIVE TRIALS: everyone whose HubSpot
            "Trial Ends" is still ahead of now, off the same users read as
            sections 2 and 3, so Bo can see who to push to convert. */}
        {/* ── DAILY TASKS (recut 2026-09-23): the rep's HubSpot tasks, and
            nothing else. One section, always drawn — empty and unread each
            have their own honest state inside HubspotTasksSection. No section
            title: its "Today's emails — <rep> · one per task overdue or due
            today" line was taken off on request (2026-10-11); the receipt's
            own "Daily Tasks — <date>" heading sits directly above. */}
        {!msg.refilling && briefing === 'tasks' && (
          <div className="flex flex-col">
            <HubspotTasksSection
              rows={tasks}
              unread={Boolean(msg.tasksUnread)}
              forNames={msg.tasksFor}
              unreadNames={msg.tasksUnreadFor}
              excluded={msg.tasksExcluded}
              dismissed={msg.tasksDismissed || null}
              receiptId={msg.at ? String(msg.at) : ''}
              operationId={msg.operationId ? String(msg.operationId) : ''}
              onOpenThread={onOpenThread}
            />
          </div>
        )}

        {/* ── FOLLOW-UP (recut 2026-09-23): the four customer lanes, in the
            owner's order — 1 Inactive users · 2 Trials ending within 7 days ·
            3 Expired trials · 4 Payment failed. Every section renders even
            when empty, each with its honest state, because the numbered
            structure IS the briefing. Section 4 is UNREAD (not empty) until
            the portal writes a payment-failure timestamp to HubSpot — the
            lane's own reason string says so. */}
        {!msg.refilling && briefing === 'customers' && (
          <>
            <div className="flex flex-col">
              <WorklistSectionTitle>1 · Inactive users</WorklistSectionTitle>
              <CustomerLane
                items={inactiveUsers}
                total={msg.inactiveUserCount}
                unread={msg.usersUnread}
                emptyText="No inactive users today — this lane lists customers with no software access in over 7 days."
                slot="inactiveUsers"
                receiptId={msg.at ? String(msg.at) : ''}
                operationId={msg.operationId ? String(msg.operationId) : ''}
              />
            </div>
            <div className="flex flex-col">
              <WorklistSectionTitle>2 · Trials ending within 7 days</WorklistSectionTitle>
              <CustomerLane
                items={trialUsers}
                total={msg.trialUserCount}
                unread={msg.usersUnread}
                emptyText={`No trial ends within the next 7 days${
                  Number(msg.trialLater) > 0
                    ? ` — ${msg.trialLater} ${msg.trialLater === 1 ? 'trial ends' : 'trials end'} later than that and ${msg.trialLater === 1 ? 'is' : 'are'} not listed.`
                    : ' — this lane lists contacts whose HubSpot “Trial Ends” falls inside the week, soonest first.'
                }`}
                slot="trialUsers"
                receiptId={msg.at ? String(msg.at) : ''}
                operationId={msg.operationId ? String(msg.operationId) : ''}
              />
            </div>
            <div className="flex flex-col">
              <WorklistSectionTitle>3 · Expired trials</WorklistSectionTitle>
              <CustomerLane
                items={expiredSubs}
                total={msg.expiredSubscriptionCount}
                unread={msg.usersUnread}
                emptyText="No expired trials today — this lane lists customers whose trial or subscription has ended and who actually used the software."
                slot="expiredSubscriptions"
                receiptId={msg.at ? String(msg.at) : ''}
                operationId={msg.operationId ? String(msg.operationId) : ''}
                onOpenThread={onOpenThread}
              />
            </div>
            <div className="flex flex-col">
              <WorklistSectionTitle>4 · Payment failed</WorklistSectionTitle>
              <CustomerLane
                items={paymentFailed}
                total={msg.paymentFailedCount}
                unread={msg.usersUnread || msg.paymentFailedUnread}
                emptyText="No failed payment on record — this lane lists contacts whose portal record carries a payment-failure timestamp, newest first."
                slot="paymentFailed"
                receiptId={msg.at ? String(msg.at) : ''}
                operationId={msg.operationId ? String(msg.operationId) : ''}
                onOpenThread={onOpenThread}
              />
            </div>
            <WrittenOffCustomersNote dismissed={msg.customersDismissed || null} unread={msg.dismissalsUnread || ''} />
          </>
        )}

        {!msg.refilling && titledSections && (
          <>
            <div className="flex flex-col">
              <WorklistSectionTitle>
                1 · HubSpot tasks
                {Array.isArray(msg.tasksFor) && msg.tasksFor.length ? ` — ${msg.tasksFor.join(', ')}` : ''}
              </WorklistSectionTitle>
              <HubspotTasksSection
                rows={tasks}
                total={msg.taskCount}
                unread={Boolean(msg.tasksUnread)}
                forNames={msg.tasksFor}
                unreadNames={msg.tasksUnreadFor}
                excluded={msg.tasksExcluded}
              />
            </div>
            <div className="flex flex-col">
              <WorklistSectionTitle>2 · Inactive users</WorklistSectionTitle>
              <CustomerLane
                items={inactiveUsers}
                total={msg.inactiveUserCount}
                unread={msg.usersUnread}
                emptyText="No inactive users today — this lane lists customers with no software access in over 7 days."
                slot="inactiveUsers"
                receiptId={msg.at ? String(msg.at) : ''}
                operationId={msg.operationId ? String(msg.operationId) : ''}
              />
            </div>
            <div className="flex flex-col">
              <WorklistSectionTitle>3 · Expired subscriptions</WorklistSectionTitle>
              <CustomerLane
                items={expiredSubs}
                total={msg.expiredSubscriptionCount}
                unread={msg.usersUnread}
                emptyText="No expired subscriptions today — this lane lists customers whose subscription has ended and who actually used the software."
                slot="expiredSubscriptions"
                receiptId={msg.at ? String(msg.at) : ''}
                operationId={msg.operationId ? String(msg.operationId) : ''}
                // The card has no email to copy, so the thread IS the
                // preparation — handed only to this lane, where it replaces
                // what was taken away.
                onOpenThread={onOpenThread}
              />
            </div>
            <div className="flex flex-col">
              <WorklistSectionTitle>4 · Active trials</WorklistSectionTitle>
              <CustomerLane
                items={trialUsers}
                total={msg.trialUserCount}
                unread={msg.usersUnread}
                emptyText="Nobody is on trial today — this lane lists contacts whose HubSpot “Trial Ends” date is still ahead, soonest-ending first, for Bo to push to convert."
                slot="trialUsers"
                receiptId={msg.at ? String(msg.at) : ''}
                operationId={msg.operationId ? String(msg.operationId) : ''}
              />
            </div>
          </>
        )}

        {/* One card per lead, free-standing — each carrying its own draft,
            copies and the arrow into how that draft was written. */}
        {!msg.refilling && leads.length > 0 && (
          <LeadCards
            leads={leads}
            onOpenThread={onOpenThread}
            // The thread this receipt lives in, so a card can switch its
            // recipient store-level (the slot is reopened and rewritten by
            // the host, which needs to address the receipt).
            scope={scope}
            convoId={convoId}
            // The receipt's identity — its timestamp is stored with it and
            // never changes — so the Copied mark belongs to THIS receipt's
            // drafts and not to every draft the company ever had.
            receiptId={msg.at ? String(msg.at) : ''}
            // Which operation wrote this receipt, so a copy or a HubSpot click
            // can be traced back to the run it came from.
            operationId={msg.operationId ? String(msg.operationId) : ''}
            onOpenDraft={(lead, facts, draft, kind, meta) =>
              onOpenDraft(msg, lead, facts, draft, kind, meta)
            }
          />
        )}

        {/* Due CALL touches: prep for Bo, never a dialer. The provenance arrow
            routes through the same onOpenDraft with kind:'call'. */}
        {calls.length > 0 && (
          <CallCards
            calls={calls}
            onOpenCall={(call) =>
              onOpenDraft(
                msg,
                // THE WHOLE STUB, not a three-field copy of it. The call stub
                // already carries the subject and opening line of the email
                // this call follows, and how many earlier sends bounced
                // (chat.js#callStub) — passing {key,name,email} threw all of it
                // away, and the call story was the one story with no evidence
                // on it at all.
                call,
                null,
                null,
                'call',
                {
                  touch: call.touch,
                  daysSince: call.daysSince,
                  lastTouchAt: call.lastTouchAt || '',
                  phone: call.phone || '',
                  points: call.points || [],
                  ...(call.lastSubject ? { lastSubject: call.lastSubject } : {}),
                  ...(call.lastSnippet ? { lastSnippet: call.lastSnippet } : {}),
                  ...(Number.isFinite(call.bouncedCount) ? { bouncedCount: call.bouncedCount } : {}),
                  // The run's admissions ride on the call item itself.
                  ...(call.replyUnchecked ? { replyUnchecked: true } : {}),
                  ...(call.exclUnchecked ? { exclUnchecked: true } : {}),
                },
              )
            }
          />
        )}
      </div>
    </div>
  )
}

/** Three dots, shown only until the first token lands. */
function Thinking() {
  return (
    <span className="inline-flex items-center gap-x-1 py-1.5" aria-label="Thinking">
      {[0, 1, 2].map((i) => (
        <span
          key={i}
          className="h-[6px] w-[6px] animate-pulse rounded-full bg-faint motion-reduce:animate-none"
          style={{ animationDelay: `${i * 160}ms`, animationDuration: '1.1s' }}
        />
      ))}
    </span>
  )
}

export default function Chat() {
  const { user } = useAuth()
  const { manifest, mode } = useManifest()
  const navigate = useNavigate()
  const location = useLocation()
  // The conversation id in the URL (/chat/<uuid>). The URL is the source of
  // truth for WHICH thread is open: a specific chat is a shareable link, and
  // bare /chat is ALWAYS a new chat — never "whatever was open last".
  const { convoId: urlConvoId } = useParams()
  const scope = currentScope()
  const { can } = useRbac()
  // The one grant an operation actually needs — see NO_OPERATION_REASON above.
  const canRunOperation = can('leads.enrich')

  // The cache paints first — synchronously, so there is no spinner and no
  // flash — and the account is read a beat later (see the sync effect below).
  const [conversations, setConversations] = useState(() => loadConversations(scope))
  const [activeId, setActiveId] = useState(() => urlConvoId || null)
  // Whether the account read has finished, either way. It gates ONE decision:
  // bouncing a /chat/<id> the cache does not hold. Threads live in the account
  // now, so a link to one written on another browser is valid and must not be
  // thrown away in the moment before the read lands.
  const [historyRead, setHistoryRead] = useState(false)

  // URL → state. Back/forward and pasted links land here: a known id opens
  // that thread, bare /chat opens a new chat, and an id neither this browser
  // nor the account holds bounces to /chat (replace — a dead link should not
  // pollute history).
  // Deliberately keyed on the URL alone (plus the read gate): reacting to
  // `conversations` too would fire this on the same commit that creates a
  // thread and clobber the fresh activeId with the not-yet-updated URL's null.
  // The in-memory list is still CONSULTED (a thread created a beat ago is known
  // before it persists), it just doesn't trigger the effect.
  useEffect(() => {
    if (!urlConvoId) {
      setActiveId(null)
      return
    }
    const known =
      conversations.some((c) => c.id === urlConvoId) ||
      loadConversations(scope).some((c) => c.id === urlConvoId)
    if (known) setActiveId(urlConvoId)
    // Not known YET is not the same as not existing. Wait for the account to
    // answer before declaring the link dead; the id stays in the URL and in
    // activeId meanwhile, so the thread simply appears when it arrives.
    else if (historyRead) navigate('/chat', { replace: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlConvoId, historyRead])

  // State → URL. Every existing setActiveId call keeps working; this one
  // effect mirrors the active thread into the address bar, so /chat/<uuid> is
  // always copyable. The pathname guard is the loop-breaker: once the two
  // agree nothing navigates.
  useEffect(() => {
    const want = activeId ? `/chat/${activeId}` : '/chat'
    if (location.pathname !== want) navigate(want)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeId])

  const [input, setInput] = useState('')
  const [error, setError] = useState('')

  // ── the host's live work, mirrored into React state ───────────────────────
  // A run HAPPENS IN THE THREAD, and it happens IN THE HOST (lib/runHost.js):
  // this page draws whatever the host says is going on, and navigating away
  // changes nothing about the work. Mounting mid-run simply reads the current
  // state — which is the whole point: a run started before this mount, or one
  // that outlives it, has exactly the same UI as one started here.
  //
  // The busy guard lives in the host too, at MODULE scope — the remount that
  // used to reset the component `running` flag (and once let a second run
  // start: two runs over the same 50 leads, researching and drafting each lead
  // twice, posting two receipts that disagreed) cannot reset it.
  // Re-read INSIDE the subscribing effect, not just at first render: passive
  // effects run after paint, so a status change landing between render-commit
  // and the subscribe (a run's final announce firing while this page mounts)
  // would otherwise be missed — and a finished host emits no further
  // announces, so the spinner would sit there until an unrelated one. The
  // synchronous getRun()/getStream() pre-checks mean no wrong run could start
  // off the stale mirror; this closes the wedged-UI half.
  const [hostRun, setHostRun] = useState(() => getRun())
  useEffect(() => {
    setHostRun(getRun())
    return subscribeRun(() => setHostRun(getRun()))
  }, [])
  const [hostStream, setHostStream] = useState(() => getStream())
  useEffect(() => {
    setHostStream(getStream())
    return subscribeStream(() => setHostStream(getStream()))
  }, [])

  // The derived shape the rest of the page always read: `running`/`runPasses`
  // draw the progress line, `runConvoId` keeps it out of unrelated threads.
  // The run's OUTCOME still lives on the posted receipt, not here, so it
  // survives a reload and two receipts in one thread stay two receipts.
  //
  // `runIsOurs` splits "the host is busy" from "it is busy with OUR work":
  // after a SPA sign-out/sign-in mid-run the host may still be finishing the
  // PREVIOUS account's run, whose thread is not this user's to open — the
  // busy guard still holds (the engine allows one run app-wide), but nothing
  // here may name that operation or link to its thread.
  const running = hostRun?.status === 'running'
  const runIsOurs = running && (hostRun.scope || 'anon') === (scope || 'anon')
  const runPasses = runIsOurs ? hostRun.passes : []
  const runConvoId = runIsOurs ? hostRun.convoId : null

  // One answer at a time (today's rule, now the host's STREAM_BUSY guard); the
  // live text renders only in the thread the answer belongs to.
  const streaming = hostStream?.status === 'streaming'
  const liveText = streaming && hostStream.convoId === activeId ? hostStream.text : ''

  // ── provenance view ───────────────────────────────────────────────────────
  // Opened by clicking a receipt (or one lead inside it), never by starting a
  // run. It is fed entirely from the message that was clicked, which is why it
  // still opens after a reload. `lead` scopes it to one lead's story.
  const [runOpen, setRunOpen] = useState(false)
  const [flow, setFlow] = useState(null)
  // The draft-provenance overlay, a sibling of `flow`. Only one of the two is
  // ever set — opening one clears the other — so the run pane renders exactly
  // the story that was asked for.
  const [draftFlow, setDraftFlow] = useState(null)
  // The HubSpot thread for one lead — set by openLeadThread, cleared whenever
  // another pane takes over, so the slide-in never shows two stories at once.
  const [timeline, setTimeline] = useState(null)

  // ── daily state ───────────────────────────────────────────────────────────
  // Today's ledger for the three operations, read from dailyTasks.js. Held in
  // state rather than read during render so a write anywhere in the app (this
  // page, the dashboard) repaints the tiles through the store's own event.
  const [daily, setDaily] = useState(() => listDailyTasks())
  // Which already-done tile is asking "again?". A rep may legitimately run an
  // operation twice in a day — this warns, it never blocks.
  const [confirmRerun, setConfirmRerun] = useState(null)
  // WHICH STATE THE TILE RUNS OVER. The slash palette already speaks
  // "/initial-outreach illinois"; the tile is the mouse path to the same run,
  // so it gets the same choice as a dropdown instead of asking people to
  // learn the argument syntax. '' = every state (exactly the bare command).
  // Keyed per operation id so a second scoped command someday remembers its
  // own choice instead of sharing this one's.
  const [tileScopes, setTileScopes] = useState({})
  // Which operations can take a state at all — derived from the command
  // catalog, never hardcoded here: when a command gains a 'state' argument,
  // its tile grows the dropdown with no edit to this page.
  const scopedTileOps = useMemo(() => {
    const ids = new Set()
    for (const cmd of SLASH_COMMANDS) {
      if (commandArg(cmd.name)?.name === 'state') ids.add(cmd.operationId)
    }
    return ids
  }, [])

  // The conversation history is a collapsible floating panel now, not a fixed
  // rail — the chat gets the full width, and history is one click away.
  const [historyOpen, setHistoryOpen] = useState(false)
  // Inline rename: which thread is being retitled, and the working text. A
  // blank commit clears the custom title so the thread goes back to deriving
  // it from the first message rather than showing an empty row.
  const [renamingId, setRenamingId] = useState(null)
  const [renameDraft, setRenameDraft] = useState('')

  // ── slash commands ────────────────────────────────────────────────────────
  // The palette is DERIVED from the input (see slashQuery below), not toggled:
  // there is no state that can disagree with what is typed, so it cannot be
  // left open over a normal message. These three hold only what the input
  // cannot say by itself.
  //
  // Escape's memory, held as the QUERY that was dismissed rather than a boolean:
  // dismissing silences the palette for the text as it stands, and editing that
  // text brings it back. A boolean would have stayed true while the command was
  // rewritten, leaving someone who pressed Escape once with no palette for the
  // rest of the line.
  const [dismissedQuery, setDismissedQuery] = useState(null)
  const [paletteIndex, setPaletteIndex] = useState(0)
  // What the composer needs to say back: an unknown command, or the reason a
  // known one will not run right now. Deliberately not `error` — that renders
  // in the thread as a failed answer, and a mistyped command is neither.
  const [commandNotice, setCommandNotice] = useState('')

  // The controller refs that lived here (abortRef for the stream, runAbortRef
  // for the run, plus the in-flight/seq/passes bookkeeping) are gone: the HOST
  // owns both controllers now, along with the synchronous pass-merge rules and
  // the "superseded run's teardown must not repaint" guard, which moved to
  // runHost.js verbatim. Stop is hostStopStream(); the busy checks are
  // getRun()/getStream(), which are module-scope and remount-proof.
  //
  // The threads as they stand right now — read by the one-time account sync
  // below and by the signed-in-account-changed reset, which both need the list
  // outside a render.
  const convosRef = useRef(conversations)
  // send() awaits buildWorkspaceContext (retrieval — hundreds of ms) BEFORE
  // the host has a stream to refuse a second send with, so during that await
  // the getStream() guard is blind and a second Enter would append a second
  // question the host then refuses — leaving it in the thread unanswered.
  // This latch is the synchronous half of the guard: set before the await,
  // cleared when the host has answered (or the context build threw).
  const sendPendingRef = useRef(false)
  // Threads already given an auto-title, so a second turn never re-titles.
  const titledRef = useRef(new Set())
  const scrollRef = useRef(null)
  const textareaRef = useRef(null)
  const pinnedRef = useRef(true)
  const chatPaneRef = useRef(null)
  const runPaneRef = useRef(null)
  const wasRunOpen = useRef(runOpen)

  // ── the signed-in account changed under a mounted page ────────────────────
  // logout()/login() is pure SPA state (auth.jsx), so `scope` can change
  // WITHOUT a remount while `conversations` still holds the previous account's
  // threads — and every write below is keyed on the current `scope`. The very
  // next commit would then save one account's history under the other's cache
  // key and queue it for the other's rows: a private thread handed to whoever
  // signed in second. So the threads are re-read from the NEW account's own
  // cache before anything is persisted. (Adjusting state during render is the
  // supported pattern for this — React re-renders immediately, before any
  // effect runs, so nothing is ever persisted from the mismatched pair.)
  const [loadedScope, setLoadedScope] = useState(scope)
  if (loadedScope !== scope) {
    const fresh = loadConversations(scope)
    convosRef.current = fresh
    titledRef.current = new Set()
    setLoadedScope(scope)
    setConversations(fresh)
    // Whatever thread was open belonged to the other account.
    setActiveId(null)
    // The new account's history has not been read yet; the routing gate must
    // wait for it again rather than bouncing a link on the old read.
    setHistoryRead(false)
  }

  const active = useMemo(
    () => conversations.find((c) => c.id === activeId) || null,
    [conversations, activeId],
  )
  const messages = active?.messages || []

  // While an answer is streaming INTO THIS THREAD, the host has already
  // appended its partial to the store (one write per 250ms beat), and the
  // store broadcast merges it back into `messages`. Rendering that row AND
  // the live bubble would paint the same half-answer twice — so the persisted
  // partial (identified by the `at` stamp the host gave it) is held back and
  // the live bubble, fed per delta rather than per beat, stands in until the
  // stream settles and the stored message becomes the one true copy.
  const shownMessages = useMemo(() => {
    if (!streaming || hostStream.convoId !== activeId || !hostStream.msgAt) return messages
    return messages.filter(
      (m) => !(m.role === 'assistant' && Number(m.at) === Number(hostStream.msgAt)),
    )
  }, [messages, streaming, hostStream, activeId])

  // Lead counts, the same shape the drawer assistant grounds on.
  const counts = useMemo(() => {
    const cats = manifest?.categories ?? {}
    const sum = (arr) => (arr || []).reduce((s, r) => s + (r.count || 0), 0)
    const byCategory = CATEGORIES.map((c) => ({
      ...c,
      count: sum(cats[c.key]),
      regions: cats[c.key] || [],
    }))
    return { byCategory, total: byCategory.reduce((n, c) => n + c.count, 0) }
  }, [manifest])

  useEffect(() => {
    convosRef.current = conversations
    // Saved as the MERGE of this state with the CURRENT cache, never the raw
    // React copy. Passive effects run after paint, so the host (runHost.js)
    // can land a write — a stream's first beat, a receipt — between this
    // commit's paint and this save; saving the state copy raw would overwrite
    // that message, and the broadcast-scheduled merge re-reads the
    // just-clobbered cache with nothing left to restore. The merge is cheap
    // (one cache read per commit, same as every store broadcast) and the
    // primitives stamp `updatedAt`, so a fresher store write always survives
    // a stale state copy; this page's own edits win the same way, because
    // patchActive stamps too. Deletes survive as well — remove() writes the
    // tombstone synchronously, and mergeConversations drops tombstoned
    // threads from both sides.
    const merged = mergeConversations(conversations, loadConversations(scope), scope)
    saveConversations(scope, merged)
    // The cache write is synchronous and always happens; the cloud write is
    // queued off the same commit. queueChanged is a DIFF against what the
    // account is known to hold, so every path that touches a thread — a
    // message, a run receipt, an auto-title, a rename — syncs without each
    // callsite having to remember to.
    queueChanged(scope, merged)
  }, [scope, conversations])

  // ── read the account, once per mount ──────────────────────────────────────
  // First paint already happened off the cache above, so this never blocks the
  // UI. The merge is done through the functional setState so a message sent
  // while the read was in flight is not clobbered by the response.
  //
  // `convosRef.current` is the local set AS OF THE READ, which is exactly what
  // "present locally but not in the cloud" has to mean for the one-time
  // migration; anything created after this moment is a normal queued write.
  useEffect(() => {
    let alive = true
    syncConversations(scope, convosRef.current)
      .then((res) => {
        if (!alive) return
        // A failed read is NOT an empty account: the cache keeps painting and
        // nothing is merged, uploaded or deleted. The read is still "done" for
        // routing purposes — a link we cannot verify must not hang forever.
        if (res.ok) setConversations((cur) => mergeConversations(cur, res.conversations, scope))
        setHistoryRead(true)
      })
      .catch(() => {
        // syncConversations resolves honest shapes rather than throwing; this
        // is belt and braces, and must still release the routing gate.
        if (alive) setHistoryRead(true)
      })
    return () => {
      alive = false
    }
  }, [scope])

  // Whether history is reaching the account, and what to say when it is not.
  // Subscribed rather than polled, so a failing write surfaces the moment it
  // fails instead of on the next render.
  const [syncStatus, setSyncStatus] = useState(chatSyncStatus)
  useEffect(() => subscribeChatSync(setSyncStatus), [])

  // Drain the write queue when the browser can act on it again. A thread
  // written offline stays queued (and stays in localStorage) until one of
  // these fires — it is never dropped, and never silently reported as saved.
  useEffect(() => {
    const drain = () => {
      if (document.visibilityState === 'visible') flushChatSync(scope)
    }
    const online = () => flushChatSync(scope)
    window.addEventListener('online', online)
    document.addEventListener('visibilitychange', drain)
    return () => {
      window.removeEventListener('online', online)
      document.removeEventListener('visibilitychange', drain)
    }
  }, [scope])

  // Follow the stream only while the user is already at the bottom — scrolling
  // up to re-read something must not be yanked back by the next token.
  const onScroll = useCallback(() => {
    const el = scrollRef.current
    if (!el) return
    pinnedRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 120
  }, [])

  useLayoutEffect(() => {
    if (!pinnedRef.current) return
    const el = scrollRef.current
    if (el) el.scrollTop = el.scrollHeight
  }, [messages.length, liveText, streaming])

  // Grow the composer with its content, up to a ceiling.
  useLayoutEffect(() => {
    const el = textareaRef.current
    if (!el) return
    el.style.height = 'auto'
    el.style.height = `${Math.min(el.scrollHeight, 200)}px`
  }, [input])

  // NOTHING is cancelled on unmount any more — deliberately. The abort that
  // lived here fixed a confirmed bug (a run that outlived the page had no UI
  // anywhere, and the remount reset the `running` guard so a second run could
  // start: two runs over the same 50 leads, paying twice for the same research
  // and posting two receipts that disagreed), and the host solves
  // both halves of it by construction instead: its busy guard is module-scope
  // (a remount cannot forget it) and a background run always has UI (the
  // Topbar chip reads getRun()/getStream()). The unmount salvage's mechanism —
  // pack a cancelled receipt, write it straight through the store — moved to
  // the host's `pagehide` handler, the one exit that still genuinely kills the
  // work ("the tab was closed mid-run"). Navigation now leaves runs, streams
  // and draft resolution running; this page just stops watching.

  // The store moved underneath us — the host appended a receipt, patched a
  // streaming answer, or filled a lead's draft slot. Re-read and MERGE rather
  // than replace: the primitives stamp `updatedAt`, so their writes win per
  // conversation (mergeConversations), while an unsaved edit this page holds
  // on some OTHER thread survives untouched. Filtered to the signed-in scope —
  // a write to another account's cache is not ours to repaint.
  useEffect(
    () =>
      subscribeConversations(({ scope: atScope }) => {
        if ((atScope || 'anon') !== (scope || 'anon')) return
        setConversations((cur) => mergeConversations(cur, loadConversations(scope), scope))
      }),
    [scope],
  )

  // ── auto-title, kept page-side ────────────────────────────────────────────
  // The host deliberately does not name threads, so the page still fires
  // summarizeTitle when an answer finishes for a thread that has no title yet
  // — same moment it always did, just heard through the host instead of owned
  // here. An answer that finishes with NO page mounted gets titleFor's derived
  // title until the next one lands with a page watching — a fallback, never a
  // blank. A hand-typed name always wins (patchActive re-checks c.title).
  useEffect(() => {
    if (!hostStream || hostStream.status !== 'done' || !String(hostStream.text || '').trim()) return
    const id = hostStream.convoId
    if (!id || titledRef.current.has(id)) return
    const convo =
      conversations.find((c) => c.id === id) ||
      loadConversations(scope).find((c) => c && c.id === id)
    if (!convo || convo.title) return
    titledRef.current.add(id)
    const msgs = (convo.messages || []).filter(
      (m) => m && (m.role === 'user' || m.role === 'assistant'),
    )
    const turns = msgs.map((m) => ({ role: m.role, text: m.text }))
    // The host's final store write may already have landed the answer in the
    // thread; only append it when it has not, so the summary never reads the
    // same answer twice.
    const landed = hostStream.msgAt && msgs.some((m) => Number(m.at) === Number(hostStream.msgAt))
    summarizeTitle(landed ? turns : [...turns, { role: 'assistant', text: hostStream.text }])
      .then((title) => {
        // Re-check inside the callback: the user may have renamed it while
        // the summary was in flight.
        if (title) patchActive(id, (c) => (c.title ? c : { ...c, title }))
      })
      .catch(() => titledRef.current.delete(id))
  }, [hostStream, conversations, scope])

  // A stream that ERRORED. The host keeps whatever partial its beats already
  // persisted, marked `stopped` — once the page may be gone, the store can be
  // the only copy, and a partial that reads complete would be a lie — and the
  // page's job is just to say why the answer cut out. Keyed by the stream's
  // start time so the banner shows once, not again every time this thread is
  // reopened.
  const shownStreamErrorRef = useRef(0)
  useEffect(() => {
    if (
      hostStream?.status === 'error' &&
      hostStream.error &&
      hostStream.convoId === activeId &&
      shownStreamErrorRef.current !== hostStream.startedAt
    ) {
      shownStreamErrorRef.current = hostStream.startedAt
      setError(hostStream.error)
    }
  }, [hostStream, activeId])

  // Keep the tiles honest about "today". Two things can make this state stale:
  // a write from somewhere else (the dashboard marking the same task done),
  // which the store announces; and midnight passing under a tab nobody closed —
  // dailyTasks resolves the day at call time, so re-reading when the tab comes
  // back is enough to roll over correctly.
  useEffect(() => {
    const refresh = () => setDaily(listDailyTasks())
    const off = subscribeDailyTasks(refresh)
    window.addEventListener('focus', refresh)
    document.addEventListener('visibilitychange', refresh)
    return () => {
      off()
      window.removeEventListener('focus', refresh)
      document.removeEventListener('visibilitychange', refresh)
    }
  }, [])

  // One tile per operation, in the daily catalog's display order, each paired
  // with its state for today. The pairing is by id and nothing here hardcodes
  // one: an operation the catalog does not list still gets a runnable tile (it
  // simply has no daily state), and a catalog entry with no operation behind it
  // is skipped rather than rendered as a button that cannot run.
  const tiles = useMemo(() => {
    const out = []
    const placed = new Set()
    for (const task of daily) {
      const op = OPERATIONS.find((o) => o.id === (task.operationId || task.id))
      if (!op || placed.has(op.id)) continue
      placed.add(op.id)
      out.push({ op, task })
    }
    for (const op of OPERATIONS) {
      if (!placed.has(op.id)) out.push({ op, task: null })
    }
    return out
  }, [daily])

  const doneToday = tiles.filter(({ task }) => task?.done).length

  // The pane that slid off screen must leave the tab order, not just the
  // viewport. `inert` is the only attribute that does both jobs — it takes the
  // whole subtree out of tab order AND hides it from assistive tech (so no
  // separate aria-hidden, which would contradict it and trip Chrome's
  // "blocked aria-hidden on an element because its descendant retained focus"
  // warning the moment Tab landed in the off-screen composer).
  //
  // React 18 has no `inert` JSX prop (it would render the string "true"), so
  // set it on the DOM node directly. Order matters: un-inert the arriving pane
  // before inerting the leaving one, so focus has somewhere to go.
  useEffect(() => {
    const chatEl = chatPaneRef.current
    const runEl = runPaneRef.current
    if (!chatEl || !runEl) return
    const arriving = runOpen ? runEl : chatEl
    const leaving = runOpen ? chatEl : runEl
    // The arriving pane has to come alive first: nothing inside an inert
    // subtree can take focus.
    arriving.removeAttribute('inert')
    arriving.removeAttribute('aria-hidden')

    // Then focus, but only on an actual open/close — never steal it on the
    // first paint.
    const changed = wasRunOpen.current !== runOpen
    wasRunOpen.current = runOpen
    if (changed) {
      if (!runOpen && textareaRef.current) {
        // Back from a run: the composer is where the user was and where they
        // are most likely to go next.
        textareaRef.current.focus()
      } else {
        // Into a run: the pane container itself, so the next Tab starts at
        // "Back" and a screen reader announces the pipeline it just entered.
        arriving.focus?.()
      }
    }

    // Only now close the pane being left behind.
    if ('inert' in HTMLElement.prototype) {
      leaving.setAttribute('inert', '')
    } else {
      // Pre-inert browsers (Safari < 15.5) fall back to the weaker aria-hidden
      // — applied after focus has already moved out, so it never hides a
      // focused element.
      leaving.setAttribute('aria-hidden', 'true')
    }
  }, [runOpen])

  function patchActive(id, fn) {
    setConversations((list) =>
      list.map((c) => (c.id === id ? { ...fn(c), updatedAt: Date.now() } : c)),
    )
  }

  // ── filling in a draft whose research landed after the run ────────────────
  // Moved store-level: chat.js#patchReceiptLead carries the two rules that
  // made the component version (fillLeadDraft) safe — WRITE-ONCE on a slot
  // that already holds an email, and the receipt stops advertising a wait
  // once nobody is waiting — and the host's resolver writes through it. This
  // page hears the write through the store broadcast above.

  // ── the slash-command palette, derived from the input ─────────────────────
  // `slashQuery` is the typed name when the WHOLE input is a single slash token
  // and null otherwise, so a message that merely contains a slash — a URL, a
  // date, "and/or", "/Users/jix/notes.txt" — is never hijacked. See
  // parseSlashQuery(): the trigger lives in one place and both the palette and
  // the send path read it, so they can never disagree about what is a command.
  const slashQuery = parseSlashQuery(input)
  // ARGUMENT MODE. "/initial-outreach il" is the command plus a place; the
  // palette stops offering commands and starts offering states, ranked by how
  // the typed text matched (lib/leadScope.js). Only a real command that takes
  // an argument gets here, so ordinary prose is untouched.
  const slashInput = useMemo(() => parseSlashInput(input), [input])
  const argSpec = slashInput?.typingArg ? commandArg(slashInput.name) : null
  const scopeStates = useMemo(
    () => statesFromManifest(manifest, DEFAULT_CATEGORY),
    [manifest],
  )
  const argMatches = useMemo(() => {
    if (!argSpec) return []
    const found = matchStates(slashInput.arg, scopeStates).map((st) => ({
      kind: 'state',
      key: st.code,
      state: st,
    }))
    // The no-argument row, offered only while the argument is still empty: once
    // someone is typing a place, "every state" is not what they are reaching for.
    return argSpec.optional && !slashInput.arg.trim()
      ? [{ kind: 'all', key: '__all__' }, ...found]
      : found
  }, [argSpec, slashInput?.arg, scopeStates])
  const paletteMatches = useMemo(
    () => (slashQuery === null ? [] : matchSlashCommands(slashQuery)),
    [slashQuery],
  )
  // Open even with zero matches: "/xyz matches nothing, did you mean
  // /daily-tasks?" has to be visible BEFORE Enter, not only after it.
  // One palette, two modes: command names, or the argument for the command
  // already typed. `paletteRows` is whichever list is showing, so the keyboard,
  // the highlight and the aria wiring below never need to know which mode it is.
  const argMode = Boolean(argSpec)
  const paletteRows = argMode ? argMatches : paletteMatches
  const paletteOpen =
    (argMode || slashQuery !== null) && (argMode ? input : slashQuery) !== dismissedQuery
  const paletteActive = paletteRows.length ? Math.min(paletteIndex, paletteRows.length - 1) : -1
  const paletteChoice = paletteActive >= 0 ? paletteRows[paletteActive] : null
  const activeOptionId = paletteChoice
    ? `slash-option-${argMode ? paletteChoice.key : paletteChoice.name}`
    : undefined
  // The closest command to something that matched nothing — offered, never run.
  const paletteNear = useMemo(
    () => (slashQuery && !paletteMatches.length ? closestSlashCommand(slashQuery) : null),
    [slashQuery, paletteMatches],
  )

  // Keep the highlight on a row that exists as the filter narrows.
  useEffect(() => {
    setPaletteIndex(0)
  }, [slashQuery, slashInput?.arg, argMode])

  // Follow the highlight when the list is longer than its box. Looked up by the
  // id the textarea already points aria-activedescendant at, so the DOM and the
  // announcement can never drift apart.
  useEffect(() => {
    if (!paletteOpen || !activeOptionId) return
    document.getElementById(activeOptionId)?.scrollIntoView({ block: 'nearest' })
  }, [paletteOpen, activeOptionId])

  /**
   * Run a command — by Enter, by Tab-then-Enter, or by click.
   *
   * It ends at startOperation(), the SAME entry point the shortcut tiles use,
   * so a typed command gets the identical RBAC gate, the identical in-flight
   * lock, the identical COMMAND chip and the identical receipt. The checks
   * above it are not a second gate (startOperation re-checks both, and is the
   * real boundary) — they exist so a refusal SAYS something instead of being a
   * keystroke that appears to do nothing.
   *
   * The tiles' "already ran today, run it again?" confirmation is deliberately
   * NOT reproduced here: it renders inside the empty-state tile that typing has
   * already replaced, so routing through it would be exactly the silent no-op
   * this is meant to avoid. Typing the command is the explicit intent the tile
   * asks for.
   */
  /**
   * Run the command in the input with the place it names.
   *
   * `row` is the highlighted palette row when there is one. With no row (the
   * typed text matched nothing, or matched two places equally), NOTHING RUNS:
   * a scoped run that quietly fell back to every state would be the opposite of
   * what was asked for, and the notice says which of the two happened.
   */
  function runScopedCommand(row) {
    const cmd = findSlashCommand(slashInput?.name)
    if (!cmd) return
    const typed = String(slashInput?.arg || '').trim()
    let scope = null
    if (row?.kind === 'state') scope = row.state
    else if (row?.kind === 'all') scope = null
    else {
      const resolved = resolveState(typed, scopeStates)
      if (!typed) scope = null
      else if (resolved.state) scope = resolved.state
      else {
        setCommandNotice(
          resolved.reason ||
            `“${typed}” is not a state with leads. Try ${scopeStates.slice(0, 3).map((st) => st.name).join(', ')}.`,
        )
        return
      }
    }
    setCommandNotice('')
    setDismissedQuery(null)
    setInput('')
    const op = getOperation(cmd.operationId)
    if (op) startOperation(op, { scope })
  }

  function runSlashCommand(cmd) {
    const op = getOperation(cmd?.operationId)
    if (!op) {
      setCommandNotice(`${cmd?.command || 'That command'} is no longer available.`)
      return
    }
    if (!canRunOperation) {
      setCommandNotice(`${NO_OPERATION_REASON}, so ${cmd.command} can’t run from your account.`)
      return
    }
    // The host's module guard is the one that actually holds (startOperation
    // re-checks it, synchronously); this early read exists so the refusal SAYS
    // something instead of being a keystroke that appears to do nothing.
    if (getRun()?.status === 'running') {
      setCommandNotice(RUN_BUSY_NOTICE)
      return
    }
    setCommandNotice('')
    setDismissedQuery(null)
    setInput('')
    startOperation(op)
  }

  // Enter on a slash token that is not a command. It must never fall through to
  // the chat endpoint: "/relpy" is a typo, not a question, and sending it would
  // burn a turn and answer something nobody asked. The input is left alone so
  // the typo can be fixed in place.
  function reportUnknownCommand(name) {
    // Re-open the palette if Escape had closed it: the "did you mean" lives
    // there too, and a refusal must never be a keystroke that shows nothing.
    setDismissedQuery(null)
    if (!name) {
      setCommandNotice('Type a command name after the slash — ↑↓ to pick one, Enter to run it.')
      return
    }
    const near = closestSlashCommand(name)
    setCommandNotice(
      `/${name} isn’t a command.${near ? ` Did you mean ${near.command} — ${near.label}?` : ''}`,
    )
  }

  async function send(question) {
    const q = question.trim()
    if (!q) return
    // One ANSWER at a time — today's rule, kept. The host is the guard that
    // holds (startStream refuses a second stream); this early read keeps the
    // question out of the thread when the refusal is certain, so a blocked
    // send never leaves a question sitting there unanswered. A RUN being live
    // is deliberately NOT checked any more: a question beside an operation is
    // exactly the parallelism the host exists to allow.
    if (getStream()?.status === 'streaming') return
    // The synchronous latch (see sendPendingRef): a send already past this
    // point but still awaiting its context build has no host stream yet for
    // the check above to see, and letting a second Enter through would put a
    // second question in the thread for the host to refuse.
    if (sendPendingRef.current) return

    // A lone slash token is a command attempt, whatever route got here (Enter,
    // or the Send button). Resolve it as one — an unknown one is reported, and
    // in neither case does it reach the chat endpoint as prose.
    const typed = parseSlashQuery(q)
    if (typed !== null) {
      const cmd = findSlashCommand(typed)
      if (cmd) runSlashCommand(cmd)
      else reportUnknownCommand(typed)
      return
    }

    sendPendingRef.current = true
    try {
      // Start a thread on first send rather than up front, so an untouched
      // "New chat" never accumulates in the sidebar.
      let convo = active
      if (!convo) {
        convo = newConversation()
        setConversations((list) => [convo, ...list])
        setActiveId(convo.id)
      }
      const id = convo.id
      // The RAW thread, question included — receipts and all. The host owns the
      // flattening for the API now (runHost.js#apiTurns), so what travels is the
      // store shape, and the assistant message the host appends as chunks land
      // goes through the store primitives — which is what lets the answer keep
      // writing into this thread after this page is gone.
      const history = [...(convo.messages || []), { role: 'user', text: q }]

      setInput('')
      setError('')
      pinnedRef.current = true
      // APPEND, never replace: the host may have landed a receipt or a draft
      // patch on this thread since `convo` was snapshotted, and rewriting
      // `messages` wholesale from that snapshot would silently drop it.
      patchActive(id, (c) => ({ ...c, messages: [...(c.messages || []), { role: 'user', text: q }] }))
      // The question is a tracked unit of work (the Activity page counts them
      // per day). Logged here, AFTER the slash-command branch, so a typed
      // operation is never counted as a question. Only a short preview leaves
      // the browser — the conversation itself is the record of the full text.
      logEvent('question_asked', {
        actor: user?.email || '',
        actorName: user?.name || '',
        detail: 'Asked the assistant',
        data: {
          conversationId: id,
          chars: q.length,
          preview: q.slice(0, 120),
          ...impersonationData(user),
        },
      })

      try {
        const context = await buildWorkspaceContext({
          user,
          counts,
          pages: PAGES.map((p) => ({ label: p.label, to: p.to })),
          // THE RETRIEVAL STEP: the leads this question is about ride along as
          // quotable facts (workspaceContext.js#retrieveLeadContext), so "who's
          // X at Y" is answered from the directory, not deflected.
          question: q,
          manifest,
          mode,
        })
        // Fire and forget: the host streams, persists per beat, and lands the
        // final message (with its nav and debug grounding) through the store —
        // this page hears every delta through subscribeStream and the final
        // through the store broadcast. Failures surface the same way (the
        // stream-error watcher up with the host state), so nothing awaits here.
        const refused = hostStartStream({ scope, convoId: id, history, context })
        if (refused) setError(refused)
      } catch (err) {
        // buildWorkspaceContext failing is the one error still thrown at us
        // directly — the question is already in the thread, so say why no
        // answer is coming rather than leaving it hanging.
        setError(err.message || 'Chat failed')
      }
    } finally {
      // By here the host either has the stream (so getStream() guards the next
      // send) or refused/errored (so the next send may honestly try again).
      sendPendingRef.current = false
    }
  }

  function onSubmit(e) {
    e.preventDefault()
    send(input)
  }

  function onInputChange(e) {
    const value = e.target.value
    setInput(value)
    // A notice answers the text that was typed; the moment that text changes it
    // is stale, so it goes rather than lingering under a corrected command.
    if (commandNotice) setCommandNotice('')
  }

  // ONE definition of "complete": Tab and a CLICK on a palette row both land
  // here, and both only FILL the composer — running stays on Enter, always.
  // A click used to run the command outright, which meant the mouse had no way
  // to merely pick a suggestion; on a machine where Tab-complete is flaky
  // (reported on Windows), that left no way to complete at all. Now the mouse
  // is the backup for Tab, and nothing fires an operation but Enter or the
  // send button — a click can be taken back, a launched run cannot.
  function completePaletteChoice(choice) {
    if (!choice) return
    if (argMode) {
      setInput(choice.kind === 'all' ? `/${slashInput.name} ` : `/${slashInput.name} ${choice.state.name}`)
    } else if (choice.command) {
      setInput(choice.command)
    }
    // A click lands with onMouseDown prevented so the textarea should never
    // have blurred — this is belt and braces for the odd browser that blurs
    // anyway, so the follow-up Enter always has somewhere to go.
    textareaRef.current?.focus()
  }

  function onKeyDown(e) {
    // ── the palette owns the keyboard while it is open ────────────────────
    // Only while OPEN, and only for these five keys: every other keystroke,
    // and every keystroke at all when the input is ordinary text, reaches the
    // textarea untouched.
    if (paletteOpen) {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault()
        if (!paletteRows.length) return
        const step = e.key === 'ArrowDown' ? 1 : -1
        // Wraps, so ↑ from the first row lands on the last — a 3-row list is
        // faster to walk backwards than to walk around.
        setPaletteIndex((paletteActive + step + paletteRows.length) % paletteRows.length)
        return
      }
      if (e.key === 'Escape') {
        e.preventDefault()
        setDismissedQuery(argMode ? input : slashQuery)
        return
      }
      if (e.key === 'Tab' && !e.shiftKey) {
        // Complete, don't run — and NEVER let Tab leave the composer while the
        // palette is open. Without the unconditional preventDefault, a query
        // with zero matches (a typo) let the browser move focus on Tab, which
        // blurred the textarea and closed the palette out from under the user
        // — on Windows that read as "Tab is broken", because the focus ring
        // landing elsewhere is easy to miss. With a highlighted row, Tab fills
        // it in so it can be read before Enter commits; with none, Tab simply
        // holds still.
        e.preventDefault()
        completePaletteChoice(paletteChoice)
        return
      }
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault()
        if (argMode) runScopedCommand(paletteChoice)
        else if (paletteChoice) runSlashCommand(paletteChoice)
        else reportUnknownCommand(slashQuery)
        return
      }
    }
    // Enter sends, Shift+Enter breaks the line — the convention every chat UI
    // has trained people on.
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      send(input)
    }
  }

  function startNew() {
    // Deliberately does NOT stop a live answer any more: the stream belongs to
    // its own thread and keeps writing there through the host — a new chat is
    // a navigation, not a cancellation. The Stop button remains the one way to
    // cut an answer short.
    setActiveId(null)
    setInput('')
    setCommandNotice('')
    setDismissedQuery(null)
    setError('')
    textareaRef.current?.focus()
  }

  function remove(id) {
    if (id === activeId) startNew()
    setConversations((list) => list.filter((c) => c.id !== id))
    // Delete it in the account too, or the next sync on any device would hand
    // it straight back. The tombstone this writes is synchronous, so even a
    // failed request cannot resurrect the thread here.
    queueConversationDelete(scope, id)
  }

  // ── rename ────────────────────────────────────────────────────────────────
  // Seeded with the CURRENT displayed title (derived or custom) so renaming an
  // untitled thread starts from the text the user is already looking at rather
  // than an empty box.
  // Focus the rename input and select its whole text, so the title is
  // immediately replaceable. Deferred a frame: the dblclick that opened the
  // field places its own caret afterwards, which would undo an eager select().
  const selectOnMount = useCallback((el) => {
    if (!el) return
    el.focus()
    el.select()
    // Again on the next task: the dblclick that opened the field places its own
    // caret after mount, which would otherwise collapse the selection. (A
    // rAF is not enough — it is throttled when the tab isn't visible.)
    setTimeout(() => {
      if (document.activeElement === el) el.select()
    }, 0)
  }, [])

  function beginRename(convo) {
    setRenamingId(convo.id)
    setRenameDraft(titleFor(convo))
  }

  function cancelRename() {
    setRenamingId(null)
    setRenameDraft('')
  }

  function commitRename(id) {
    const next = normalizeTitle(renameDraft)
    setConversations((list) =>
      list.map((c) => {
        if (c.id !== id) return c
        // Blank clears the custom title: the thread falls back to deriving one
        // from its first message instead of showing an empty row.
        const { title, ...rest } = c
        return next ? { ...rest, title: next } : rest
      }),
    )
    cancelRename()
  }

  // Every region of the lead directory, via the exact path CompanyDirectory
  // uses — loadRegionRows memoises per fetch path, so arriving here from the
  // directory costs nothing and arriving cold costs one pass over the regions.
  // Resilient like useCategoryCompanies: one flaky region must not blank a run,
  // but zero rows is an error, never an empty-but-successful pipeline.
  const loadLeadRows = useCallback(async () => {
    const regions = manifest?.categories?.[DEFAULT_CATEGORY] ?? []
    if (!regions.length) {
      throw new Error('The lead directory has not finished loading — try again in a moment.')
    }
    const settled = await Promise.allSettled(
      regions.map((region) => loadRegionRows(mode, DEFAULT_CATEGORY, region)),
    )
    const rows = []
    for (const outcome of settled) {
      if (outcome.status === 'fulfilled') rows.push(...outcome.value)
    }
    if (!rows.length) {
      const first = settled.find((s) => s.status === 'rejected')
      throw new Error(first?.reason?.message || 'Could not load any leads from the directory.')
    }
    // THE CRM'S OWN COMPANIES, ADDED TO THE POOL. Bo's book is bigger than this
    // lead file: most of the companies he is actually working were never
    // imported. Annotating first tells us which mirrored companies the
    // directory already covers; everything else joins as a CRM lead, so a
    // follow-up can come due for a company that exists only in HubSpot and the
    // daily worklist can name it. Fails soft: no index, no CRM leads, and the
    // run reads exactly as it did before.
    try {
      const index = await hubspotIndex()
      if (index) {
        annotateRows(rows, index)
        rows.push(...crmLeadRows(index, claimedCompanyIds(rows)))
      }
    } catch {
      /* the mirror is unreachable — the directory alone still runs */
    }
    return rows
  }, [manifest, mode])

  // NEVER RUN AN OPERATION ON A STALE BUILD (2026-09-25). The rep's tab ran
  // the pre-recut Daily Tasks for a whole day after the recut shipped, because
  // an open tab keeps the code it loaded. So every start first asks the live
  // index.html which bundle is current; if this tab is behind, the click is
  // remembered in sessionStorage, the page reloads onto the live build, and
  // the SAME operation starts there (the resume effect below). An unknown —
  // offline, a dev build, a check that times out — never blocks a run.
  const startingRef = useRef(false)
  async function startOperation(op, opts = {}) {
    if (!canRunOperation || !op) return
    if (getRun()?.status === 'running' || startingRef.current) return
    startingRef.current = true
    let check = { ok: true }
    try {
      check = await checkBeforeRun()
    } catch {
      check = { ok: true }
    } finally {
      startingRef.current = false
    }
    if (!check.ok) {
      stashPendingOperation({ operationId: op.id, scopeCode: opts?.scope?.code || '' })
      setCommandNotice('Updating to the latest version of Revailo Sales — your run starts as soon as it loads.')
      reloadForUpdate(check.latest)
      return
    }
    startOperationNow(op, opts)
  }

  // The click that caused an update reload, carried out on the fresh build.
  // Waits until this page can actually run it — the role grant, the lead
  // directory (the host loads it for every operation, and a start before it
  // lands stops with "has not finished loading" — verified: it did, on the
  // first try of this path) and, for a run scoped to one state, the state
  // list — and gives up quietly after 20 s rather than starting something the
  // rep clicked a minute ago.
  const pendingRunRef = useRef(undefined)
  if (pendingRunRef.current === undefined) pendingRunRef.current = takePendingOperation()
  const directoryReady = Boolean(manifest?.categories?.[DEFAULT_CATEGORY]?.length)
  useEffect(() => {
    const pending = pendingRunRef.current
    if (!pending) return undefined
    const op = getOperation(pending.operationId)
    if (!op) {
      pendingRunRef.current = null
      return undefined
    }
    if (canRunOperation && directoryReady && (!pending.scopeCode || scopeStates.length)) {
      pendingRunRef.current = null
      const scopeObj = pending.scopeCode ? scopeStates.find((st) => st.code === pending.scopeCode) || null : null
      startOperationNow(op, { scope: scopeObj })
      return undefined
    }
    const giveUp = setTimeout(() => {
      pendingRunRef.current = null
      setCommandNotice(`Updated to the latest version — press ${op.label} again to run it.`)
    }, 20000)
    return () => clearTimeout(giveUp)
    // startOperationNow reads live state through closures refreshed every
    // render; the effect only needs to wake when the page becomes able to run.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canRunOperation, directoryReady, scopeStates])

  function startOperationNow(op, { scope: runScope = null } = {}) {
    // The button is disabled for a role without the grant, but the button is
    // not the security boundary — this is. Every caller (the shortcut, the flow
    // view's "Run again") lands here, so a role that can't spend enrichment
    // credits can't start a run that would spend them.
    if (!canRunOperation) return
    // One run at a time. Two concurrent runs would research and draft the same
    // leads twice, and the second would exclude nobody the first had just
    // picked. The guard that holds is the HOST's, module-scope
    // and checked synchronously inside hostStartOperation (with agentRun's own
    // inFlight lock as the hard floor under it) — so two clicks landing in the
    // same tick cannot both get through, and a remount cannot reset it. This
    // early read only keeps the click from posting an intent message the host
    // would immediately refuse.
    if (getRun()?.status === 'running') return
    setConfirmRerun(null)

    // The thread this run reports into. A missing one gets its id MINTED here
    // but is committed to state only after the host accepts the start: a
    // refused start (the engine lock still unwinding after an abort, a
    // malformed op) used to leave the just-created empty conversation behind
    // as an anonymous "New chat" row the commit effect then persisted. The
    // host's own appendMessage creates the store copy for a brand-new id, so
    // nothing is lost to the gap.
    const convo = active || newConversation()
    const startedNewThread = !active
    const convoId = convo.id
    // The scope rides in the intent text, so the receipt says which place this
    // run was about — a run scoped to Illinois and one over everything must
    // not read identically a week later. The HOST posts the intent message
    // (flagged as a COMMAND) and logs operation_started beside it; posting it
    // here too would put two command chips in the thread.
    const intent = runScope?.name ? `${op.label} — ${runScope.name}` : op.label
    const startedThisThread = !(convo.messages || []).length

    // Hand the run to the host and let go. Scope filtering stays COMPONENT-
    // side — the host takes rows as data — and travels as a function so the
    // directory loads INSIDE the host's try: the intent is posted first, and
    // a directory that will not load produces the same blamed error receipt
    // it always did (the throw lands on the root nodes it blocked). From here
    // the host owns every exit: the receipt on success, stop, throw or
    // RunInFlightError; recordRunResult (dailyTasks broadcasts its own write,
    // which is what repaints the tiles); the operation_run ledger row with
    // `user` captured NOW, not at completion; and the receipt's own draft
    // resolution, headless.
    const refused = hostStartOperation({
      op,
      convoId,
      scope,
      rows: async () => {
        let rows = await loadLeadRows()
        // SCOPED TO A PLACE, when the command named one. Filtering here —
        // before the engine sees the pool — means every downstream count,
        // gate and receipt line is about the scope the user asked for, with
        // no node needing to know a scope exists. A CRM-only company carries
        // no directory geography, so a scoped run cannot include it.
        if (runScope?.code) {
          const before = rows.length
          rows = rows.filter((r) => rowInState(r, runScope.code))
          if (!rows.length) {
            throw new Error(
              `No leads in ${runScope.name} — the directory holds ${before.toLocaleString()} leads, none of them there.`,
            )
          }
        }
        return rows
      },
      category: DEFAULT_CATEGORY,
      user,
      intentText: intent,
      scopeCode: runScope?.code || '',
    })
    // Lost the race to another entry point (the drawer assistant someday, or
    // a second click that beat the state read above): say so where command
    // refusals are said, rather than doing silently nothing. The thread was
    // not committed to state yet, so a refusal leaves no phantom row behind.
    if (refused) {
      setCommandNotice(refused)
      return
    }
    pinnedRef.current = true
    setHistoryOpen(false)
    if (startedNewThread) {
      // The host has already created (and broadcast) the store copy holding
      // the intent message, and the merge updater that broadcast queued may
      // have put it into state ahead of this one — React applies updaters in
      // order — so dedupe by id rather than blindly prepending an empty twin.
      setConversations((list) => (list.some((c) => c.id === convoId) ? list : [convo, ...list]))
      setActiveId(convoId)
    }
    // A COMMAND thread is named for the DAY it ran — "Initial outreach —
    // Sept 8th, 2026" — because five runs of the same operation otherwise
    // produce five identical rows in the history drawer and only opening them
    // tells Monday from Thursday. Set as an explicit title, only when this
    // command is what STARTED the thread: a command typed into an existing
    // conversation must not rename what the conversation was already about,
    // and a title the user renamed by hand is theirs (patchActive re-checks
    // c.title because `convo` here can be a stale snapshot of state).
    //
    // AFTER the host call on purpose. For a brand-new thread the host has just
    // CREATED the store copy (title '', intent message inside) and broadcast
    // it; the merge queued by that broadcast replaces this page's empty copy
    // wholesale (the moreComplete rule — the copy with the intent must win).
    // React applies these updaters in order, so titling after the start lands
    // the title ON the merged copy instead of under it, and neither the date
    // title nor the intent message is lost to the tie-break.
    if (startedThisThread && !convo.title) {
      const title = commandThreadTitle(op.label, runScope?.name)
      patchActive(convoId, (c) => (c.title ? c : { ...c, title }))
    }
  }

  // What a shortcut click means, in priority order:
  //   1. A run is in flight — do nothing. The engine allows one run at a time
  //      and startOperation enforces that too; the progress line already in the
  //      thread is the answer to "what is happening".
  //   2. The task already ran today — ask first. Running twice is allowed (a
  //      rep may have new leads by the afternoon), so this is a warning, not a
  //      block: the same click again goes through.
  //   3. Otherwise, run it.
  function onTileClick(op, task) {
    if (running) return
    if (!canRunOperation) return
    if (task?.done && confirmRerun !== op.id) {
      setConfirmRerun(op.id)
      return
    }
    startOperation(op, { scope: tileScopeFor(op) })
  }

  // The tile honours its dropdown: a chosen state makes the click exactly
  // "/initial-outreach illinois" — same scope object, same startOperation
  // path, same receipt wording and thread title as the typed command. A stale
  // code (a state that left the manifest since it was chosen) resolves to
  // null and the run honestly covers every state rather than erroring. Used
  // by the tile click AND the run-again confirm, so the two can never run
  // different scopes.
  function tileScopeFor(op) {
    const code = scopedTileOps.has(op.id) ? tileScopes[op.id] || '' : ''
    return code ? scopeStates.find((st) => st.code === code) || null : null
  }

  // ── opening the provenance view ───────────────────────────────────────────
  // Everything it needs comes off the clicked RECEIPT, never off live run
  // state: that is what makes a receipt from three days and one reload ago open
  // exactly as well as the one posted a second ago. `lead` scopes it to one
  // lead's story; the key is keyOf(), the same key the run's tracer wrote.
  function openFlow(msg, lead = null) {
    if (!msg?.passes?.length) return
    setDraftFlow(null)
    setTimeline(null)
    setFlow({
      // The live catalog entry when it still exists (so "Run again" can start
      // it), falling back to the label the receipt stored — a receipt must
      // still name itself if the catalog ever changes underneath it.
      operation: getOperation(msg.operationId) || {
        id: msg.operationId,
        label: msg.operationLabel || 'Agent run',
      },
      runnable: Boolean(getOperation(msg.operationId)),
      passes: msg.passes,
      result: msg.result || null,
      lead,
    })
    setRunOpen(true)
  }

  // The arrow on a lead card: open the story of how THAT lead's draft was
  // composed. It needs no stored run passes — the pipeline is rebuilt from the
  // draft itself (lib/draftProvenance.js) — so it works even on a receipt whose
  // run steps were trimmed away to save space.
  //
  // `kind` picks the story: 'followup' (an email touch with cadence context),
  // 'call' (a due phone touch — no draft at all, the passes explain why the
  // call is due and where the points came from), and undefined for the
  // original initial-outreach path, which is byte-for-byte what it always was.
  // THE RUN'S OWN RECORD FOR ONE LEAD, lifted off the receipt it is already
  // rendering. Every node of the run wrote one sentence per lead while it was
  // deciding — "3 open CRM tasks on HubSpot company 12345", "no inbound after
  // our last touch in the conversations read" — and packRunMessage stores them
  // per lead key. The draft story quotes them verbatim; this is the only place
  // they are read for it.
  //
  // Every field is allowed to be missing. A receipt whose trace was shed under
  // storage pressure, a call row (which never gets one), and every receipt
  // written before the trace existed all produce an empty Map, and the nodes
  // then show what the stub carries and say nothing about what the run read.
  function provenanceOf(msg, lead) {
    const raw = msg?.result?.trace && typeof msg.result.trace === 'object' ? msg.result.trace : null
    const steps = raw && lead?.key ? raw[lead.key] : null
    const trace = new Map()
    for (const step of Array.isArray(steps) ? steps : []) {
      const id = String(step?.nodeId || '')
      if (id && !trace.has(id)) {
        trace.set(id, { verdict: String(step?.verdict || ''), note: String(step?.note || '') })
      }
    }
    const passes = Array.isArray(msg?.passes) ? msg.passes : []
    return {
      trace,
      // The run's own node titles, so a record can name the step that wrote it
      // using the label the run itself used.
      nodeTitles: new Map(passes.map((p) => [p.id, p.title || p.id])),
      // WHERE EACH NODE'S CODE LIVES, as the receipt stored it when the run
      // executed (agentRun.js NODE_EXEC → chat.js#packPass). The Developer
      // view renders it per step as "the code that ran". Empty on every
      // receipt from before the descriptors existed.
      nodeExec: new Map(
        passes.filter((p) => p && p.exec && typeof p.exec === 'object').map((p) => [p.id, p.exec]),
      ),
    }
  }

  // THE THREAD PANE. Same slide-in the draft story uses, so provenance and
  // history are one gesture apart: the card's arrow explains the email, this
  // button shows everything that came before it. Fetching lives in the view —
  // it is one read of the mirror, scoped to the matched company.
  function openLeadThread(lead, companyId, hubspotUrl) {
    // A lead with no matched record but an address can still have a thread:
    // the timeline reads by the lead's own addresses as well as by record
    // (LeadTimelineView), so only a lead with neither has nothing to open.
    if (!lead || (!companyId && !String(lead.email || '').trim())) return
    setFlow(null)
    setDraftFlow(null)
    setTimeline({ lead, companyId, hubspotUrl: hubspotUrl || '' })
    setRunOpen(true)
  }

  function openDraftFlow(msg, lead, rawFacts, draft, kind, meta) {
    // The STORY reads what the DRAFT read: the very merge LeadCard builds its
    // draft-builder input from (builderFacts), so the two can only ever tell
    // one account of what was known. Both halves of it matter here.
    //
    // The SCRUB, because the story builders (draftProvenance, draftNarrative,
    // DraftFlowView) prefer the snapshot's research signal over the stub's, and
    // an intel snapshot that ever came to carry research_* keys (a lead someone
    // opened and enriched later) would otherwise rewrite every receipt
    // containing that lead — old, failed and timed-out ones included — as a
    // researched run. Research is the RUN's story alone: it may only enter here
    // through the receipt's own stub, which is where researchOf reads it.
    //
    // The STUB OVERLAY, because without it the story read a strictly THINNER
    // row than the draft was written from: the stub's own city_state, trade and
    // contact_name reached buildOutreachEmail (via LeadCard) but not
    // buildDraftPasses, so an email that opens "Hi Donnie" and names the city
    // sat under a story claiming only the company name was known. Nothing new
    // enters the story here — every value is one this receipt's stub carries.
    // A CALL card carries no snapshot (only LeadCards builds that map), so the
    // one this account holds is read here instead — otherwise the call story's
    // row node has nothing but a company name to show, which is a statement
    // about the plumbing rather than about the lead.
    const snapshot =
      rawFacts || (kind === 'call' && lead?.key ? getIntel(lead.key)?.company || null : null)
    const facts = builderFacts(lead || {}, snapshot, STORY_STUB_FACT_FIELDS)
    const prov = provenanceOf(msg, lead)
    if (kind === 'call') {
      if (!lead) return
      const { passes } = buildCallPasses(lead, facts, meta || {}, prov)
      setFlow(null)
      setTimeline(null)
      setDraftFlow({
        lead,
        facts,
        draft: null,
        passes,
        kind,
        meta: meta || null,
        operationLabel: msg?.operationLabel || '',
      })
      setRunOpen(true)
      return
    }
    if (!lead || !draft) return
    const { passes } =
      kind === 'followup'
        ? buildFollowupDraftPasses(lead, facts, draft, meta || {}, prov)
        : buildDraftPasses(lead, facts, draft, prov)
    setFlow(null)
    setTimeline(null)
    setDraftFlow({
      lead,
      facts,
      draft,
      passes,
      kind,
      meta: meta || null,
      operationLabel: msg?.operationLabel || '',
    })
    setRunOpen(true)
  }

  // Back is a navigation, not an undo: a run in flight keeps running, and the
  // receipts already in the thread stay exactly where they are.
  function backToChat() {
    setRunOpen(false)
  }

  // The compact progress line in the thread. Several nodes can be in flight at
  // once — name the first and count the rest rather than picking one and
  // calling it "the" step, which on a graph silently hides half of what is
  // happening.
  const progressLine = useMemo(() => {
    if (!running) return ''
    const total = runPasses.length
    if (!total) return 'Starting'
    const live = runPasses.filter((p) => p.status === 'running')
    if (live.length > 1) {
      return `Step ${runPasses.indexOf(live[0]) + 1} of ${total} · ${live.length} steps running together`
    }
    if (live.length === 1) {
      return `Step ${runPasses.indexOf(live[0]) + 1} of ${total} · ${live[0].title}`
    }
    // Nothing running yet: the run has started but no node has. Before the
    // first node that is the directory fetch, and claiming "step 1" for it
    // would attribute a shared prerequisite to whichever node the graph happens
    // to list first.
    const settled = runPasses.filter((p) => p.status !== 'pending').length
    return settled ? `${settled} of ${total} steps done` : 'Loading the lead directory'
  }, [runPasses, running])

  const empty = !active || messages.length === 0

  // A thread the account could not hand over IN FULL. It arrives with no
  // messages and a `damaged`/`omitted` flag, and the one thing we must not do
  // is render it as the new-chat starter screen: that reads as "this
  // conversation is empty", which is the opposite of true and invites the user
  // to type into it — which is exactly the write that would overwrite the
  // stored rows. chat.js already refuses to sync it; this says so on screen.
  const unreadable = Boolean(active?.incomplete) && messages.length === 0

  // ── what the history panel is allowed to claim ────────────────────────────
  // "Saved" is only ever said after the server actually answered (status.cloud
  // is set by a successful read, and cleared by nothing else). Every other
  // state says what is true instead: still checking, working from this browser,
  // or a write that failed and is being retried — with the server's own reason.
  // There is deliberately no reassuring default: a fake "saved" is exactly the
  // lie that lost the user's threads in the first place.
  const syncNote = (() => {
    const { state, cloud, reason, pending } = syncStatus
    if (state === 'error') {
      return {
        tone: 'bad',
        text: `Not saved to your account yet${pending ? ` (${pending} pending)` : ''} — ${
          reason || 'the request failed'
        }. Retrying.`,
      }
    }
    if (state === 'signedOut') {
      return { tone: 'warn', text: 'Signed out — history stays in this browser only.' }
    }
    if (state === 'offline') {
      return {
        tone: 'warn',
        text: 'Your account could not be reached — working from this browser.',
      }
    }
    if (!cloud) return { tone: 'quiet', text: 'Checking your account…' }
    if (state === 'syncing' || pending) return { tone: 'quiet', text: 'Saving to your account…' }
    return { tone: 'quiet', text: 'History syncs to your account.' }
  })()

  // Both panes stay mounted and slide past each other inside a clipped track,
  // so a run keeps its state while the user reads the chat behind it. The
  // classes below only move the hidden pane and stop pointers; what actually
  // takes it out of the tab order and out of the accessibility tree is the
  // `inert` attribute the effect above puts on it (translate + opacity +
  // aria-hidden leave every button and the composer reachable by Tab).
  const SLIDE =
    'transition-[transform,opacity] duration-300 ease-[cubic-bezier(0.32,0.72,0.24,1)] motion-reduce:transition-none'

  return (
    <div className="relative h-full min-h-0 overflow-hidden">
      <div
        ref={chatPaneRef}
        tabIndex={-1}
        className={`relative flex h-full min-h-0 flex-col outline-none ${SLIDE} ${
          runOpen ? 'pointer-events-none -translate-x-full opacity-0' : 'translate-x-0 opacity-100'
        }`}
      >
      {/* ── top controls: History (left) + New chat (right) ── */}
      <div className="relative z-30 mb-1 flex flex-none items-center justify-between gap-x-2">
        {/* The panel anchors to THIS box, so "12px under the button" holds no
            matter how tall the button renders. */}
        <div className="relative">
        <button
          type="button"
          onClick={() => setHistoryOpen((v) => !v)}
          aria-expanded={historyOpen}
          className="inline-flex cursor-pointer items-center gap-x-1.5 rounded-[10px] bg-surface px-3 py-2 text-[13px] font-semibold text-dim transition-colors [border:1px_solid_var(--border)] hover:bg-panel hover:text-ink"
        >
          <ClockIcon className="h-[15px] w-[15px]" />
          History
          {conversations.length > 0 && (
            <span className="rounded-full bg-panel px-1.5 text-[11px] font-bold text-faint">
              {conversations.length}
            </span>
          )}
          <ChevronDownIcon
            className={`h-3.5 w-3.5 transition-transform ${historyOpen ? 'rotate-180' : ''}`}
          />
        </button>
      <aside
        aria-hidden={!historyOpen}
        className={`absolute left-0 top-[calc(100%+12px)] z-40 h-[420px] max-h-[70vh] overflow-hidden rounded-[14px] bg-surface transition-[width,opacity] duration-200 ease-[cubic-bezier(0.32,0.72,0.24,1)] ${
          historyOpen
            ? 'w-[360px] max-w-[calc(100vw-32px)] opacity-100 shadow-[24px_0_60px_-24px_rgba(2,6,23,0.3)] [border:1px_solid_var(--border)]'
            : 'pointer-events-none w-0 opacity-0 [border:0_solid_transparent]'
        }`}
      >
        {/* Fixed width so the list doesn't reflow while the clip animates. */}
        {/* The content fades in just behind the widening clip (delay-100), so
            the list arrives rather than being dragged into view. */}
        <div
          className={`flex h-full w-[360px] max-w-[calc(100vw-32px)] flex-col transition-opacity duration-200 ${
            historyOpen ? 'opacity-100 delay-100' : 'opacity-0'
          }`}
        >
        <div className="flex flex-none items-center justify-between px-3 py-2 [border-bottom:1px_solid_var(--border)]">
          <span className="text-[12.5px] font-semibold tracking-[0.01em] text-faint">
            Conversations
          </span>
          <button
            type="button"
            aria-label="Close history"
            onClick={() => setHistoryOpen(false)}
            className="cursor-pointer rounded-[6px] bg-transparent p-1 text-faint transition-colors hover:text-ink"
          >
            <CloseIcon className="h-3.5 w-3.5" />
          </button>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto p-1.5">
          {conversations.length === 0 ? (
            <p className="m-0 px-2 py-3 text-[12.5px] leading-[1.5] text-faint">
              Your conversations show up here.
            </p>
          ) : (
            <ul className="m-0 flex list-none flex-col gap-y-0.5 p-0">
              {conversations.map((c) => {
                const on = c.id === activeId
                return (
                  <li key={c.id} className="group relative">
                    {renamingId === c.id ? (
                      <input
                        ref={selectOnMount}
                        value={renameDraft}
                        maxLength={TITLE_MAX}
                        aria-label="Rename conversation"
                        onChange={(e) => setRenameDraft(e.target.value)}
                        onBlur={() => commitRename(c.id)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault()
                            commitRename(c.id)
                          }
                          if (e.key === 'Escape') {
                            e.preventDefault()
                            cancelRename()
                          }
                        }}
                        className="block w-full rounded-[8px] bg-panel py-2 pl-2.5 pr-2 text-[13px] text-ink outline-none [border:1px_solid_var(--primary)]"
                      />
                    ) : (
                      <>
                        <button
                          type="button"
                          onClick={() => {
                            // Switching threads no longer stops a live answer
                            // — it keeps writing into its own thread via the
                            // host, and coming back shows it landed.
                            setActiveId(c.id)
                            setError('')
                            setHistoryOpen(false)
                          }}
                          // Double-click to rename, the convention every file
                          // manager and chat app already taught people.
                          onDoubleClick={() => beginRename(c)}
                          className={`block w-full cursor-pointer rounded-[8px] bg-transparent py-2 pl-2.5 pr-[52px] text-left text-[13px] transition-colors ${
                            on ? 'bg-panel font-semibold text-ink' : 'text-dim hover:bg-panel hover:text-ink'
                          }`}
                        >
                          <MarqueeTitle text={titleFor(c)} />
                        </button>
                        {/* Always present, not hover-only: a control nobody can see is a
                            control nobody uses. Dimmed at rest, full strength on
                            hover or keyboard focus. */}
                        <div className="absolute right-1.5 top-1/2 flex -translate-y-1/2 items-center gap-x-0.5 opacity-60 transition-opacity group-hover:opacity-100 focus-within:opacity-100">
                          <button
                            type="button"
                            aria-label={`Rename ${titleFor(c)}`}
                            onClick={() => beginRename(c)}
                            className="cursor-pointer rounded-[6px] bg-transparent p-1 text-faint transition-colors hover:text-ink"
                          >
                            <PencilIcon className="h-[13px] w-[13px]" />
                          </button>
                          <button
                            type="button"
                            aria-label={`Delete ${titleFor(c)}`}
                            onClick={() => remove(c.id)}
                            className="cursor-pointer rounded-[6px] bg-transparent p-1 text-faint transition-colors hover:text-danger"
                          >
                            <TrashIcon className="h-[13px] w-[13px]" />
                          </button>
                        </div>
                      </>
                    )}
                  </li>
                )
              })}
            </ul>
          )}
        </div>
        {/* The one place the panel says where these threads actually live.
            Quiet by default, explicit only when a write is failing — never a
            reassurance the server has not earned (see syncNote). */}
        <div
          role={syncNote.tone === 'bad' ? 'status' : undefined}
          className={`flex-none px-3 py-2 text-[11.5px] leading-[1.45] [border-top:1px_solid_var(--border)] ${
            syncNote.tone === 'bad'
              ? 'text-danger'
              : syncNote.tone === 'warn'
                ? 'text-dim'
                : 'text-faint'
          }`}
        >
          {syncNote.text}
        </div>
        </div>
      </aside>
        </div>
        <div className="flex flex-none items-center gap-x-2">
        {/* REPORT ISSUE — the complaint channel, in the spot the Markdown
            export used to sit. Screenshots the page, takes a sentence, and puts
            both in S3 and in Jackson's inbox. Never disabled: something being
            wrong with this screen is exactly when there is nothing to export,
            and that is the moment the button has to work. */}
        <ReportIssueButton onBeforeCapture={() => setHistoryOpen(false)} />
        <button
          type="button"
          onClick={() => {
            setHistoryOpen(false)
            startNew()
          }}
          className="inline-flex cursor-pointer items-center gap-x-2 rounded-[10px] bg-primary px-3.5 py-2 text-[13px] font-semibold text-white transition-colors hover:bg-primary-dark"
        >
          <PlusIcon className="h-4 w-4" />
          New chat
        </button>
        </div>
      </div>

      {/* History opens by GROWING IN WIDTH, left→right, rather than sliding in
          from off-screen. The outer element animates `width` from 0 with
          overflow-hidden, so the panel is revealed left edge first; the inner
          content is pinned to a fixed 300px so the text never squashes as the
          clip widens. It sits below the controls row (top-[46px]) so History /
          New chat stay reachable, and is scoped to the chat pane. */}
      {historyOpen && (
        <div
          // z-20, BELOW the controls row (z-30) that now contains the panel.
          // At z-30 this sat later in the DOM at the same level, so it painted
          // over the panel and swallowed every hover and click inside it.
          className="absolute inset-x-0 bottom-0 top-[46px] z-20"
          onClick={() => setHistoryOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* ── conversation ──────────────────────────────────────────────── */}
      <section className="flex min-h-0 min-w-0 flex-1 flex-col">
        {/* The thread's title, and the place to rename it. Only once a thread
            exists — an empty page has nothing to name yet. */}
        {!empty && active && (
          <div className="mx-auto mb-1 flex w-full max-w-[760px] flex-none items-center gap-x-2 px-1">
            {renamingId === active.id ? (
              <input
                ref={selectOnMount}
                value={renameDraft}
                maxLength={TITLE_MAX}
                aria-label="Rename conversation"
                onChange={(e) => setRenameDraft(e.target.value)}
                onBlur={() => commitRename(active.id)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    commitRename(active.id)
                  }
                  if (e.key === 'Escape') {
                    e.preventDefault()
                    cancelRename()
                  }
                }}
                className="min-w-0 flex-1 rounded-[8px] bg-panel px-2 py-1 text-[19px] font-bold tracking-[-0.015em] text-ink outline-none [border:1px_solid_var(--primary)]"
              />
            ) : (
              <button
                type="button"
                onDoubleClick={() => beginRename(active)}
                title="Double-click to rename this chat"
                className="group/title inline-flex min-w-0 cursor-pointer items-center gap-x-2 rounded-[8px] bg-transparent px-2 py-1 text-left transition-colors hover:bg-panel"
              >
                <span className="min-w-0 truncate text-[19px] font-bold tracking-[-0.015em] text-ink">
                  {titleFor(active)}
                </span>
              </button>
            )}
          </div>
        )}
        <div
          ref={scrollRef}
          onScroll={onScroll}
          className="min-h-0 flex-1 overflow-y-auto"
          aria-live="polite"
          aria-busy={streaming || running}
        >
          <div className="mx-auto flex w-full max-w-[760px] flex-col gap-y-6 px-1 pb-6 pt-2">
            {unreadable ? (
              <div className="flex flex-col items-center pt-[6vh] text-center">
                <h2 className="m-0 text-[19px] font-bold tracking-[-0.02em] text-ink">
                  This conversation could not be loaded
                </h2>
                <p className="m-0 mt-2 max-w-[460px] text-[14px] leading-[1.55] text-dim">
                  {active?.incomplete === 'omitted'
                    ? 'It is stored in your account but was too large to load with the rest of your history. Reload to try again.'
                    : 'Part of it is missing from your account, so it cannot be shown in full. It has not been changed or deleted.'}
                </p>
                <p className="m-0 mt-2 max-w-[460px] text-[13px] leading-[1.5] text-dim">
                  Start a new chat rather than typing here — a message sent to this
                  thread would replace what is still stored.
                </p>
              </div>
            ) : empty ? (
              /* Two extra tiles is roughly 130px of new content, so the block
                 above them gives some of its breathing room back rather than
                 pushing the starters below the fold. */
              <div className="flex flex-col items-center pt-[6vh] text-center">
                <div className="flex h-[46px] w-[46px] items-center justify-center rounded-[14px] bg-primary-tint">
                  <ChatIcon className="h-[22px] w-[22px] text-primary-dark" />
                </div>
                <h2 className="m-0 mt-4 text-[21px] font-bold tracking-[-0.02em] text-ink">
                  What can I help with?
                </h2>
                <p className="m-0 mt-2 max-w-[440px] text-[14px] leading-[1.55] text-dim">
                  Grounded in your live workspace — leads, outreach, pipeline and activity. Ask
                  anything else too.
                </p>
                {/* THE SHORTCUTS ARE THE OPERATIONS. There are no suggested
                    questions any more: a question is one keystroke away in the
                    composer below, while these three are the only things this
                    page can DO, and burying them among example prompts made
                    them look like more examples. Each is one line of title and
                    one line of state. */}
                <div className="mt-6 w-full max-w-[780px] text-left">
                  <div className="mb-2 flex items-baseline justify-between gap-x-3">
                    <p className="m-0 text-[11px] font-bold uppercase tracking-[0.06em] text-faint">
                      Today’s operations
                    </p>
                    {tiles.length > 0 && (
                      <p className="m-0 text-[11.5px] text-faint">
                        {doneToday === tiles.length
                          ? `All ${tiles.length} done today`
                          : `${doneToday} of ${tiles.length} done today`}
                      </p>
                    )}
                  </div>
                  {/* One row, three columns — each tile a compact card (icon
                      top, title, two-line blurb) instead of a full-width row.
                      Collapses back to a stack on a narrow pane, where three
                      columns would crush the titles. */}
                  <div className="grid grid-cols-3 gap-2 max-[760px]:grid-cols-1">
                    {tiles.map(({ op, task }) => {
                      const busy = running && hostRun?.operationId === op.id
                      const done = Boolean(task?.done)
                      const asking = confirmRerun === op.id
                      const receipt = receiptLine(task)
                      return (
                        <div key={op.id} className="flex min-w-0 flex-col">
                          <button
                            type="button"
                            onClick={() => onTileClick(op, task)}
                            // One run at a time is enforced in startOperation
                            // and again in the engine; saying so on the button
                            // beats a click that silently does nothing.
                            disabled={!canRunOperation || running}
                            title={
                              !canRunOperation
                                ? NO_OPERATION_REASON
                                : running
                                  ? 'An operation is already running — one at a time'
                                  : (done && receipt) || op.label
                            }
                            aria-describedby={asking ? `rerun-${op.id}` : undefined}
                            className="group flex h-full w-full flex-col items-start gap-y-2 rounded-[12px] bg-surface px-3.5 py-3 text-left transition-colors [border:1px_solid_var(--border)] enabled:cursor-pointer enabled:hover:bg-panel enabled:hover:[border-color:var(--primary)] disabled:cursor-not-allowed disabled:opacity-60"
                          >
                            <span className="flex w-full items-center justify-between">
                              {/* Done is a satisfied state, not a disabled one:
                                  the tile keeps its full contrast and stays
                                  clickable, it just wears a tick instead of a
                                  prompt. */}
                              <span
                                className={`flex h-[30px] w-[30px] flex-none items-center justify-center rounded-[10px] ${
                                  done ? 'bg-[rgba(22,163,74,0.12)]' : 'bg-primary-tint'
                                }`}
                              >
                                {done ? (
                                  <CheckIcon className="h-[15px] w-[15px] text-good" />
                                ) : (
                                  <SparklesIcon className="h-[16px] w-[16px] text-primary-dark" />
                                )}
                              </span>
                              {busy ? (
                                <span className="flex-none text-[12px] font-medium text-primary-dark">
                                  running…
                                </span>
                              ) : (
                                <ChevronRightIcon className="h-4 w-4 flex-none text-faint transition-colors group-hover:text-primary" />
                              )}
                            </span>
                            <span
                              className="w-full truncate text-[13.5px] font-semibold text-ink"
                              title={op.label}
                            >
                              {op.label}
                            </span>
                            {/* The receipt replaces the blurb once the task has
                                run — by then "what it does" matters less than
                                what it actually built. Two lines, then clipped:
                                a card column has no room for a paragraph. */}
                            <span
                              className={`w-full overflow-hidden text-[12.5px] leading-[1.45] [-webkit-box-orient:vertical] [-webkit-line-clamp:2] [display:-webkit-box] ${
                                done && receipt ? 'text-good' : 'text-dim'
                              }`}
                            >
                              {done && receipt ? receipt : task?.blurb || op.blurb}
                            </span>
                          </button>
                          {/* WHERE THE RUN LOOKS, for the one command that
                              takes a place. Sits OUTSIDE the tile button — a
                              control inside a <button> is invalid HTML and
                              swallows clicks — so choosing never runs: pick
                              Illinois here, then press the card, and the run
                              is exactly "/initial-outreach illinois". Full
                              state names, because "IL · 312 leads" makes the
                              reader do the decoding the page exists to do. */}
                          {scopedTileOps.has(op.id) && scopeStates.length > 0 && (
                            <div className="mt-1.5">
                              <Select
                                ariaLabel={`Which state ${op.label} runs over`}
                                value={tileScopes[op.id] || ''}
                                onChange={(code) =>
                                  setTileScopes((prev) => ({ ...prev, [op.id]: code }))
                                }
                                disabled={!canRunOperation || running}
                                options={[
                                  {
                                    value: '',
                                    label: `Every state · ${scopeStates
                                      .reduce((n, st) => n + st.count, 0)
                                      .toLocaleString()} leads`,
                                  },
                                  ...scopeStates.map((st) => ({
                                    value: st.code,
                                    label: `${st.name} · ${st.count.toLocaleString()} leads`,
                                  })),
                                ]}
                                buttonClassName="w-full"
                              />
                            </div>
                          )}
                          {/* Warn, never block: a second run in one day is a
                              legitimate thing to want, it just researches and
                              re-drafts the whole batch again, so say so once. */}
                          {asking && (
                            <div
                              id={`rerun-${op.id}`}
                              className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-[12px] leading-[1.5] text-dim"
                            >
                              <span>
                                Already run today — running it again researches and re-drafts the
                                whole batch, and posts a second receipt.
                              </span>
                              <span className="flex items-center gap-x-2">
                                <button
                                  type="button"
                                  // Same scope resolution as the tile itself:
                                  // confirming a re-run must not silently drop
                                  // the state chosen in the dropdown above.
                                  onClick={() => startOperation(op, { scope: tileScopeFor(op) })}
                                  className="cursor-pointer rounded-[7px] bg-transparent px-1 py-0.5 text-[12px] font-semibold text-primary-dark transition-colors hover:text-ink [border:none]"
                                >
                                  Run it again
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setConfirmRerun(null)}
                                  className="cursor-pointer rounded-[7px] bg-transparent px-1 py-0.5 text-[12px] font-medium text-faint transition-colors hover:text-ink [border:none]"
                                >
                                  Cancel
                                </button>
                              </span>
                            </div>
                          )}
                        </div>
                      )
                    })}
                  </div>
                  {/* Say why, rather than leaving a dead tile. Chat stays fully
                      usable — only the operations, which spend on research and
                      drafting, are gated. */}
                  {!canRunOperation && (
                    <p className="m-0 mt-2 text-[12px] leading-[1.5] text-faint">
                      {NO_OPERATION_REASON}, so these can’t be run from your account. Everything
                      else in chat works as normal.
                    </p>
                  )}
                  {/* Where a run's receipt goes, said once so the first click
                      is not a surprise. There is no "view the last run" link
                      any more — every run's receipt is a message in its own
                      thread, which is a better place for it than a single slot
                      that only ever held the most recent one. */}
                </div>
              </div>
            ) : (
              /* iMessage-style bubbles: yours blue and right-aligned, the
                 assistant's grey and left-aligned. No avatars — the side and
                 the colour already say who is speaking, and dropping them
                 gives long answers the full column width. */
              shownMessages.map((m, i) =>
                m.role === 'run' ? (
                  <RunMessage
                    // KEYED BY CONVERSATION AS WELL AS POSITION, still: the
                    // resolver lives in the host now (keyed by receipt, so a
                    // remount is free), but a thread switch reusing this
                    // instance for a DIFFERENT receipt would still hand the
                    // re-arm effect a stale msg for one render. The id keeps
                    // a switch an honest remount.
                    key={`${activeId || ''}:${i}`}
                    msg={m}
                    // Where this receipt lives, so the render path can re-arm
                    // the host's draft resolver for an OLD receipt that still
                    // owes drafts (ensureDraftsResolving dedupes the rest).
                    scope={scope}
                    convoId={activeId || ''}
                    onOpenDraft={openDraftFlow}
                    onOpenThread={openLeadThread}
                  />
                ) : (
                <div
                  key={i}
                  className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  {m.role === 'user' && m.kind === 'command' ? (
                    /* A command, not a message: monospace, a run glyph and a
                       hairline instead of the blue bubble, so an operation is
                       never mistaken for something a person typed. */
                    <div className="inline-flex max-w-[85%] items-center gap-x-2 rounded-[10px] bg-panel px-3 py-2 [border:1px_solid_var(--border)]">
                      <SparklesIcon className="h-[14px] w-[14px] flex-none text-primary-dark" aria-hidden="true" />
                      <span className="text-[11px] font-bold uppercase tracking-[0.06em] text-faint">
                        Command
                      </span>
                      <span className="min-w-0 truncate font-mono text-[12.5px] text-ink">
                        {m.text}
                      </span>
                    </div>
                  ) : m.role === 'user' ? (
                    <div className="max-w-[78%] rounded-[20px] rounded-br-[6px] bg-primary px-4 py-2.5">
                      <p className="m-0 whitespace-pre-wrap text-[14.5px] leading-[1.5] text-white">
                        {m.text}
                      </p>
                    </div>
                  ) : (
                    <div className="max-w-[85%] min-w-0">
                      {/* The bubble carries the answer; the actions sit under
                          it, outside the grey, the way a timestamp does. */}
                      <div className="rounded-[20px] rounded-bl-[6px] bg-[#e9e9eb] px-4 py-2.5 dark:bg-[#3a3a3c] [&_*]:text-ink">
                        <Markdown text={m.text} />
                      </div>
                      {m.stopped && (
                        <p className="m-0 mt-1.5 text-[12px] italic text-faint">Stopped</p>
                      )}
                      <div className="mt-1.5 flex flex-wrap items-center gap-2">
                        <CopyButton text={m.text} />
                        {(m.nav || []).filter((n) => pathAllowedFor(can, n.to)).map((n) => (
                          <Link
                            key={n.to}
                            to={n.to}
                            className="rounded-[7px] bg-panel px-2.5 py-1 text-[12px] font-semibold text-dim no-underline transition-colors hover:text-ink"
                          >
                            {n.label} →
                          </Link>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                ),
              )
            )}

            {/* ── the run, in the thread ──────────────────────────────────
                Compact on purpose. A run is one calm line among the bubbles,
                not a takeover: the flowchart is a provenance view now and only
                opens when someone asks how a finished result came about. */}
            {running && (
              <div className="flex justify-start">
                {!runIsOurs ? (
                  /* A previous sign-in's run is still finishing (SPA account
                     switch mid-run). Its operation and thread are not this
                     user's to see, so say only what blocks them — never name
                     the work or link to the thread. */
                  <div
                    role="status"
                    className="inline-flex max-w-[85%] items-center gap-x-2.5 rounded-[14px] bg-surface px-3.5 py-2.5 text-[12.5px] leading-[1.5] text-dim [border:1px_solid_var(--border)]"
                  >
                    Background work from a previous sign-in is finishing — new operations wait for
                    it.
                  </div>
                ) : runConvoId === activeId ? (
                  <div
                    role="status"
                    className="inline-flex max-w-[85%] items-center gap-x-2.5 rounded-[14px] bg-surface px-3.5 py-2.5 [border:1px_solid_var(--border)]"
                  >
                    <span
                      aria-hidden="true"
                      className="h-[13px] w-[13px] flex-none animate-spin rounded-full [border:1.5px_solid_var(--primary)] [border-top-color:transparent] motion-reduce:animate-none"
                    />
                    <span className="min-w-0 text-[12.5px] leading-[1.5] text-dim">
                      <span className="font-semibold text-ink">{hostRun?.operationLabel}</span>
                      {progressLine ? <span className="ml-1.5">· {progressLine}</span> : null}
                    </span>
                    {/* The run's Stop. Navigation deliberately no longer
                        cancels anything, so this button is the ONE way to
                        call off a mis-fired operation (wrong state, wrong
                        day) short of closing the tab. The host owns every
                        consequence: the "You stopped the run" receipt, the
                        aborted ledger row, and stopping the receipt's draft
                        resolution. */}
                    <button
                      type="button"
                      onClick={() => hostStopRun()}
                      className="ml-1 flex-none cursor-pointer rounded-[7px] bg-panel px-2.5 py-1 text-[12px] font-semibold text-dim transition-colors hover:text-ink"
                    >
                      Stop
                    </button>
                  </div>
                ) : (
                  /* The run is going, but it is not this thread's run — say
                     whose it is instead of pasting its progress under an
                     unrelated conversation. */
                  <button
                    type="button"
                    onClick={() => setActiveId(runConvoId)}
                    className="inline-flex max-w-[85%] cursor-pointer items-center gap-x-2.5 rounded-[14px] bg-transparent px-3.5 py-2.5 text-left transition-colors hover:bg-panel [border:1px_solid_var(--border)]"
                  >
                    <span
                      aria-hidden="true"
                      className="h-[13px] w-[13px] flex-none animate-spin rounded-full [border:1.5px_solid_var(--primary)] [border-top-color:transparent] motion-reduce:animate-none"
                    />
                    <span className="min-w-0 text-[12.5px] leading-[1.5] text-dim">
                      <span className="font-semibold text-ink">{hostRun?.operationLabel}</span> is
                      running in another chat — open it
                    </span>
                  </button>
                )}
              </div>
            )}

            {/* Only in the thread the answer belongs to — a stream live in
                another chat shows nothing here (the Topbar chip is its UI). */}
            {streaming && hostStream?.convoId === activeId && (
              <div className="flex justify-start">
                <div className="max-w-[85%] min-w-0 rounded-[20px] rounded-bl-[6px] bg-[#e9e9eb] px-4 py-2.5 dark:bg-[#3a3a3c] [&_*]:text-ink">
                  {liveText ? (
                    <>
                      <Markdown text={liveText} />
                      {/* Caret rides the end of the text while tokens land. */}
                      <span
                        aria-hidden
                        className="ml-0.5 inline-block h-[15px] w-[7px] translate-y-[2px] animate-pulse bg-primary motion-reduce:animate-none"
                      />
                    </>
                  ) : (
                    <Thinking />
                  )}
                </div>
              </div>
            )}

            {error && (
              <div className="rounded-[10px] bg-surface px-3.5 py-3 text-[13px] leading-[1.5] text-danger [border:1px_solid_var(--border)]">
                {error}
              </div>
            )}
          </div>
        </div>

        {/* The run strip that used to live here is gone: progress is a line in
            the thread and the outcome is a message in the thread, so there is
            nothing left for a strip to be a handle to. */}

        {/* ── composer ─────────────────────────────────────────────────── */}
        <div className="shrink-0 pb-1 pt-3">
          <form onSubmit={onSubmit} className="relative mx-auto w-full max-w-[760px]">
            {/* ── the slash-command palette ────────────────────────────────
                Sits ABOVE the composer (bottom-full) so it never covers the
                text being typed, and is a real listbox: the textarea keeps
                focus throughout and points at the highlighted row with
                aria-activedescendant, which is the pattern screen readers
                already know from every combobox. */}
            {paletteOpen && (
              <div className="absolute bottom-full left-0 right-0 z-30 mb-2 overflow-hidden rounded-[14px] bg-surface shadow-[0_20px_44px_-26px_rgba(2,6,23,0.5)] [border:1px_solid_var(--border)]">
                <p className="m-0 px-3 py-2 text-[11px] font-bold uppercase tracking-[0.06em] text-faint [border-bottom:1px_solid_var(--border)]">
                  {argMode ? `${findSlashCommand(slashInput.name)?.label || slashInput.name} — pick a ${argSpec.name}` : 'Operations'}
                </p>
                {argMode ? (
                  argMatches.length > 0 ? (
                    <ul
                      id="slash-command-list"
                      role="listbox"
                      aria-label={`Pick a ${argSpec.name}`}
                      className="m-0 max-h-[240px] list-none overflow-y-auto p-1.5"
                    >
                      {argMatches.map((row, i) => {
                        const on = i === paletteActive
                        return (
                          // eslint-disable-next-line jsx-a11y/click-events-have-key-events
                          <li
                            key={row.key}
                            id={`slash-option-${row.key}`}
                            role="option"
                            aria-selected={on}
                            onMouseDown={(e) => e.preventDefault()}
                            onMouseEnter={() => setPaletteIndex(i)}
                            // Fills the box, never runs — see completePaletteChoice.
                            onClick={() => completePaletteChoice(row)}
                            className={`cursor-pointer rounded-[10px] px-2.5 py-2 transition-colors ${
                              on ? 'bg-primary-tint' : 'hover:bg-panel'
                            }`}
                          >
                            <span className="flex items-baseline gap-x-2">
                              <span className="flex-none font-mono text-[12.5px] font-semibold text-primary-dark">
                                {row.kind === 'all' ? '*' : row.state.code}
                              </span>
                              <span className="min-w-0 truncate text-[13px] font-semibold text-ink">
                                {row.kind === 'all' ? argSpec.emptyLabel : row.state.name}
                              </span>
                              <span className="ml-auto flex-none text-[11.5px] tabular-nums text-faint">
                                {row.kind === 'all'
                                  ? `${scopeStates.reduce((n, st) => n + st.count, 0).toLocaleString()} leads`
                                  : `${row.state.count.toLocaleString()} leads`}
                              </span>
                            </span>
                            {row.kind === 'state' && row.why && (
                              // WHY this row matched, when it was not the obvious
                              // way — "chicago" landing on Illinois should say so
                              // rather than looking like a mistake.
                              <span className="mt-0.5 block text-[11.5px] leading-[1.45] text-dim">
                                {row.why === 'did you mean' ? 'did you mean this?' : `matched ${row.why}`}
                              </span>
                            )}
                          </li>
                        )
                      })}
                    </ul>
                  ) : (
                    <p className="m-0 px-3 py-2.5 text-[12.5px] leading-[1.5] text-dim">
                      No leads in{' '}
                      <span className="font-mono text-ink">{slashInput.arg.trim()}</span>. The
                      directory covers{' '}
                      {scopeStates.map((st) => st.name).join(', ')}.
                    </p>
                  )
                ) : paletteMatches.length > 0 ? (
                  <ul
                    id="slash-command-list"
                    role="listbox"
                    aria-label="Operations you can run"
                    className="m-0 max-h-[240px] list-none overflow-y-auto p-1.5"
                  >
                    {paletteMatches.map((cmd, i) => {
                      const on = i === paletteActive
                      return (
                        // eslint-disable-next-line jsx-a11y/click-events-have-key-events
                        <li
                          key={cmd.name}
                          id={`slash-option-${cmd.name}`}
                          role="option"
                          aria-selected={on}
                          // Honest rather than hidden: the row still lists what
                          // the operation is, it just says it cannot be run.
                          aria-disabled={!canRunOperation || running || undefined}
                          // mousedown would blur the textarea before the click
                          // lands, which closes the palette out from under the
                          // pointer. Keep focus where it is.
                          onMouseDown={(e) => e.preventDefault()}
                          onMouseEnter={() => setPaletteIndex(i)}
                          // Fills the box, never runs — see completePaletteChoice.
                          // This is also why the aria-disabled row stays
                          // clickable: completing TEXT is harmless for a role
                          // that cannot run operations, and the footer below
                          // already says why Enter would refuse.
                          onClick={() => completePaletteChoice(cmd)}
                          className={`cursor-pointer rounded-[10px] px-2.5 py-2 transition-colors ${
                            on ? 'bg-primary-tint' : 'hover:bg-panel'
                          }`}
                        >
                          <span className="flex items-baseline gap-x-2">
                            <span className="flex-none font-mono text-[12.5px] font-semibold text-primary-dark">
                              {cmd.command}
                            </span>
                            <span className="min-w-0 truncate text-[13px] font-semibold text-ink">
                              {cmd.label}
                            </span>
                          </span>
                          {cmd.blurb && (
                            <span className="mt-0.5 block text-[11.5px] leading-[1.45] text-dim">
                              {cmd.blurb}
                            </span>
                          )}
                        </li>
                      )
                    })}
                  </ul>
                ) : (
                  <p className="m-0 px-3 py-2.5 text-[12.5px] leading-[1.5] text-dim">
                    No command matches <span className="font-mono text-ink">/{slashQuery}</span>.
                    {paletteNear ? (
                      <>
                        {' '}
                        Did you mean{' '}
                        <span className="font-mono text-primary-dark">{paletteNear.command}</span> —{' '}
                        {paletteNear.label}?
                      </>
                    ) : (
                      ' Clear the slash to send it as a message instead.'
                    )}
                  </p>
                )}
                {/* Why a highlighted row would refuse, said in the palette
                    rather than only after the keystroke. */}
                {!canRunOperation ? (
                  <p className="m-0 px-3 py-2 text-[11.5px] leading-[1.45] text-faint [border-top:1px_solid_var(--border)]">
                    {NO_OPERATION_REASON}, so these can’t be run from your account. Everything else
                    in chat works as normal.
                  </p>
                ) : running ? (
                  <p className="m-0 px-3 py-2 text-[11.5px] leading-[1.45] text-faint [border-top:1px_solid_var(--border)]">
                    An operation is already running — one at a time.
                  </p>
                ) : null}
                {/* "Click" is named first because it is the path that works on
                    every machine — Tab-complete was reported dead on a Windows
                    keyboard, and a hint that leads with the flaky key teaches
                    the wrong habit. Nothing in this palette runs anything:
                    choosing fills the box, and only Enter commits. */}
                <p className="m-0 px-3 py-2 text-[11.5px] text-faint [border-top:1px_solid_var(--border)]">
                  Click or Tab fills the box · ↑↓ to choose · Enter runs · Esc dismisses
                </p>
              </div>
            )}
            {/* An unknown command, or the reason a known one will not run.
                role="status" so it is announced without stealing focus. */}
            {commandNotice && (
              <p
                role="status"
                className="m-0 mb-2 rounded-[10px] bg-panel px-3 py-2 text-[12.5px] leading-[1.5] text-dim [border:1px_solid_var(--border)]"
              >
                {commandNotice}
              </p>
            )}
            <div className="flex items-end gap-x-2 rounded-[14px] bg-surface p-2 [border:1px_solid_var(--border)] focus-within:[border-color:var(--primary)]">
              <textarea
                ref={textareaRef}
                rows={1}
                value={input}
                onChange={onInputChange}
                onKeyDown={onKeyDown}
                placeholder="Ask anything about your workspace…"
                aria-label="Message"
                // Combobox semantics only while the palette is actually open —
                // the rest of the time this is a plain message box and should
                // be announced as one.
                role={paletteOpen ? 'combobox' : undefined}
                aria-expanded={paletteOpen || undefined}
                aria-controls={paletteOpen && paletteMatches.length ? 'slash-command-list' : undefined}
                aria-activedescendant={paletteOpen ? activeOptionId : undefined}
                aria-autocomplete={paletteOpen ? 'list' : undefined}
                className="max-h-[200px] min-h-[24px] flex-1 resize-none bg-transparent px-2 py-1.5 text-[14.5px] leading-[1.5] text-ink outline-none placeholder:text-faint [border:none]"
              />
              {streaming ? (
                <button
                  type="button"
                  onClick={() => hostStopStream()}
                  aria-label="Stop generating"
                  className="inline-flex h-[34px] w-[34px] shrink-0 cursor-pointer items-center justify-center rounded-[10px] bg-ink text-surface transition-opacity hover:opacity-80"
                >
                  <StopIcon className="h-[15px] w-[15px]" />
                </button>
              ) : (
                <button
                  type="submit"
                  disabled={!input.trim()}
                  aria-label="Send"
                  className="inline-flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-[10px] bg-primary text-white transition-colors enabled:cursor-pointer enabled:hover:bg-primary-dark disabled:cursor-not-allowed disabled:opacity-35"
                >
                  <SendIcon className="h-[15px] w-[15px]" />
                </button>
              )}
            </div>
            <p className="m-0 mt-2 text-center text-[11.5px] text-faint">
              Enter to send · Shift + Enter for a new line · <span className="font-mono">/</span> for
              commands. Answers can be wrong — check anything that matters.
            </p>
          </form>
        </div>
      </section>
      </div>

      {/* ── provenance ────────────────────────────────────────────────────
          Fed entirely from the receipt that was clicked, so it is the same view
          whether the run finished a second ago or three reloads ago. */}
      <div
        ref={runPaneRef}
        tabIndex={-1}
        className={`absolute inset-0 outline-none ${SLIDE} ${
          runOpen ? 'translate-x-0 opacity-100' : 'pointer-events-none translate-x-full opacity-0'
        }`}
      >
        {timeline ? (
          <LeadTimelineView
            lead={timeline.lead}
            companyId={timeline.companyId}
            hubspotUrl={timeline.hubspotUrl}
            onBack={backToChat}
          />
        ) : draftFlow ? (
          <DraftFlowView
            lead={draftFlow.lead}
            operationLabel={draftFlow.operationLabel}
            passes={draftFlow.passes}
            draft={draftFlow.draft}
            facts={draftFlow.facts}
            kind={draftFlow.kind}
            meta={draftFlow.meta}
            onBack={backToChat}
          />
        ) : (
          flow && (
            <AgentRunView
              operation={flow.operation}
              passes={flow.passes}
              result={flow.result}
              lead={flow.lead}
              onBack={backToChat}
              // Offered only when the operation still exists and this role may
              // run it — the view's own button is not the boundary, but it should
              // not dangle either. Starting a run returns to the thread, which is
              // where it now reports.
              onRestart={
                flow.runnable && canRunOperation
                  ? () => {
                      setRunOpen(false)
                      startOperation(flow.operation)
                    }
                  : undefined
              }
            />
          )
        )}
      </div>
    </div>
  )
}
